import { showcaseEnglish } from "./showcase";
import { CompetitionFormat, Exercise } from "../index.js";
import dico from "./dico.js";

const en = {
  showcase: showcaseEnglish,
  athleteCount: {
    title: "Athlete counting",
    notice:
      "Registration reserves a place. Participation is permanently counted when the selected field matches a completion value. Without a rule, the place remains reserved until deletion.",
    field: "Participation completion field",
    none: "No rule",
    values: "Completion values (one per line)",
    hint: "For a list, use option values rather than translated labels.",
    defaultValue: "Done",
    invalid: "Choose an athlete field and at least one valid completion value.",
  },
  athleteEntry: {
    signAndSend: "Sign and send",
    signatureTitle: "Signature for this competition",
    signatureDraw: "Draw your signature with a finger or mouse",
    signatureClear: "Clear and start again",
    signatureNotice:
      "By signing, I confirm the displayed values and authorize saving my signature for this competition’s tickets. Each click on “Sign and send” will attach this signature to the confirmed ticket.",
    mobileTicketTitle: "Athlete entry",
    ticketReceivedAt: "Received on",
    ticketSigned: "Signed",
    ticketUnsigned: "Unsigned",
    signatureConfirmedAt: "Confirmation received at",

    refusedAttemptClosed:
      "Entry for this attempt is closed following the secretary’s refusal.",
    attemptsTab: "Attempts",
    notifications: "Notifications",
    equipmentTab: "Equipment",
    historyTab: "History",
    noTickets: "No tickets",

    equipmentGroupPartial: "Equipment partially locked",
    equipmentGroupLocked: "Equipment locked",
    equipmentGroupOpen: "Equipment unlocked",
    equipmentLockGroup: "Lock equipment for the whole group",
    equipmentUnlockGroup: "Unlock equipment for the whole group",
    refusedBySecretary: "Ticket refused by the secretary",
    retainedValue: "Retained value",
    hideCode: "Hide code",
    phoneNotConnected: "Not connected",
    equipmentEditable: "Editable",
    onlineCompact: "Connected",
    offlineCompact: "Unavailable",
    equipmentSettings: "Equipment settings",
    equipmentLock: "Lock equipment settings",
    positionRack: "Squat position",
    cales: "Blocks",
    athleteAccessAction: "This athlete’s access",
    meetEntryButton: "Athlete entry",
    settingsSavedCompact: "Settings saved",
    entryDisabledCompact: "Disabled",
    entrySyncPending: "Synchronizing",
    entryGroupExcluded: "Mobile entry disabled for this group",
    entryGroupNotReady:
      "Confirm the platform state in the competition settings",
    processedAt: "Platform processing",
    notRecorded: "Entry not saved",
    allTickets: "All tickets",
    historySearch: "Name, attempt, weight or comment",
    athleteTicketHistory: "This athlete’s tickets",
    reviewTicket: "Review",
    ticketAccepted: "Accepted",
    ticketRefused: "Refused",
    ticketFailed: "Not saved",
    ticketCancelled: "Cancelled",
    ticketToReview: "To review",
    ticketSaved: "Saved",
    ticketPending: "Pending",
    ticketHistory: "Ticket history",
    customAttemptChange: "Attempt change awaiting the secretary’s decision",
    allowWhileCalled: "Allow entry while the athlete is called",
    unlimitedChanges: "Allow unlimited changes",
    reviewChanges: "Submit changes for the secretary’s review",
    thirdAttemptChange:
      "Third attempt change awaiting the secretary’s decision",
    refuseKeepCurrent: "Refuse the ticket without changing the weight",
    refuseKeepCurrentHelp:
      "The refusal can be recorded without changing the current weight. Enter a comment; any correction to the weight must then be made manually by the secretariat.",
    refusedEntryManual:
      "Ticket refused; weight unchanged. Any correction must be made manually by the secretariat.",
    decisionConflict:
      "The attempt has changed, started, or cannot be restored. Nothing was changed. Check the weight with the secretary.",
    previousValue: "Previous value",
    lateCellHelp:
      "Ticket awaiting a decision: click to accept or refuse the entry.",
    refusedEntry: "Entry refused by the secretary; previous state restored",
    acceptedEntry: "Entry accepted by the secretary",
    refuseEntry: "Refuse entry",
    acceptEntry: "Accept entry",
    decisionHelp:
      "This weight has been saved. Accept confirms its eligibility. Refuse restores the previous state if the attempt has not changed or started.",
    draftNotSent:
      "The attempt changed or was locked. Your proposal was not sent.",
    groupContextUnavailable:
      "Entry is waiting for the secretary to confirm the platform state.",
    prepareGroup: "Prepare entry for this group",
    prepareGroupHelp:
      "After a restart, confirm the platform state before opening entry for this group.",
    prepareGroupConfirm:
      "Confirm that no athlete from this group is currently on the bar. Existing platform calls remain taken into account.",

    draftChanged:
      "This attempt has changed. Your proposal is preserved. Check the values before confirming again.",
    proposed: "Your proposal",
    useCurrentRules: "Continue with current data",
    lateReceiptHelp:
      "Weight saved after the deadline. Check its eligibility with the secretary before lifting.",
    secretaryFeedback: "Secretary feedback",
    phoneNumber: "Phone no.",

    settingsTab: "Settings",
    accessTab: "Codes and phones",
    plateauFilter: "Platform",
    allPlateaus: "All platforms",
    groupFilter: "Group",
    allGroups: "All groups",
    profileUnavailable: "Configure the mobile profile in the discipline",
    excludedAthlete:
      "This group is not allowed to enter weights on phones. A previously issued code remains available, but cannot be used to sign in.",
    syncPending: "Setting saved locally, awaiting server confirmation.",
    settingsSaved:
      "Settings saved. Check the synchronization confirmation before handing out codes.",
    accessChanged: "Access updated.",
    cancelledRequests: "Cancelled requests",
    inProgress: "Entries being saved",
    revocationHelp:
      "Queued requests are cancelled. An entry already being saved may still complete: check its receipt in Tickets.",
    requestsInFlight: "Requests in progress",
    refreshSessions: "Refresh phones",
    localHistory:
      "Local history: server unavailable. Ticket review will be available after reconnection.",
    reviewHelp:
      "Late tickets, uncertain timing and changes requiring review await the secretary’s decision: accept or refuse the entry.",
    unreviewedOnly: "Unreviewed only",
    emptyFilteredTickets: "No tickets match these filters.",
    executing: "Saving in progress - wait for the final receipt.",

    otherPhonePending:
      "An entry is in progress on the other phone. Wait for confirmation.",

    unconfirmedAfterExpiry:
      "An entry was in progress. Check with the secretary whether it was saved before sending another request.",
    checkedWithSecretary: "Checked with the secretary",

    weight: "Weight",
    invalidWeight: "Value not allowed: follow the minimum and increments shown",
    waitingResult:
      "Weight saved. Waiting for the result before the next entry.",
    onBar: "Attempt in progress. Entry resumes after the result is confirmed.",
    finished: "All attempts are complete. No weight to enter.",
    noHeightsAvailable:
      "No rack settings can be changed right now. Contact the secretary if needed.",

    recordPermissions: "Records allowing 0.5 kg increments",
    recordPermissionsHelp:
      "None by default. Select only disciplines, organisations and categories whose records are eligible at this meet. The threshold includes lifts already achieved.",
    recordException: "Allow reduced increments for records",
    recordStep: "Record increment",
    recordThreshold: "Record threshold (computed)",
    recordEligibility: "Record eligible (computed)",
    recordBehaviorHelp:
      "Two read-only Behavior outputs: the applicable record threshold and eligibility for this discipline, category and meet. Without a threshold or eligibility, only the ordinary increment is allowed.",

    ticketsToReview: "Tickets to review",
    noAttemptAvailable:
      "The next attempt becomes available once the previous result is confirmed, unless locked by the platform.",
    ticketSubmitted: "Ticket already submitted",
    retry: "Retry the same request",
    profileSettings: "Access and identification",
    attemptStateRules: "Results and announcement",
    changeRules: "Attempt changes",
    profileHelp:
      "Choose rack heights and attempts available on phones, including openers, then set the allowed number of changes. Body weight and results remain with the secretary. Competition activation is independent.",
    profileEnabled: "Allow these fields for athlete entry",
    profileInvalid:
      "Complete the rules and check selected fields before enabling this profile.",
    nameFields: "Fields forming the displayed name",
    field: "Field",
    entryKind: "Entry type",
    min: "Minimum",
    max: "Maximum",
    step: "Increment",
    attemptNumber: "Attempt number (1 or higher)",
    previousWeight: "Previous weight",
    previousResult: "Previous result",
    targetResult: "Target attempt status",
    successValue: "Successful value",
    failedValue: "Failed value",
    announcedValue: "Announced value",
    successIncrement: "Increase after success",
    autoField: "Automatically proposed weight",
    changesField: "Changes counter",
    maxChanges: "Allowed changes",
    ticketReference: "Ticket timer",
    ticketReferenceHelp:
      "Behavior timer reference. Available variables: {athleteId}, {field}.",
    conditions: "Opening conditions (all required)",
    allowedValues: "Allowed values, comma separated",
    addCondition: "Add condition",
    addField: "Add entry field",
    remove: "Remove",
    title: "Athlete entry",
    openPage: "Athlete entry page",
    manage: "Athlete access",
    enabled: "Enable phone entry",
    optional: "Disabled by default. The secretary can always enter data.",
    groups: "Allowed groups",
    save: "Save",
    close: "Close",
    print: "Print QR code",
    code: "Personal code",
    connect: "Sign in",
    disconnect: "Sign out",
    twoPhones: "Up to two phones per athlete. Share the code with your coach.",
    disabled: "Phone entry is disabled for this competition.",
    offline:
      "The platform computer is unavailable. Please contact the secretary.",
    online: "Platform computer connected",
    loading: "Connecting…",
    athlete: "Athlete",
    showCode: "Show code",
    replaceCode: "Replace code and disconnect phones",
    revoke: "Disconnect this phone",
    replaceConfirm: "Replace this code? Connected phones will lose access.",
    sessions: "Connected phones",
    noSessions: "No phone connected",
    heights: "Rack heights",
    attempts: "Next attempts",
    submit: "Confirm entry",
    locked: "Entry locked",
    current: "Saved value",
    pending: "Processing - wait for platform confirmation.",
    uncertain:
      "Confirmation needs checking. Do not submit again; contact the secretary.",
    applied: "Saved on the platform computer",
    rejected: "Entry rejected",
    cancelled: "Request cancelled",
    late: "Late ticket",
    unknownTime: "Ticket time needs checking",
    remaining: "Ticket time remaining",
    exceeded: "Ticket time exceeded by",
    noTimer: "Ticket time unavailable",
    lateNotice: "You can submit; the secretary will be notified of the delay.",
    timely: "Ticket received on time",
    tickets: "Mobile entry history",
    review: "Mark as reviewed",
    reviewed: "Reviewed",
    note: "Secretary decision / comment",
    receivedAt: "Server reception",
    appliedAt: "Platform recording",
    deadline: "Ticket deadline",
    lateOnly: "Only late tickets or tickets needing review",
    emptyTickets: "No mobile entries recorded",
    refresh: "Refresh",
    error: "Operation failed",
    kg: "kg",
    squat1: "Squat - 1st attempt",
    squat2: "Squat - 2nd attempt",
    squat3: "Squat - 3rd attempt",
    bench1: "Bench - 1st attempt",
    bench2: "Bench - 2nd attempt",
    bench3: "Bench - 3rd attempt",
    deadlift1: "Deadlift - 1st attempt",
    deadlift2: "Deadlift - 2nd attempt",
    deadlift3: "Deadlift - 3rd attempt",
    hauteurSquat: "Squat rack height",
    hauteurDc: "Bench rack height",
    hauteurSecu: "Safety height",
    errors: {
      SIGNATURE_REQUIRED: "A signature is required for this competition.",
      SIGNATURE_CONFIRMATION_REQUIRED: "Please sign and send this ticket.",
      SIGNATURE_ALREADY_CAPTURED:
        "A signature was already saved on another phone. Check the values, then sign and send again.",
      SIGNATURE_CANCELLED: "Signature cancelled: no ticket sent.",
      INVALID_CODE: "Incorrect code.",
      TWO_PHONES_CONNECTED:
        "Two phones are already connected. Disconnect one or contact the secretary.",
      ENTRY_SESSION_EXPIRED:
        "Access expired or revoked. Sign in again or contact the secretary.",
      ENTRY_DISABLED: "Phone entry is disabled.",
      DESKTOP_OFFLINE: "The platform computer is unavailable.",
      PERSISTENCE_UNAVAILABLE:
        "Saving failed on the platform computer. The decision is not confirmed. Refresh and try again.",
      CONFLICT: "Data changed. Refresh before trying again.",
      LOCKED: "Entry locked. Contact the secretary.",
      INVALID_VALUE: "Value not allowed.",
      COMMAND_PENDING: "An entry is being processed. Wait for confirmation.",
      OTHER_DESKTOP_ACTIVE:
        "Another computer is already handling mobile entries.",
      RECOVERY_REQUIRED:
        "A command must be reconciled with the previous computer before resuming.",
      ENTRY_KEY_REQUIRED: "Code encryption must be configured on the server.",
      ENTRY_UNAVAILABLE: "Entry is unavailable for this competition.",
      COMMAND_CANCELLED: "This request was cancelled.",
      INVALID_REQUEST: "Invalid request.",
      ENTRY_OPERATOR_REQUIRED: "Authorized secretary access required.",
    },
  },
  planningOutputs: {
    refereeField: "Planning referee field",
    selection: "Planning selection",
    single: "Single selection",
    multiple: "Multiple selection",
    electronImport: "Electron import",
    electronImportHelp:
      "Map planning information to discipline fields. Custom values imported into the planning are preserved by field key.",
    customValueOnly: "Custom value only",
    refereesRules: "Referee assignment",
    minimum: "Minimum",
    maximum: "Maximum",
    unlimited: "0 = unlimited",
    exclusive: "Exclusion group",
    exclusiveHelp: "Empty = roles may be combined",
    allowedRanks: "Allowed ranks",
    allowedRanksHelp: "Comma-separated; empty = all ranks",
    assignmentError: "Assignment does not match this discipline’s rules.",
    policyHelp:
      "Rules from the platform’s disciplines apply to these assignments.",
  },
  planningDisciplines: {
    yes: "Yes",
    no: "No",
    section: "Planning",
    description:
      "Defaults offered in the licensed portal. Age and weight categories come from this discipline’s catalog.",
    enabled: "Available in the planning tool",
    movementCount: "Number of movements",
    attemptsPerMovement: "Attempts per movement",
    seedField: "Performance used to seed athletes",
    alphabetical: "Alphabetical order",
    perDiscipline: "Settings per discipline",
    perDisciplineHelp:
      "Each discipline keeps its own groups and durations. Settings are saved with this planning.",
    importProfile: "Import profile",
    autoDetect: "Multiple disciplines · detect from column",
    importHelp:
      "The Discipline column identifies each row. The admin column mappings are applied for each discipline.",
    appendImport: "Add to existing athletes",
    importIssues: "Some rows were not imported",
    importFailed: "Import failed. Check the file format and column mapping.",
    noAthletes: "No athletes imported: check disciplines and categories.",
    ageCategory: "Age category",
    weightCategory: "Weight category",
    isHandisport: "Para athlete",
    unknownCategory: "Category incompatible with this discipline, sex or age.",
    mixedPlateau: "A platform can only contain one discipline.",
    mixedGroup: "A group can only contain one discipline.",
    restoreDefaults: "Restore admin defaults",
    importColumns: "Import columns",
    customFields: "Discipline data",
    generateError: "Planning generation failed.",
    resumeExisting: "Resume existing planning",
    resumeError: "The existing planning could not be opened.",
    disabledDiscipline: "Planning is disabled for this discipline.",
    loading: "Loading disciplines…",
  },
  refereeing: {
    sounds: "Timer sound signals",
    screenTitle: "Screen title",
    buttonLabel: "Element label",
    appearance: "Element",
    appearances: { BUTTON: "MUI button", AREA: "Custom area", TEXT: "Text" },
    addClickableElement: "Add clickable element",
    refresh: "Refresh profiles",
    edit: "Edit",
    title: "Refereeing",
    classic: "Classic refereeing",
    legacy: "Legacy refereeing",
    profiles: "Shared profiles",
    newProfile: "New profile",
    profileName: "Profile name",
    screens: "Screens",
    assignments: "Disciplines",
    rules: "Behavior rules",
    layout: "Display Editor",
    addScreen: "Add screen",
    addButton: "Add button",
    role: "Position",
    key: "Key",
    label: "Label",
    enabled: "Enabled",
    save: "Save profile",
    saved: "Profile saved",
    remove: "Delete",
    back: "Back to profiles",
    preview: "Interactive preview",
    command: "Command",
    value: "Value",
    mode: "Refereeing system",
    classicMode: "Classic · three referees and jury",
    behaviorMode: "Custom · result calculated by Behavior",
    sharedHint:
      "A shared profile produces a single set of links and QR codes. Disciplines without an assigned profile use the legacy refereeing screens.",
    rulesHint:
      "On each command, Behavior receives inputValues.refereeing (votes, state, result, role, command). The refereeing service accepts setState and publishResult through options. Edit buttons and their Behavior in Display Editor.",
    syncHint:
      "In Meet Manager, refresh the discipline catalog to download profiles, then open Refereeing.",
    discard: "Leave without saving changes?",
    assigned: "Already assigned to another profile",
    profile: "Refereeing profile",
    selectDisciplines: "Competition disciplines",
    none: "No enabled screen",
    pending: "Loading refereeing…",
    commands: {
      vote: "Vote",
      startTimer: "Bar loaded / start timer",
      resumeTimer: "Resume timer",
      stopTimer: "Stop timer",
      resetTimer: "Reset timer",
      resetLights: "Reset votes",
      juryStop: "Jury discussion",
      resetJuryMember: "Clear jury vote",
      updateTimer: "Timer duration",
      updateResultDuration: "Result duration",
      setState: "Update shared state",
      publishResult: "Publish Behavior result",
    },
    errors: {
      REFEREE_PROFILE_INVALID: "Invalid refereeing configuration.",
      REFEREE_DISCIPLINE_INVALID: "Unknown discipline.",
      REFEREE_DISCIPLINE_ASSIGNED:
        "This discipline is already assigned to another profile.",
      REFEREE_CLASSIC_REQUIRED: "The classic profile is required.",
      REFEREE_PROFILE_UNAVAILABLE:
        "This profile or screen is unavailable. Refresh the Meet Manager catalog.",
      REFEREE_PROFILE_INACTIVE:
        "The displayed group uses another refereeing profile.",
      REFEREE_CATALOG_UNAVAILABLE: "Refereeing catalog unavailable.",
      REFEREE_COMMAND_INVALID: "Invalid command.",
      REFEREE_COMMAND_FAILED:
        "Command not confirmed. Check the current state before retrying.",
      REFEREE_VOTE_INVALID: "Invalid vote for this position.",
      REFEREE_TIME_INVALID: "Use minutes:seconds.",
    },
  },
  appName: "Meet Manager",
  accountSync: {
    synced: "Synced",
    syncing: "Syncing…",
    offline: "Offline",
    error: "Sync pending",
    partial: "Partially synced",
    ownershipConflictDetail:
      "A local competition belongs to another account and remains unchanged. Your account's other competitions are synced.",
    "auth-required": "Sign in required",
    detail:
      "Your competitions and groups sync automatically across devices signed in to your account.",
    offlineDetail:
      "Changes are saved on this device. Sync will resume when the connection returns.",
    authDetail: "Sign in to your account to sync your competitions and groups.",
    errorDetail:
      "Your changes are saved on this device. Sync will retry automatically.",
    restoreDetail:
      "This competition was deleted from the account. Import it as a new competition to sync the copy saved on this device.",
    identityConflictDetail:
      "A group or athlete belongs to multiple competitions. Remove the duplicate from the affected competition to resume syncing.",
    adoption: {
      title: "Review older copies",
      examine: "Review older copy",
      detail:
        "An older copy of this competition has differences. Review them before its first synchronization.",
      description:
        "Compare this copy with the shared values and choose which values to keep. No automatic merge takes place during this review.",
      backupDetail:
        "The original local copy is saved permanently before any changes. You can find it under Original copies even after confirming your choices.",
      nextPage: "Next page",
      previousPage: "Previous page",
      differences: "Differences between older copy and shared data",
      field: "Athlete / field",
      local: "Older copy",
      remote: "Shared value",
      choice: "Value to keep",
      localChoice: "Local copy",
      remoteChoice: "Shared",
      protected: "Shared · protected",
      empty: "-",
      emptyText: "(empty)",
      yes: "Yes",
      no: "No",
      keepWeighIn: "Keep my weigh-in",
      keepShared: "Keep shared values",
      weighInDetail:
        "Keep this copy's editable body weights and opening attempts; explicitly choose the shared value for every other field.",
      remaining: (count: number) =>
        `${count} field${count === 1 ? "" : "s"} still need${count === 1 ? "s" : ""} a choice.`,
      additions: (count: number) =>
        `${count} item${count === 1 ? "" : "s"} found only in this copy will be added after confirmation.`,
      competition: "Competition",
      loading: "Loading differences…",
      saving: "Saving…",
      confirm: "Apply these choices",
      archive: "Archive local copy",
      deleted:
        "This competition was deleted from the account. Archiving preserves the original copy without recreating the shared competition.",
      stale:
        "The data has changed since this preview. It has been reloaded and your choices have been cleared. Review the new differences before confirming.",
      saved: "Your choices have been saved.",
      noReviews: "There are no older copies left to review.",
      loadError:
        "Unable to load copies for review. Your data is preserved. Retry or restart the application if the problem persists.",
      scoreUnavailable:
        "The score cannot be recalculated with these choices. Check body weight, sex, discipline and formula, then load the required coefficients before retrying.",
      resolveError:
        "Unable to apply these choices. Your data is preserved. Reload the preview before trying again.",
      archives: "Original copies",
      reviewTab: "To review",
      archiveTab: "Archives",
      export: "Save original copy",
      exportSaved: "The original copy has been saved.",
      exportError: "Unable to save the original copy. Please retry.",
      noArchives: "No original copies have been archived.",
      archiveDescription:
        "These copies are preserved before their first synchronization. Save them as JSON to keep an independent copy.",
      archivedOn: "Saved on",
      pendingArchive: "Review pending",
      resolvedArchive: "Review completed",
    },
  },
  common: {
    kg: "kg",
    retry: "Retry",
    close: "Close",
    exitApplication: "Exit Application",
  },
  ahleteDisplay: "Athlete Display",
  refereeLightsDisplay: "Referee Lights",
  LoaderDisplay: "Loader Display",
  publicDisplay: "Public Display",
  publicDisplayLabels: {
    discipline: "Discipline",
    name: "Name",
    pdc: "Body Weight",
    category: "Category",
    squat: "Squat",
    squat1: "Squat 1",
    squat2: "Squat 2",
    squat3: "Squat 3",
    bench: "Bench",
    bench1: "Bench 1",
    bench2: "Bench 2",
    bench3: "Bench 3",
    deadlift: "Deadlift",
    deadlift1: "Deadlift 1",
    deadlift2: "Deadlift 2",
    deadlift3: "Deadlift 3",
    totalAnnonced: "Total Announced",
    totalReal: "Total Achieved",
    placement: "Place",
  },
  Live: "Live",
  enableLive: "Enable live",
  enableLiveError: "Error while enabling live",
  disableLive: "Disable live",
  refereeLights: "Lights",
  liveEnabled: "Live enabled.",
  liveDisabled: "Live disabled.",
  liveAccessDenied:
    "Your license does not allow enabling live streaming. Contact your administrator.",
  definePublic: "Define public",
  definePrivate: "Define private",
  loading: "Loading...",
  about: {
    title: "About Meet Manager",
    version: "Version",
    logoAlt: "Meet Manager Logo",
    buildNumber: "Build num",
    localFolder: "Local folder",
  },
  meet: {
    success: {
      imported: "Meet successfully imported",
    },
    confirm: {
      overwriteMeet: (existing: string, incoming: string) =>
        `The meet "${incoming}" will replace the existing meet "${existing}". Do you want to continue?`,
    },
    importSynchronisedMeet: "Import synchronised meet",
    groupCount: "Groups",
    title: "Competition",
    name: "Name",
    date: "Date",
    startDate: "Start Date",
    endDate: "End Date",
    location: "Location",
    ville: "City",
    streamUrl: "Stream URL",
    streamUrlPlaceholder:
      "YouTube: youtube.com/watch?v=xxx or @channel | Twitch: twitch.tv/channel",
    streamUrlHelper: "Supports YouTube and Twitch",
    isNational: "National competition",
    isNationalHelper:
      "Rankings combine all age categories, within each sex, discipline and weight class.",
    pointsFormula: "Points formula",
    pointsFormulaGl: "GL (IPF Goodlift)",
    pointsFormulaDots: "DOTS",
    pointsFormulaHelper:
      "Formula used to compute the index (points = total × index).",
    description: "Description",
    selectMeet: "Select a competition",
    currentMeet: "Current competition",
    selectExistingMeet: "Select an existing competition",
    createNewMeet: "Create a new competition",
    saved: "Competition saved",
    created: "Competition created",
    deleted: "Competition deleted",
    error: {
      leagueRequired:
        "Please select an organisation before creating the competition.",
      creationFailed:
        "Unable to create the competition. Check the entered information and try again.",
      prismaGeneric:
        "A technical error occurred while saving. Please try again or contact support.",
    },
    confirmDeleteTitle: "Confirm deletion",
    confirmDelete:
      "Do you really want to delete this competition? All linked groups will also be deleted. This action is irreversible.",
    dragAndDrop: "Drag and drop a scoresheet file here or click to select.",
    importMatchSheet: "Import match sheet",
    importSynchronisedGroup: "Import synchronised group",
    noGroupFound: "No group found",
    noMeetFound: "No meet found",
    nbAthletes: "Athletes",
    // Competition formats
    formats: {
      fullPower: "3 Lifts (FA, PL)",
      singleLift: "1 Lift (DC, BP)",
    },
    selectFormat: "Select competition format",
    formatDescription: {
      fullPower: "Competition with squat, bench press and deadlift",
      singleLift: "Competition with bench press only",
    },
    validation: {
      incompatibleDisciplines: "Disciplines incompatible with group format",
      selectCompatibleDiscipline: "Select a compatible discipline",
      tooFast:
        "Automatic validation too fast. Please wait 5 seconds before validating again.",
    },
    tabs: {
      createOrImportMatchSheet: "Create / Import (Match sheet)",
      importSyncPlanning: "Import (Sync / Planning)",
    },
    fromPlanning: {
      createFromPlanning: "Create from planning",
      myPlannings: "My plannings",
      selectPlanning: "Select a planning",
      noPlanningFound: "No planning found",
      refresh: "Refresh",
      searchMeet: "Search competition",
      searchPlanning: "Search planning",
      overwriteConfirm:
        "This competition already exists. Importing again will overwrite existing data. Continue?",
      statusOnlyCreated:
        'Only finalized plannings (status "Created") can be imported.',
      missingRefereesInfo:
        "Some referee roles are not assigned on one or more platforms.",
      errorLoadPlannings: "Unable to load the list of plannings.",
      errorCreateMeet: "Unable to create the competition from the planning.",
      statusColumn: "Status",
    },
    import: {
      formatDetected: "Format detected automatically",
      fullPowerDetected: "3 lifts format detected (FA/PL)",
      singleLiftDetected: "1 lift format detected (DC/BP)",
      formatDetectionFailed: "Unable to detect format, using default format",
      missingFieldError: (fieldName: string, cellAddress?: string) =>
        cellAddress
          ? `The field "${fieldName}" is required but missing or empty in the import file (cell ${cellAddress})`
          : `The field "${fieldName}" is required but missing or empty in the import file`,
      niveaux: {
        fullPowerFormat: "Expected format: Levels based on total of 3 lifts",
        singleLiftFormat: "Expected format: Levels based on bench press only",
      },
      records: {
        fullPowerFormat:
          "Expected format: Records for squat, bench, deadlift and total",
        singleLiftFormat: "Expected format: Records for bench press only",
      },
      downloadGenericTemplate: "Download import template",
      generateLots: "Automatically generate lot numbers",
      generateLotsHint:
        "If every lot in the file is already filled, they are kept. Otherwise unique random lots (1…N) are assigned to all athletes.",
      noImportEnabled: "Import is not enabled for your organisation",
      allowedFormats: (formats: string) => `Allowed formats: ${formats}`,
      formatUnknown:
        "Unrecognized file format. Use an FFForce sheet or the Meet Manager import template.",
      formatNotAllowed:
        "This import format is not allowed for your organisation",
      formatAmbiguous: "Unable to determine the file format",
      invalidDiscipline: (value: string, sheet: string, row: number) =>
        `Invalid discipline "${value}" (sheet ${sheet}, row ${row})`,
      invalidAgeCategory: (value: string, sheet: string, row: number) =>
        `Invalid age category "${value}" (sheet ${sheet}, row ${row})`,
      invalidWeightCategory: (value: string, sheet: string, row: number) =>
        `Invalid weight category "${value}" (sheet ${sheet}, row ${row})`,
      ageCategoryDisciplineMismatch: (
        ageValue: string,
        discipline: string,
        sheet: string,
        row: number,
      ) =>
        `Age category "${ageValue}" does not match discipline "${discipline}" (sheet ${sheet}, row ${row})`,
      weightCategorySexMismatch: (
        weightValue: string,
        sex: string,
        sheet: string,
        row: number,
      ) =>
        `Weight category "${weightValue}" does not match sex "${sex}" (sheet ${sheet}, row ${row})`,
      invalidSex: (value: string, sheet: string, row: number) =>
        `Invalid sex "${value}" (sheet ${sheet}, row ${row})`,
      missingAthleteName: (sheet: string, row: number) =>
        `Missing last name or first name (sheet ${sheet}, row ${row})`,
      missingSex: (sheet: string, row: number) =>
        `Missing sex (sheet ${sheet}, row ${row})`,
      missingDiscipline: (sheet: string, row: number) =>
        `Missing discipline (sheet ${sheet}, row ${row})`,
      missingAgeCategory: (sheet: string, row: number) =>
        `Missing age category (sheet ${sheet}, row ${row})`,
      missingWeightCategory: (sheet: string, row: number) =>
        `Missing weight category (sheet ${sheet}, row ${row})`,
      // Fallback when Prisma rejects null catAgeId/catPoidId (should be rare after row validation)
      categoryRequiredDb:
        "Age or weight category is missing - fill age_category and weight_category.",
      noGroupsInWorkbook: "No groups found in the workbook",
      organisationNotMatched: (value: string) =>
        `Organisation "${value}" was not found in your organisations - the form organisation was kept.`,
      capabilityFfForce: "FFForce sheet",
      capabilityGeneric: "Excel import",
    },
  },
  browseFiles: "Browse files",
  supportedFiles(extensions: string[]) {
    return `Supported files: ${extensions?.join(", ").toUpperCase()}`;
  },
  webClient: "Web Client",
  athlete: "Athlete",
  home: "Home",
  webHome: {
    title: "Competition Management Solution",
    subtitle:
      "Unlock seamless competition management with Meet Manager! Streamline athlete registration, event organization, and result tracking effortlessly. Try now and revolutionize your competitions!",
    features: {
      title: "Features",
      subtitle: "Varied and adapted to your needs",
      manager: {
        title: "Results Entry Interface",
        description:
          "Quickly enter attempts and track performances in real-time, without wasting time.",
      },
      displays: {
        title: "Displays",
        description:
          "Project key information on multiple screens for the public, officials, and athletes.",
      },
      groups: {
        title: "Group Management",
        description:
          "Centralize competitions and each of their groups in one place.",
      },
      widgets: {
        title: "Live Resources",
        description:
          "Integrate widgets into your live broadcast to share results in real-time.",
      },
      lights: {
        title: "Referee Lights",
        description:
          "Digitize refereeing with a simple, fast, and connected system for more fluidity on the platform.",
      },
      webApp: {
        title: "Web Application",
        description:
          "Offer the public and clubs direct access to results and rankings from any device.",
      },
    },
    pricing: {
      title: "Pricing",
      subtitle: "Plans tailored to your needs",
      leagues: {
        title: "Leagues",
        subtitle: "Plans tailored to your needs for leagues organizers",
        oneDay: {
          title: "1 day",
          price: "50 €",
          pricePerDay: "50 €/day",
          description: "Access for 1 day",
        },
        tenDays: {
          title: "10 days plan",
          price: "400 €",
          pricePerDay: "40 €/day",
          description: "Use freely throughout the year",
        },
        twentyDays: {
          title: "20 days plan",
          price: "700 €",
          pricePerDay: "35 €/day",
          description: "Use freely throughout the year",
        },
        twentyDaysPlus: {
          title: "30 days plan",
          price: "900 €",
          pricePerDay: "30 €/day + unlimited services",
          description:
            "20 days + unlimited access to viewer/loader screens + priority support",
        },
      },
      streamer: {
        subtitle: "Plans tailored to your needs for streamers of competitions",
      },
      features: {
        fullAccess: "Full application access",
        technicalSupport: "Technical support",
        prioritySupport: "Priority support",
        athleteManagement: "Athlete management",
        groupManagement: "Group management",
        yearlyValidity: "Valid for 365 days",
        unlimitedViewers: "Unlimited viewer screens",
        unlimitedLoaders: "Unlimited loader screens",
      },
    },
    contact: {
      title: "Contact Us",
      subtitle: "Have a question? Need something specific? Get in touch!",
      form: {
        name: "Name",
        email: "Email",
        subject: "Subject",
        message: "Message",
        send: "Send",
      },
    },
    services: {
      title: "Included Services",
      subtitle: "All the tools you need to make your competitions successful",
      support: {
        title: "Technical Support",
        description: "A dedicated team to assist you in using the application",
      },
      updates: {
        title: "Regular Updates",
        description: "Continuous improvements and new features",
      },
      training: {
        title: "Training",
        description: "Complete training for your team on software usage",
      },
      backup: {
        title: "Cloud Backup",
        description:
          "Your data is automatically backed up and accessible anywhere",
      },
      customization: {
        title: "Customization",
        description: "Adapt the interface to your specific needs",
      },
      export: {
        title: "Data Export",
        description: "Export your data in various standard formats",
      },
    },
    cta: {
      title: "Ready to transform your competitions?",
      subtitle: "Join the future of competition management with Meet Manager",
      demo: "Book a demo",
      try: "Try our services",
      start: "Start for free",
    },
  },
  webFooter: {
    legalSectionTitle: "Legal",
    linkTerms: "Terms of use",
    linkPrivacy: "Privacy policy",
    linkLegal: "Legal notice",
    linkCookies: "Cookies",
    brandDescription:
      "The complete solution for managing your strength sports competitions. Simplify how you run your events.",
    copyright: (year: number) =>
      `© ${year} Meet Manager. All rights reserved.`,
    developedInFrance: "Developed in France by 9/9 solutions",
  },
  analyticsBanner: {
    message:
      "We collect anonymous browsing statistics (pages visited, time spent) to improve the site. No personal data is collected.",
    privacyLink: "Learn more in our privacy policy",
    acknowledge: "Got it",
  },
  legalPages: {
    lastUpdated: (formattedDate: string) => `Last updated: ${formattedDate}`,
    legalNotice: {
      breadcrumb: "Legal notice",
      title: "Legal notice",
      section1Title: "1. Publisher",
      section1Body: `9/9 Solutions
Simplified joint-stock company (SAS)
Share capital: €2,500
Registered office: 3, place d'Orléans, 31380 Montastruc-la-Conseillère, France
Email: contact@9of9solutions.com`,
      section2Title: "2. Publication director",
      section2Body: `Marin Lespingal
As President`,
      section3Title: "3. Hosting provider",
      section3Body: `OVH
2, rue Kellermann, 59100 Roubaix, France
Phone: +33 9 72 10 10 07`,
      section4Title: "4. Intellectual property",
      section4Body:
        "The meet-manager.com website, including its structure and content (text, images, graphics, logos, icons, audio, software, etc.), is the exclusive property of Meet Manager. Any reproduction, representation, modification, publication, transmission, alteration, in whole or in part, of the site or its content, by any means and on any medium, is prohibited without the prior written consent of Meet Manager.",
      section5Title: "5. Personal data",
      section5Body:
        "In accordance with the French Data Protection Act of 6 January 1978 as amended and the General Data Protection Regulation (GDPR), you have the right to access, rectify, erase, and object to processing of your personal data. To exercise these rights, contact us at: dpo@meet-manager.com",
      section6Title: "6. Cookies",
      section6Body:
        "The site uses cookies to improve the user experience. For more information, see our cookie policy.",
      section7Title: "7. Governing law and jurisdiction",
      section7Body:
        "These legal notices are governed by French law. Any dispute shall fall within the exclusive jurisdiction of the French courts.",
      section8Title: "8. Contact",
      section8Body:
        "For any questions about these legal notices, you can contact us at: contact@9of9solutions.com",
    },
    terms: {
      breadcrumb: "Terms of use",
      title: "Terms of use",
      section1Title: "1. Acceptance of terms",
      section1Body:
        "By accessing and using Meet Manager, you agree to be bound by these terms of use, all applicable laws and regulations, and you agree that you are responsible for compliance with any applicable local laws.",
      section2Title: "2. License to use",
      section2Body:
        "Meet Manager grants you a limited, non-exclusive, non-transferable license to use the service in accordance with these terms. This license is strictly for your personal, non-commercial use.",
      section3Title: "3. User accounts",
      section3Body:
        "To access certain features, you may need to create an account. You are responsible for maintaining the confidentiality of your account and password and for restricting access to your computer.",
      section4Title: "4. Use of the service",
      section4Body: `You agree not to use the service for:
- Illegal or fraudulent activities
- Infringement of intellectual property rights
- Transmission of malicious content
- Any activity that could disrupt the service`,
      section5Title: "5. Intellectual property",
      section5Body:
        "The service and its original content, features, and functionality are owned by Meet Manager and are protected by international copyright and trademark laws.",
      section6Title: "6. Limitation of liability",
      section6Body:
        "Meet Manager shall not be liable for any direct, indirect, incidental, special, or consequential damages resulting from the use or inability to use the service.",
      section7Title: "7. Changes to the terms",
      section7Body:
        "Meet Manager reserves the right to modify these terms at any time. Changes take effect immediately after they are published on the service.",
      section8Title: "8. Contact",
      section8Body:
        "For any questions about these terms of use, please contact us at: contact@meet-manager.com",
    },
    privacy: {
      breadcrumb: "Privacy policy",
      title: "Privacy policy",
      section1Title: "1. Data collection",
      section1Body: `We collect information you provide directly when you:
- Create an account
- Use our services
- Communicate with us
- Take part in surveys or promotions`,
      section2Title: "2. Types of data collected",
      section2Body: `Personal data we may collect includes:
- Name and surname
- Email address
- Phone number
- Login credentials
- Service usage data`,
      section3Title: "3. Use of data",
      section3Body: `We use your personal data to:
- Provide and maintain our services
- Improve our services
- Communicate with you
- Ensure the security of our services
- Comply with legal obligations`,
      section4Title: "4. Data protection",
      section4Body:
        "We implement appropriate security measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction.",
      section5Title: "5. Sharing of data",
      section5Body: `We do not sell your personal data. We may share your information with:
- Our service providers
- Legal authorities when required
- Third parties with your consent`,
      section6Title: "6. Your rights",
      section6Body: `Under the GDPR, you have the right to:
- Access your data
- Rectify your data
- Erase your data
- Restrict processing
- Data portability
- Object to processing`,
      section7Title: "7. Cookies",
      section7Body:
        "We use cookies to improve your experience. You can control cookies through your browser settings.",
      section8Title: "8. Contact",
      section8Body:
        "For any questions about our privacy policy or to exercise your rights, contact our data protection officer at: dpo@meet-manager.com",
    },
    cookies: {
      breadcrumb: "Cookie policy",
      title: "Cookie policy",
      section1Title: "1. What is a cookie?",
      section1Body:
        "A cookie is a small text file placed on your device (computer, tablet, or mobile) when you visit a website. It allows the site to remember information about your visit, such as your preferred language and other settings.",
      section2Title: "2. Types of cookies we use",
      section2Intro: "We use different types of cookies:",
      section2EssentialTitle: "Essential cookies:",
      section2EssentialBody: `- Authentication and security
- Session management
- Basic preferences`,
      section2PerformanceTitle: "Performance cookies:",
      section2PerformanceBody: `- Traffic analysis
- Usage statistics
- Performance optimization`,
      section2FunctionalityTitle: "Functionality cookies:",
      section2FunctionalityBody: `- Interface customization
- Saving preferences
- Social features`,
      section3Title: "3. Retention period",
      section3Body: `We use two types of cookies by duration:
- Session cookies: deleted when you close your browser
- Persistent cookies: stored for a defined period (up to 13 months)`,
      section4Title: "4. Managing cookies",
      section4Body: `You can disable cookies at any time. Your browser can also be configured to notify you when cookies are placed on your device and ask you to accept or reject them.

To manage cookies, you can configure your browser:
- Chrome: Menu > Settings > Privacy and security > Cookies
- Firefox: Menu > Options > Privacy & Security
- Safari: Menu > Preferences > Privacy
- Edge: Menu > Settings > Privacy and services`,
      section5Title: "5. Third-party cookies",
      section5Body: `Some cookies are placed by third-party services for:
- Audience analytics (e.g. Google Analytics)
- Social features
- Personalized advertising`,
      section6Title: "6. Impact of disabling cookies",
      section6Body: `Disabling cookies may prevent the use of certain site features, including:
- Access to your personal area
- Remembering your preferences
- Some interactive features`,
      section7Title: "7. Policy updates",
      section7Body:
        "We reserve the right to modify this cookie policy at any time. Changes take effect as soon as they are published on the site.",
      section8Title: "8. Contact",
      section8Body:
        "For any questions about our cookie policy, contact us at: privacy@meet-manager.com",
    },
  },
  navigation: {
    administration: "Administration",
    licensedPortal: "Licensed space",
    displayEditorPortal: "Display editor",
    speakerPortal: "Speaker space",
    backToHome: "Back to Home",
    logIn: "Log in",
    logOut: "Log out",
  },
  // Portal shell sidebars (Licensed + Admin workspace tabs)
  portalNav: {
    licensedTitle: "Licensed space",
    adminTitle: "Administration",
    toggleSidebar: "Show / hide menu",
    collapseSidebar: "Hide menu",
    expandSidebar: "Show menu",
    groups: {
      home: "Home",
      competition: "Competition",
      display: "Display",
      live: "Live",
      speaker: "Speaker",
      accounts: "Accounts",
      competitions: "Competitions",
      federation: "Federation",
      tools: "Tools",
    },
    items: {
      profile: "My profile",
      myLicense: "My license",
      installer: "Installer",
      acceptedTerms: "Accepted terms",

      dashboard: "Dashboard",
      myMeets: "My meets",
      myRecords: "My records",
      myLevels: "My levels",
      myReferees: "My referees",
      backups: "My backups",
      layouts: "My layouts",
      themes: "Themes",
      controlRoom: "Control room",
      speakerLanding: "Speaker space",
      users: "Users",
      licenses: "Licenses",
      licenseTerms: "Terms",
      meets: "Meets",
      organisations: "Organisations",
      referees: "Referees",
      themesAdmin: "Themes",
      levels: "Levels",
      records: "Records",
      analytics: "Analytics",
      testExport: "Test Export",
      ffForceExport: "FFForce Export",
      planningTab: (name: string) => (name ? name : "Planning"),
      planningCreatedTab: (name: string) => (name ? name : "Planning"),
      layoutEditorTab: (name: string) => `Editor · ${name}`,
      meetDetailTab: (name: string) => `Meet · ${name}`,
      backupDetailTab: (name: string) => `Backup · ${name}`,
      backupMeetTab: (name: string) => `Backups · ${name}`,
      liveTab: (name: string) => `Live · ${name}`,
    },
  },
  searchHere: "Search here...",
  noMeetFound: "No meet found",
  name: "Name",
  code: "Code",
  sex: "Sex",
  male: "Male",
  female: "Female",
  league: "Organisation",
  leagues: {
    title: "Organisations",
    name: "Name",
    description: "Description",
    selectLigue: "Select an organisation",
    currentLigue: "Current organisation",
    selectExistingLigue: "Select an existing organisation",
    createNewLigue: "Create a new organisation",
  },
  keyBinds: {
    title: "Keybinds",
    keybind: "Shortcut",
    key: "Keys",
    bindingError: (value: string) => {
      return `You must combine the '${value}' key with another key.`;
    },
    pressAKey: "Press keys",
    keybindName: (keybind: string) => {
      switch (keybind) {
        case "validateLift":
          return "Validate the lift";
        case "failLift":
          return "Invalidate the lift";
        case "resetLift":
          return "Reset the lift";
        case "validateAuto":
          return "Validate the current athlete's lift";
        case "failAuto":
          return "Invalidate the current athlete's lift";
        default:
          return "";
      }
    },
  },
  catage: {
    title: "Age categories",
    name: "Name",
    nomRecord: "Record name",
    description: "Description",
    selectCatage: "Select an age category",
    currentCatage: "Current age category",
    selectExistingCatage: "Select an existing age category",
    createNewCatage: "Create a new age category",
    added: "Age category added successfully.",
    updated: "Age category updated successfully.",
    deleted: "Age category deleted successfully.",
    minAge: "Minimum age",
    maxAge: "Maximum age",
    interdit: "Forbidden",
  },
  catpoid: {
    title: "Weight categories",
    name: "Name",
    description: "Description",
    selectCatpoid: "Select a weight category",
    currentCatpoid: "Current weight category",
    selectExistingCatpoid: "Select an existing weight category",
    createNewCatpoid: "Create a new weight category",
    added: "Weight category added successfully.",
    updated: "Weight category updated successfully.",
    deleted: "Weight category deleted successfully.",
    minWeight: "Minimum weight",
    maxWeight: "Maximum weight",
    isJunior: "Is Junior",
    errorMinMax: "The maximum weight must be greater than the minimum weight.",
  },
  category: "Category",
  group: "Group",
  groups: {
    historyConflict:
      "This action cannot be applied because the data changed in the meantime. No data was overwritten. Check the current values before trying again.",
    historyRestoreUnavailable:
      "This entry cannot be restored here. A synchronized deletion requires a new copy in an existing group.",
    historyRestoredAsCopy:
      "The athlete was restored as a new entry. The original entry remains deleted.",
    status: "Status",
    statusLabels: {
      upcoming: "Upcoming",
      openerToEnter: "Opener to enter",
      completed: "Completed",
    },
    synchronisationSuccess: "Group synchronised successfully",
    action: {
      import: "Import",
    },
    confirm: {
      title: "Confirm action",
      overwriteGroup: (existing: string, incoming: string) =>
        `The group "${incoming}" will replace the existing group "${existing}". Do you want to continue?`,
      confirmLabel: "Overwrite",
      cancelLabel: "Cancel",
      exceedBarChangeLimit: {
        title: "Change limit reached",
        message: (liftField: string, count: number, max: number) =>
          `This bar (${liftField}) has already been changed ${count} times out of ${max} allowed. Do you want to continue anyway?`,
      },
    },
    success: {
      imported: "Group successfully imported",
    },
    error: {
      default: "An error occurred",
      duplicateAthleteImport: (athleteName: string, groupName: string) =>
        `Import not allowed: athlete "${athleteName}" already exists in group "${groupName}".`,
    },
    new: "New group",
    title: "Groups",
    plateauSection: (n: number) => `Platform ${n}`,
    noPlateauSection: "No platform",
    name: "Name",
    description: "Description",
    selectGroup: "Select a group",
    currentGroup: "Current group",
    selectExistingGroup: "Select an existing group",
    createNewGroup: "Create a new group",
    added: "Group added successfully.",
    updated: "Group updated successfully.",
    deleted: "Group deleted successfully.",
    plateauNumber: "Platform n°",
    groupNumber: "Group n°",
    jury: "Jury",
    chefPlateau: "Platform chief",
    assesseur1: "Assessor 1",
    assesseur2: "Assessor 2",
    secretaire: "Secretary",
    observations: "Observations",
    editModal: {
      title: "Edit group",
      open: "Edit group",
      save: "Save",
      cancel: "Cancel",
    },
    selectFormatModal: {
      title: "Choose the group format",
      cancel: "Cancel",
      loading: "Loading disciplines…",
      syncError: "Unable to load custom disciplines.",
    },
    createModal: {
      title: "Create a group",
      save: "Save",
      cancel: "Cancel",
    },
    actions: "Groups actions",
    rowActionsColumn: "Actions",
    exportFeuilleFederation: "Export as federation sheet",
    exportFeuilleFederationMeetManager: "Federation sheet (Meet Manager)",
    exportFeuilleFederationMeetManagerDescription:
      "Export as federation sheet (Meet Manager)",
    exportFeuilleFederationMacroExcel: "Federation sheet (Macro Excel)",
    exportFeuilleFederationMacroExcelDescription:
      "Export as federation sheet (Macro Excel)",
    exportSuccess: "Export successful",
    exportResultat: "Export results",
    exportOpenPowerlifting: "Export as OpenPowerlifting",
    selectSheetDate: {
      title: "Select federation sheet",
      question: "Which federation sheet do you want to use?",
      select: "Select",
      cancel: "Cancel",
      loading: "Loading federation sheets...",
      error: "Error loading federation sheets",
    },
    tooltips: {
      synchroGroup: "Synchronise group",
      autoProposal: "Automatically proposed weight",
      auto: "Attempt automatically validated by referees",
      overturn: "The jury has overturned the decision",
      automaticRefereeing:
        "Automatically validates or invalidates the current athlete's attempt based on the referee lights",
      automaticRefereeingDisabled:
        "You must enable the refereeing lights to use automatic refereeing.",
      undo: "Undo last modification (Ctrl+Z)",
      redo: "Redo last undone modification (Ctrl+Y)",
      validate: (shortcut: string, shortcutAuto: string) =>
        `Validate selected athlete's lift (${shortcut} or ${shortcutAuto} for current athlete)`,
      fail: (shortcut: string, shortcutAuto: string) =>
        `Mark lift as failed (${shortcut} or ${shortcutAuto} for current athlete)`,
      reset: (shortcut: string) => `Reset lift status (${shortcut})`,
      orderByBar: "Sort athletes by lifting order",
      orderByCategory: "Sort athletes by category",
      start: "Start group display",
      stop: "Stop group display",
      addAthlete: "Add a new athlete to the group",
      deleteAthlete: "Remove athlete from group",
      moveAthlete: "Move athlete from group",
      export: "Export group data",
      columns: "Manage column display",
      density: "Adjust display density",
      exportFeuilleFederation: "Export as federation sheet",
      exportOpenPowerlifting:
        "Export competition data in OpenPowerlifting format",
      barChanges: (count: number, max: number) =>
        `${count} change(s) out of ${max} allowed`,
      ticketTime: "Ticket time",
      ticketTimeExpired: "Ticket time expired",
    },
    messages: {
      barChangesCancelled: "Bar change cancelled",
    },
    exports: "Exports",
    export: "Export",
  },
  barOrder: {
    title: "Bar Order",
    print: "Print Bar Order",
    squatOrder: "Bar Order - Squat",
    benchOrder: "Bar Order - Bench Press",
    deadliftOrder: "Bar Order - Deadlift",
    group: "Group",
    athlete: "Athlete",
    weight: "Weight (kg)",
    order: "Order",
    selectGroups: "Select groups to print",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    noGroupSelected: "No group selected",
  },
  tickets: {
    tickets: "Tickets",
    discipline: "Discipline:",
    lastName: "Last Name:",
    firstName: "First Name:",
    drawNum: "Draw Number:",
    sex: "Sex:",
    birthDate: "Date of Birth:",
    ageCategory: "Age Category:",
    athleteVisa: "Athlete Visa:",
    club: "Club:",
    licenceNumber: "Licence Number:",
    bodyweight: "Bodyweight:",
    opener: "Opening Bar",
    firstTry: "1st Attempt",
    secondTry: "2nd Attempt",
    thirdTry: "3rd Attempt",
    modifThirdTry: "Modify 3rd Attempt",
    result: "Result",
    squat: "Squat",
    bench: "Bench Press",
    deadlift: "Deadlift",
    supportHeight: "Support Height:",
    hauteurDC: "H support",
    hauteurSecu: "H safety",
    cales: "Plates:",
    height: "Height:",
    yes: "Y",
    no: "N",
  },
  openers: "Openers",
  identification: "Identification",
  techniques: "Techniques",
  athletes: {
    title: "Athletes",
    licence: "License",
    club: "Club",
    sexe: "Gender",
    dateNaissance: "Date of Birth",
    nom: "Last Name",
    prenom: "First Name",
    fullName: "Full Name",
    pdc: "Body Weight",
    indice: "Index",
    /** Column header for realised points; depends on meet scoring formula. */
    points: (formula?: string) =>
      String(formula ?? "").toUpperCase() === "DOTS" ? "DOTS" : "GL Points",
    lot: "Lot",
    squat1: "1",
    squat2: "2",
    squat3: "3",
    bench1: "1",
    bench2: "2",
    bench3: "3",
    deadlift1: "1",
    deadlift2: "2",
    deadlift3: "3",
    totalkg: "Total KG",
    totalAnnonce: "Total Annonced",
    /** Column header for announced points; depends on meet scoring formula. */
    pointsAnnonced: (formula?: string) =>
      String(formula ?? "").toUpperCase() === "DOTS"
        ? "DOTS Announced"
        : "GL Points Announced",
    placement: "Placement",
    hauteurSquat: "Squat Height",
    hauteurDc: "Bench Height",
    hauteurSecu: "Safety Height",
    cales: "Blocks",
    positionRack: "Rack Position",
    // Display labels; storage remains EXT/INT/1/2 D/1/2 G
    rackPositions: {
      ext: "OUT",
      int: "IN",
      demiDroit: "1/2 R",
      demiGauche: "1/2 L",
    },
    catAge: "Age Category",

    catAgeN1: "Age Category N+1",
    catPoid: "Weight Category",
    isDisqualified: "Disqualified/Guest",
    dq: "DQ",
    hm: "DQ",
    disqualified: "Disqualified",
    niveau: "Level",
    niveauAnnonced: "Level Annonced",
    niveauN1: "Level N+1",
    niveauDpt: "dept.",
    discipline: "Discipline",
  },
  powerscout: {
    columnHeader: "Info",
    notSupported: "Discipline not supported by Powerscout",
    notSynced: "Not synchronized",
    notFound: "Not found in Powerscout",
    syncError: "Synchronization error",
    syncStarted: (count: number) =>
      `Powerscout synchronization started for ${count} athlete${count > 1 ? "s" : ""}`,
    serverUnreachable:
      "Powerscout server is not reachable. Please check your network connection.",
    pending: "Pending",
    syncedOn: (date: string) => `Synced on ${date}`,
    squatBest: (kg: string, meetName: string, meetDate: string) =>
      `Squat: ${kg}kg (${meetName}, ${meetDate})`,
    benchBest: (kg: string, meetName: string, meetDate: string) =>
      `Bench: ${kg}kg (${meetName}, ${meetDate})`,
    deadliftBest: (kg: string, meetName: string, meetDate: string) =>
      `Deadlift: ${kg}kg (${meetName}, ${meetDate})`,
    totalBest: (kg: string, meetName: string, meetDate: string) =>
      `Total: ${kg}kg (${meetName}, ${meetDate})`,
    glBest: (points: string, meetName: string, meetDate: string) =>
      `GL: ${points} points (${meetName}, ${meetDate})`,
    squat1SuccessRate: (rate: string) => `Squat 1 success rate: ${rate}%`,
    squat2SuccessRate: (rate: string) => `Squat 2 success rate: ${rate}%`,
    squat3SuccessRate: (rate: string) => `Squat 3 success rate: ${rate}%`,
    bench1SuccessRate: (rate: string) => `Bench 1 success rate: ${rate}%`,
    bench2SuccessRate: (rate: string) => `Bench 2 success rate: ${rate}%`,
    bench3SuccessRate: (rate: string) => `Bench 3 success rate: ${rate}%`,
    deadlift1SuccessRate: (rate: string) => `Deadlift 1 success rate: ${rate}%`,
    deadlift2SuccessRate: (rate: string) => `Deadlift 2 success rate: ${rate}%`,
    deadlift3SuccessRate: (rate: string) => `Deadlift 3 success rate: ${rate}%`,
    na: "N/A",
    selectLifter: "Select Powerscout athlete",
    multipleMatches: "Multiple athletes found - click to select",
    noMatchesFound: "No athlete found",
    birthYear: "Birth year",
    country: "Country",
    bestTotal: "Best total",
    lastCompetition: "Last competition",
    select: "Select",
    actions: "Actions",
    name: "Name",
    sex: "Sex",
    searchPlaceholder: "Search by name…",
    searchButton: "Search on Powerscout",
    changeLink: "Change",
    searchTitle: "Powerscout search",
    linkError: "Unable to link Powerscout athlete",
    meetNotLive: "The meet is not live",
    authRequired: "Sign in required to search on Powerscout",
    sessionExpired: "Session expired, please sign in again",
    linkSuccess: "Powerscout athlete linked",
    competitionsTitle: "Competition history",
    competitionsButton: "Competitions",
    competitionsEmpty: "No competitions found",
    competitionsLoadError: "Unable to load Powerscout history",
    competitionDate: "Date",
    competitionName: "Competition",
    competitionTown: "Town",
    competitionFederation: "Federation",
    competitionRegion: "Region",
    competitionDiscipline: "Discipline",
    competitionWeightClass: "Weight class",
    competitionAgeClass: "Age class",
    competitionAge: "Age",
    competitionCategories: "Categories",
    competitionBodyweight: "Bodyweight",
    competitionPlace: "Place",
    competitionTotal: "Total",
    competitionSquat: "Squat",
    competitionBench: "Bench press",
    competitionDeadlift: "Deadlift",
    competitionGoodlift: "GL",
    competitionDots: "Dots",
    competitionWilks: "Wilks",
    competitionMinima: "Minima",
    competitionResult: "Result",
    competitionHistory: "View history",
    hypotheticalRankTooltip: "Powerscout ranking",
    hypotheticalRankTitle: "PowerScout ranking",
    hypotheticalRankSelected: "Selected performance",
    hypotheticalRankLoadError: "Unable to load Powerscout ranking",
    hypotheticalRankEmpty: "No results in this universe",
    hypotheticalRankShowMore: "Show more",
    hypotheticalRankNational: "National",
    hypotheticalRankInternational: "International",
    hypotheticalRankFilterAge: "Age category",
    hypotheticalRankAgeSelectPlaceholder: "Select age categories",
    hypotheticalRankAgeSelectEmpty: "All ages",
    hypotheticalRankFilterWeight: "Weight category",
    hypotheticalRankWeightSelectPlaceholder: "Select weight categories",
    hypotheticalRankWeightSelectEmpty: "All weight categories",
    hypotheticalRankWeightJunior: "Junior",
    hypotheticalRankFilterFederation: "Federation",
    hypotheticalRankFederationSelectPlaceholder: "Select federations",
    hypotheticalRankFederationSelectEmpty: "All federations",
    hypotheticalRankFilterYearFrom: "From year",
    hypotheticalRankFilterYearTo: "To year",
    hypotheticalRankRank: "Rank",
    hypotheticalRankKg: "Weight",
    hypotheticalRankOf: (rank: number, total: number) => `${rank} / ${total}`,
    hypotheticalRankBar: (lift: string, kg: string) => `${lift} - ${kg} kg`,
    hypotheticalRankRecords: "Federation records",
    hypotheticalRankRecordSquat: "Squat record",
    hypotheticalRankRecordBench: "Bench press record",
    hypotheticalRankRecordDeadlift: "Deadlift record",
    hypotheticalRankRecordTotal: "Total record",
    hypotheticalRankRecordBy: (name: string) => `by ${name}`,
    hypotheticalRankNoRecord: "No record",
  },
  success: {
    athleteAdded: "Athlete added successfully",
    importSuccess: {
      records: "Records import completed successfully",
      niveaux: "Levels import completed successfully",
    },
  },
  error: {
    noCellSelected: "No cell selected. Please click on a cell.",
    invalidCell: "Invalid cell. Please click on a valid cell.",
    athleteAddFailed: "Error while adding an athlete",
    exportError: "Error during export",
    importError: "Error during import",
    editError: "Error while editing an athlete",
    wifiInfoError:
      "Unable to retrieve Wi-Fi network information. Please try again.",
    invalidDateFormat: (value: string, row?: number) =>
      `Invalid date format: "${value}" ${row ? `(row: ${row})` : ""}`,
    // Specific error messages for license and counting issues
    licenseNotFound: "No valid license found. Please check your license.",
    quotaCheckFailed:
      "Unable to verify athlete quota. Please try again or reconnect online.",
    dataCorrupted:
      "Corrupted data detected. Please reconnect online to synchronize your data.",
    serverUnavailable:
      "Unable to contact server. Please check your internet connection.",
    validationFailed: "Critical validation failed. Please reconnect online.",
    // Messages d'erreur spécifiques pour l'ajout d'athlètes
    athleteLimitReached: {
      title: "Athlete limit reached",
      message: "You have reached the athlete limit of your license",
      details: "Limit reached: {current}/{max} athletes",
      action: "Contact your administrator to increase your quota",
    },
    athleteQuotaExceeded: {
      title: "Athlete quota exceeded",
      message: "Cannot add this athlete as it would exceed your quota",
      details:
        "Attempt to add: {current} + {nonFinished} unfinished + 1 new = {total}/{max}",
      action: "Finish current athletes first or contact your administrator",
    },
    athleteDataCorrupted: {
      title: "Corrupted data detected",
      message: "Athlete counting data appears to have been modified",
      action: "Please reconnect online to synchronize your data",
    },
    // Generic error messages
    databaseError: "Database error",
    unknownError: "Unknown error",
    retryOrContactSupport: "Please try again or contact support",
    // Functions for athlete error messages
    getAthleteLimitError: (reason?: string) => ({
      title: "Athlete limit reached",
      message: reason || "You have reached the athlete limit of your license",
      action: "Contact your administrator to increase your quota",
    }),
    getAthleteQuotaError: (reason?: string) => ({
      title: "Athlete quota exceeded",
      message:
        reason || "Cannot add this athlete as it would exceed your quota",
      action: "Finish current athletes first or contact your administrator",
    }),
    getAthleteDataCorruptedError: (reason?: string) => ({
      title: "Corrupted data detected",
      message: reason || "Athlete counting data appears to have been modified",
      action: "Please reconnect online to synchronize your data",
    }),
    getDatabaseError: (details?: string) => ({
      title: "Database error",
      message: "An error occurred while creating the athlete",
      details: details || "Unknown error",
      action: "Please try again or contact support",
    }),
    requiredField(name: string) {
      return `${name} is required`;
    },
    importAthleteLimitExceeded: (
      athletesToImportCount: number,
      nonFinishedCount: number,
      maxAthletes: number,
    ) =>
      `Import impossible: adding ${athletesToImportCount} athletes would exceed your license limit (${nonFinishedCount} unfinished + ${athletesToImportCount} to import > ${maxAthletes} allowed)`,
    /** Message consistent with canAddResult (cumulative + unfinished + to import). */
    importAthleteLimitExceededFull: (
      cumulativeCount: number,
      nonFinishedCount: number,
      toImportCount: number,
      maxAthletes: number,
    ) => {
      const total = cumulativeCount + nonFinishedCount + toImportCount;
      return `Import not allowed: athlete limit reached (${total}/${maxAthletes}) - ${cumulativeCount} finished + ${nonFinishedCount} unfinished + ${toImportCount} to import.`;
    },
  },
  add: "Add",
  afficher: "Show",
  stopper: "Stop",
  updateSuccess: "Element updated successfully",
  addSuccess: "Element saved successfully",
  deleteSuccess: "Element deleted successfully",
  deleteError: "Error during deletion",
  select: "Select",
  discipline: "Discipline",
  disc: "Disc.",

  edit: "Edit",

  print: "Print",
  save: "Save",
  delete: "Delete",
  validate: "Validate",
  chargement: "Loading",
  undo: "Undo",
  redo: "Redo",
  barreReussie: "Successful lift",
  automaticRefereeing: "Auto refereeing",
  barreRatee: "Failed lift",
  resetBarre: "Reset lift",
  ordreBarre: "Bar order",
  triCate: "Sort by category",
  refuse: "Refuse",
  invalidate: "Invalidate",
  create: "Create",
  cancel: "Cancel",
  /** Generic DataGrid / toolbar column header for row actions */
  actionsColumn: "Actions",
  import: "Import",
  deleteConfirm: "Do you really want to delete this item?",
  deleteAthleteConfirm: "Do you really want to delete this athlete?",
  squat: "Squat",
  sq: "Sq",
  bench: "Bench",
  dc: "B",
  deadlift: "Deadlift",
  sdt: "D",
  annonce: "Announced",
  best: "Best",
  customDisciplineExports: {
    section: "File exports",
    intro: "Independent CSV, Excel and PDF profiles.",
    addProfile: "New profile",
    copyProfile: "Duplicate",
    deleteProfile: "Delete profile",
    profile: "Profile",
    name: "Profile name",
    enabled: "Available in Meet Manager",
    general: "Formats and file",
    columns: "Columns and values",
    rows: "Athletes and rows",
    template: "Excel template",
    preview: "Preview",
    fileName: "File name · group name when empty",
    sheetName: "Excel worksheet",
    addColumn: "Add column",
    label: "Header",
    field: "Source field",
    type: "Value type",
    excelColumn: "Excel column",
    width: "Excel width · characters",
    numberFormat: "Excel number format",
    valueBehavior: "Value and style · Behavior",
    copyField: "Copy field calculation",
    signedAttempt: "Failed attempt → negative value",
    statusField: "Status field",
    failureValue: "Failure status value",
    applySigned: "Create rule",
    remove: "Remove",
    up: "Move up",
    down: "Move down",
    visible: "Export this column",
    style: "Style",
    cellStyle: "Column cells",
    columnHeaderStyle: "Column header",
    defaultStyle: "Cells · default style",
    headerStyle: "Headers · default style",
    font: "Font",
    fontSize: "Size · points",
    bold: "Bold",
    italic: "Italic",
    underline: "Underline",
    fill: "Fill",
    textColor: "Text",
    horizontal: "Horizontal alignment",
    vertical: "Vertical alignment",
    wrap: "Wrap text",
    inherit: "Inherited style",
    border: "Borders",
    borderColor: "Border color",
    borderStyle: "Line",
    top: "Top",
    right: "Right",
    bottom: "Bottom",
    left: "Left",
    center: "Center",
    middle: "Middle",
    thin: "Thin",
    medium: "Medium",
    thick: "Thick",
    dashed: "Dashed",
    dotted: "Dotted",
    double: "Double",
    filter: "Include athletes · boolean Behavior",
    order: "Advanced ordering · Behavior",
    sectionRule: "Section key · Behavior",
    addSort: "Add sort rule",
    ascending: "Ascending",
    descending: "Descending",
    startRow: "First Excel data row",
    headerRow: "Excel header row",
    keepTemplateHeader: "Empty: keep the template headers",
    keepTemplateStyle: "Empty: keep each template cell’s style",
    rowStep: "Step between athletes · Excel rows",
    gap: "Blank rows between sections",
    endRow: "Last allowed row",
    rowHeight: "Row height · points",
    headerHeight: "Header height · points",
    copyStyleFromRow: "Copy style from Excel row · optional",
    templateDataEndRow: "Last athlete row reserved in the template",
    templateDataEndRowHint:
      "Empty: fixed template. Otherwise, extra rows move the template footer and its cell bindings down.",
    clearRange: "Clear mapped columns inside the bounded row range",
    uploadTemplate: "Import .xlsx template",
    removeTemplate: "Remove template",
    templateHint:
      "The template is saved with the discipline and available offline. Only mapped cells are filled.",
    templateLimit: "A valid .xlsx template, up to 5 MB.",
    templateError: "Unable to read this Excel template.",
    bindings: "Fixed template cells",
    addBinding: "Add fixed cell",
    cell: "Cell · e.g. B4",
    sheet: "Worksheet",
    delimiter: "CSV delimiter",
    bom: "UTF-8 encoding with BOM",
    includeHeader: "Include CSV headers",
    protectFormulas: "Neutralize formulas in CSV text values",
    landscape: "Landscape PDF",
    paperSize: "PDF paper",
    margin: "PDF margins · mm",
    title: "PDF title",
    sampleData: "Test data",
    athlete: "Athlete",
    addAthlete: "Add test athlete",
    value: "Value",
    group: "Group",
    previewRows: "Destination rows",
    downloadPreview: "Download Excel preview",
    noProfiles: "No export profile configured.",
    exportFile: "Export",
    scope: "Scope",
    thisGroup: "This group",
    allGroups: "All groups of this discipline",
    format: "Format",
    close: "Close",
    loading: "Preparing preview…",
    totalRows: "Exported athletes",
    rowNumber: "Excel row",
    capacityError:
      "Athletes exceed the last allowed row. Increase the range in administration.",
    invalid:
      "The profile contains an invalid setting. Check columns, rows and template.",
    previewError: "Unable to prepare the export.",
    templateMode: "Excel from template",
    tableMode: "Excel without template",
    noRows: "No athlete matches this profile’s filter.",
    unlimited: "Unlimited",
    confirmDelete: "Delete this export profile?",
    text: "Text",
    number: "Number",
    boolean: "Boolean",
    date: "Date",
    auto: "Automatic",
    reset: "Reset",
    previewNote: "First rows previewed; the file includes all rows.",
    success: "Export complete.",
    none: "None",
    true: "Yes",
    false: "No",
  },
  customDisciplineResults: {
    emptyResults: "No athlete currently meets the configured result rules.",
    section: "Results and printing",
    independent:
      "Competition result and print columns and rules, independent from editing and live views.",
    order: "Results order",
    podiumOrder: "Podium order",
    sexField: "Sex field",
    ageField: "Age category field",
    weightField: "Weight category field",
    eligibility: "Athletes eligible for results",
    loadError: "Unable to load custom results.",
  },
  customDisciplineWeb: {
    toolbar: {
      title: "Command bar",
      viewMode: "Display mode",
      followOff: "Following off",
      preview: "Interactive preview · sample data",
      add: "Add control",
      preset: "Use legacy controls",
      gridPreset: "Use legacy grid style",
      styles: "Control states · Display Editor",
      kind: "Control type",
      kinds: {
        TITLE: "Group name",
        SEARCH: "Athlete search",
        BUTTON: "Button",
        TOGGLE: "Exclusive toggle buttons",
      },
      action: "Action",
      actions: {
        REFRESH: "Refresh configuration",
        RESET_VIEW: "Reset view",
        CLEAR_SEARCH: "Clear search",
        VIEW_MODE: "Switch view · categories / live",
        FOLLOW_CURRENT: "Enable / disable athlete following",
        INPUT: "Local value used by Behavior",
      },
      inputKey: "Local value key",
      inputHint:
        "Available in Behavior: inputValues.<key>. Only changes this web view.",
      defaultValue: "Initial value",
      searchFields: "Search fields · all when none selected",
      optionValue: "Value",
      optionLabel: "Label",
      addOption: "Add option",
      options: "Toggle options",
      icon: "Icon",
      automaticWidth: "Automatic width",
      styleHint:
        "Each control has normal, hover, selected and disabled states in the Display Editor.",
    },
    tooltip: "Tooltip · Display Editor",
    section: "Live web view",
    independent: "This view has its own columns and layouts.",
    create: "Create an independent web view",
    empty: "The web view for this group has not been configured yet.",
    columns: "Published columns",
    addColumn: "Add column",
    newColumn: "New column",
    source: "Source field",
    computed: "Computed value",
    label: "Title",
    width: "Width",
    pin: "Freeze",
    noPin: "Scroll normally",
    left: "Left",
    right: "Right",
    visible: "Visible",
    up: "Move up",
    down: "Move down",
    remove: "Remove",
    cell: "Cell · Display Editor",
    header: "Header · Display Editor",
    calculation: "Calculation · Behavior",
    styles: "Grid style · Display Editor",
    liveOrder: "Live order · Behavior",
    categoryOrder: "Ranking order · Behavior",
    categoryColumns: "Group categories by",
    defaultMode: "Initial mode",
    live: "Live",
    category: "Categories",
    follow: "Follow current athlete",
    refresh: "Refresh view",
    search: "Search athlete",
    loading: "Loading web view…",
    error: "Unable to load the web view.",
    retry: "Retry",
    noAthletes: "No athletes in this group.",
    noColumns: "No published columns.",
    copyAction: "Copy ordering from an action",
    copy: "Copy",
    apply: "Apply to draft",
    back: "Back to web view",
    headerGroup: "Header group",
    rowCount: "athletes",
    details: "Athlete details",
  },
  admin: {
    title: "Administration",
    tabs: {
      licenseTerms: "Terms",
      meets: "Meets",
      analytics: "Analytics",
      testExport: "Test export",
    },
    ligues: "Organisations",
    meet: "Meet",
    items: "item(s)",
    admintable: "Admin Table",
    config: {
      title: "Configuration",
      id: "ID",
      value: "Value",
    },
    refresh: "Refresh",
    resetGrid: "Reset display",
    resetGridConfirm:
      "Are you sure you want to reset the grid display? All column preferences (width, visibility, order) will be lost.",
    resetGridSuccess: "Grid display reset successfully",
    meets: {
      title: "Meets Management",
      licenseReport: "License Usage Report",
      confirmDelete: (meetName: string) =>
        `Are you sure you want to delete the meet "${meetName}"?`,
      columns: {
        name: "Name",
        city: "City",
        organisation: "Organisation",
        license: "License",
        startDate: "Start",
        endDate: "End",
        status: "Status",
        createdAt: "Created on",
        togglePublic: "Public/Private",
      },
      status: {
        public: "Public",
        private: "Private",
      },
      actions: {
        makePublic: "Public",
        makePrivate: "Private",
        processing: "...",
      },
      errors: {
        statusChangeError: "Error while changing status",
      },
      allOrganisations: "All organisations",
      form: {
        title: {
          create: "Create a new meet",
          edit: "Edit meet",
        },
        fields: {
          name: "Meet name",
          city: "City",
          startDate: "Start date",
          endDate: "End date",
          isPublic: "Public meet",
        },
        validation: {
          nameRequired: "Name is required",
          cityRequired: "City is required",
          startDateRequired: "Start date is required",
          endDateRequired: "End date is required",
          endDateAfterStart: "End date must be after start date",
        },
        actions: {
          create: "Create",
          edit: "Edit",
          cancel: "Cancel",
          saving: "Saving...",
        },
      },
    },
    organisations: {
      title: "Organisations",
      empty: "No organisation",
      create: "Create",
      edit: "Edit",
      delete: "Delete",
      name: "Name",
      parent: "Parent organisation",
      noParent: "None (root)",
      confirmDelete: "Are you sure you want to delete this organisation?",
      confirmDeleteWithChildren:
        "This organisation has children or linked users/referees. Deleting it may affect them. Continue?",
      createTitle: "Create organisation",
      editTitle: "Configure organisation",
      identityTitle: "Identity",
      cancel: "Cancel",
      save: "Save",
      loading: "Loading...",
      error: "Error loading organisations",
      code: "Code",
      countryCode: "Country code",
      recordLabel: "Record label",
      recordColor: "Record colour",
      recordBadge: "Record logo or flag",
      federationSettings: "Federation settings",
      sourcesTitle: "Data sources",
      sourceDiscipline: "Disciplines",
      sourceAgeCategory: "Age categories",
      sourceWeightCategory: "Weight categories",
      sourceLevel: "Minimums / levels",
      sourceRecord: "Records",
      sourceReferee: "Referees",
      exportsTitle: "Available export formats",
      exportOpenPowerlifting: "OpenPowerlifting",
      exportFfForceSheet: "FFForce meet sheet",
      exportFfForceMacro: "FFForce macros",
      importsTitle: "Available import formats",
      importFfForceSheet: "FFForce meet sheet",
      importGenericSheet: "Excel import",
      openPowerliftingFederation: "OpenPowerlifting federation code",
      openPowerliftingCountry: "OpenPowerlifting country",
      powerscoutFederationName: "Powerscout federation name",
      powerscoutFederationNameHint:
        "Exact (or partial) name in Powerscout for national rankings (e.g. FFForce, KDKS). Empty = FFForce.",
      defaultLevelName: "Default level",
      defaultLevelNameHint:
        "Label shown when no minima is reached (e.g. dept.). Empty = dept.",
      uploadBadge: "Upload logo/flag",
      deleteBadge: "Delete logo",
      noBadge: "No logo uploaded",
      badgeDeleted: "Logo deleted",
      deleteBadgeError: "Could not delete the logo/flag",
      badgeFileHint: "PNG, JPEG or WebP - 2 MB maximum",
      recordsTitle: "Record import / export",
      downloadEmptyTemplate: "Download empty template",
      downloadCurrentRecords: "Export current records",
      importRecords: "Import records",
      importedRecords: (count: number) => `${count} record(s) imported`,
      levelsTitle: "Level / minima import / export",
      downloadCurrentLevels: "Export current levels",
      importLevels: "Import levels",
      importedLevels: (count: number) => `${count} level(s) imported`,
      levelsWorkbookError: "Could not process the levels file",
      configurationSaved: "Settings saved",
      uploadError: "Could not upload the logo/flag",
      workbookError: "Could not process the records file",
      taxonomyTitle: "Organisation-owned reference data",
      taxonomyHint:
        "These items can be selected as a source by this organisation or its sub-organisations.",
      manageTaxonomy: "Manage categories and disciplines",
      addDiscipline: "Add discipline",
      addAgeCategory: "Add age category",
      addWeightCategory: "Add weight category",
      disciplineName: "Discipline name",
      disciplineCode: "Discipline code",
      competitionFormat: "Competition format",
      fullPower: "Full power",
      singleLift: "Single lift",
      categoryName: "Category name",
      categoryCode: "Category code",
      recordName: "Records name",
      ageMin: "Minimum age",
      ageMax: "Maximum age",
      weightMin: "Minimum weight",
      weightMax: "Maximum weight",
      sex: "Sex",
      juniorWeight: "Junior weight category",
      openAge: "Open category",
      displayOrder: "Display order",
      noTaxonomyRows: "No configured items",
      taxonomySaved: "Reference data saved",
      taxonomyError: "Could not update reference data",
      taxonomyDeleteConfirm: (name: string) => `Permanently delete “${name}”?`,
    },
    customDisciplines: {
      cellSection: "Cell",
      headerSection: "Header",
      headerGroupSection: "Column group header",
      cellHeight: "Cell height",
      headerHeight: "Header height",
      headerGroupHeight: "Column group height",
      canvasBackground: "Canvas background",
      gridHeightHint:
        "The grid uses the largest configured height to keep columns aligned.",
      workspace: {
        previewSample: "Preview · sample data",
        fitLayout: "Fit layout",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        fitGraph: "Fit graph",
        keepEditing: "Keep editing",
        discardDraft: "Discard draft",
        customizeCell: "Customize cell",
        sharedGroupActions: "In the group actions column",
        variants: {
          contained: "Filled",
          outlined: "Outlined",
          text: "Text",
        },
        sizes: {
          small: "Compact",
          medium: "Standard",
          large: "Large",
        },
        colors: {
          inherit: "Default",
          primary: "Primary",
          secondary: "Secondary",
          success: "Success",
          error: "Error",
          info: "Information",
          warning: "Warning",
        },
        iconTypes: {
          MUI: "Icon library",
          SVG: "Uploaded image",
        },
        window: "window",
        catalog: "Discipline catalog",
        languages: "Translations",
        configure: "Configure group",
        identity: "Discipline identity",
        newDiscipline: "Create discipline",
        overview: "Overview",
        groupFields: "Group fields",
        athleteFields: "Athlete fields",
        pageActions: "Buttons & shortcuts",
        gridActions: "List icons",
        windows: "Windows & documents",
        gridsForms: "Grids & forms",
        screens: "Competition displays",
        transfer: "Templates & transfers",
        structure: "Structure",
        interactions: "Interactions",
        appearance: "Appearance",
        tools: "Tools",
        back: "Disciplines",
        backToSection: "Back to details",
        saved: "Saved",
        unsaved: "Unsaved changes",
        apply: "Apply to draft",
        discard: "Discard unsaved changes?",
        search: "Search by name or key",
        definitions: "Definitions",
        untitled: "Untitled",
        newDefinition: "New item",
        noResults: "No results",
        noDefinitions: "No items yet. Use Add to get started.",
        properties: "Properties",
        logic: "Logic · Behavior",
        design: "Appearance · Display Editor",
        configured: "Customized",
        default: "Standard",
        openEditor: "Open editor",
        groupGrid: "Group list",
        athleteGrid: "Athlete grid",
        groupForm: "Group form",
        athleteForm: "Athlete form",
        forms: "Entry forms",
        grids: "Lists & tables",
        importTitle: "Import configuration",
        exportTitle: "Export configuration",
        templates: "Starting templates",
        exportLabels: "Export translation keys",
        deleteType: "Delete group type",
        active: "Active",
        inactive: "Inactive",
        settings: "Identity",
        fields: "fields",
        actions: "actions",
        modals: "windows",
        openBehavior: "Open Behavior",
        editCell: "Cell & headers",
        addGroupField: "Add group field",
        addAthleteField: "Add athlete field",
        addPageAction: "Add button or shortcut",
        addGridAction: "Add icon",
        addModal: "Add window",
        copyHint: "JSON · fields, actions, Behavior and layouts",
        templateHint: "For starting a new group type",
        newGroupType: "Create group type",
        saving: "Saving…",
        saveError: "Could not save. Your changes are preserved.",
        allDisciplines: "All disciplines",
        allLanguages: "All languages",
        translationsSearch: "Search translations",
        emptyCatalog: "Create your first discipline",
        changesPending: "Draft",
        editorDiscard: "Leave this editor without applying changes?",
      },
      importConfiguration: "Import configuration",
      exportConfiguration: "Export configuration",
      invalidConfiguration: "Invalid group configuration.",
      configurationImported: "Configuration loaded. Save to apply it.",

      configurationHelp:
        "Behavior defines calculations and actions. Display Editor defines cells, dialogs, printed documents and competition screens.",
      startFromTemplate: "Start from a template:",
      templateFullPower: "Full power (3 lifts)",
      templateBench: "Bench press",
      competitionDisplays: "Competition screens",
      competitionDisplaysHelp:
        "Default layouts for this discipline. Local screen assignments take priority.",
      gridPresentationHelp:
        "For grids, edit the style and height of the named samples while keeping their IDs. Each field has its own cell and header editor. In a list mixing several group types, each style applies only to rows of that type; shared headers keep the standard style.",
      athleteScreen: "Athlete",
      loaderScreen: "Loaders",
      rankingScreen: "Ranking",
      publicScreen: "Public",

      title: "Custom disciplines",
      groupTypesTitle: "Group types",
      add: "Add",
      name: "Name",
      code: "Code",
      description: "Description",
      groupType: "Group type",
      active: "Active",
      actions: "Actions",
      discipline: "Discipline",
      fields: "Fields",
      groupActions: "Group actions",
      edit: "Edit",
      delete: "Delete",
      loading: "Loading...",
      emptyDisciplines: "No discipline",
      emptyGroupTypes: "No group type",
      emptyFields: "No field",
      emptyActions: "No action",
      emptyOptions: "No option",
      emptyTranslations: "No translation",
      emptyValue: "-",
      yes: "Yes",
      no: "No",
      cancel: "Cancel",
      save: "Save",
      confirmDeleteDiscipline: (name: string) =>
        `Delete the discipline ${name}?`,
      confirmDeleteGroupType: (name: string) =>
        `Delete the group type ${name}?`,
      confirmDeleteTranslation: (key: string) =>
        `Delete the translation ${key}?`,
      disciplineDeleted: "Discipline deleted",
      disciplineUpdated: "Discipline updated",
      disciplineCreated: "Discipline created",
      groupTypeDeleted: "Group type deleted",
      groupTypeUpdated: "Group type updated",
      groupTypeCreated: "Group type created",
      translationDeleted: "Translation deleted",
      translationUpdated: "Translation updated",
      translationCreated: "Translation created",
      translationsImported: "Translations imported",
      editDiscipline: "Edit discipline",
      newDiscipline: "New discipline",
      editGroupType: "Edit group type",
      newGroupType: "New group type",
      editTranslation: "Edit translation",
      newTranslation: "New translation",
      groupFields: "Group fields",
      athleteFields: "Athlete fields",
      groupPageActions: "Group page actions",
      modals: "Modals",
      translationsTitle: "Translations",
      key: "Key",
      label: "Label",
      value: "Value",
      locale: "Language",
      type: "Type",
      required: "Required",
      visible: "Visible (grid)",
      visibleInModal: "Visible (modals)",
      readOnly: "Read only",
      sortable: "Sortable",
      order: "Order",
      unit: "Unit",
      referenceSource: "Reference",
      referenceIdPath: "ID key",
      referenceLabelPath: "Label key",
      referenceFilterBehavior: "Reference filter",
      options: "Options",
      behavior: "Behavior",
      disabled: "Disabled",
      startIcon: "Icon",
      variant: "Variant",
      size: "Size",
      color: "Color",
      width: "Width",
      height: "Height",
      contentMaxHeight: "Content max height",
      resizable: "Resizable",
      cellLayout: "Cell layout",
      configure: "Configure",
      enabled: "Enabled",
      disable: "Disable",
      preview: "Preview",
      noneIcon: "None",
      iconGridOrder: "Column order",
      iconCellOrder: "Cell order",
      iconColumnName: "Column",
      iconTooltip: "Tooltip",
      iconGridTarget: "Grid",
      iconGridTargets: {
        ATHLETES: "Athletes",
        GROUPS: "Groups",
      },
      iconGroupAction: "Group action",
      iconType: "Icon type",
      iconAssets: "Icon assets",
      groupGridIconActions: "Group grid icons",
      athleteGridIconActions: "Athlete grid icons",
      gridIconActions: "Grid icons",
      iconLayout: "Icon layout",
      iconLayoutTitle: "Icon button layout",
      selectFile: "Select file",
      behaviorVariables: {
        athlete: "Athlete",
        athletes: "Athletes",
        currentValue: "Current value",
        selectedRowId: "Selected row ID",
        selectedColumnKey: "Selected column key",
        currentMeet: "Current meet",
        group: "Context group",
        currentGroup: "Current group",
        groups: "Groups",
        records: "Records",
        levels: "Levels",
        ageCategories: "Age categories",
        disciplineAgeCategories: "Discipline age categories",
        disciplineWeightCategories: "Discipline weight categories",
        weightCategories: "Weight categories",
        disciplines: "Disciplines",
        leagues: "Leagues",
        organisations: "Organisations · catalog",
        organisation: "Organisation",
        modalValues: "Modal values",
        refereeLights: "Referee result (event)",
        inputValues: "Input values",
        referenceItem: "Reference item",
      },
      placement: "Placement",
      optionsPlaceholder: "label:value",
      addOption: "Add option",
      editOptions: (count: number) => `${count} option${count > 1 ? "s" : ""}`,
      optionsTitle: (label: string) => `Options - ${label}`,
      importJson: "Import JSON",
      exportLabels: "Export labels",
      untitledField: "Field",
      untitledAction: "Action",
      emptyModals: "No modals",
      behaviorTitle: (label: string) => `Behavior - ${label}`,
      referenceFilterTitle: (label: string) => `Reference filter - ${label}`,
      cellLayoutTitle: (label: string) => `Cell layout - ${label}`,
      modalLayoutTitle: (label: string, section: string) =>
        `Modal layout - ${label} - ${section}`,
      modalTitleLayout: "Title",
      modalContentLayout: "Content",
      modalActionsLayout: "Actions",
      fieldTypes: {
        TEXT: "Text",
        NUMBER: "Number",
        SELECT: "List",
        LIFT: "Lift",
        BOOLEAN: "Boolean",
        DATE: "Date",
        FOREIGN_KEY: "Reference",
      },
      referenceSources: {
        groups: "Groups",
        ageCategories: "Age categories",
        disciplineAgeCategories: "Discipline age categories",
        disciplineWeightCategories: "Discipline weight categories",
        weightCategories: "Weight categories",
        disciplines: "Disciplines",
        levels: "Levels",
        records: "Records",
        leagues: "Leagues",
      },
      placements: {
        TOOLBAR_START: "Toolbar - start",
        TOOLBAR: "Toolbar",
        FOOTER: "Footer",
        GRID_ICON: "Grid icon",
        ROW_ACTIONS: "Row actions",
      },
      getError: (code?: string | null) => {
        switch (code) {
          case "CUSTOM_DISCIPLINE_NOT_FOUND":
            return "Discipline not found.";
          case "CUSTOM_DISCIPLINE_HAS_GROUP_TYPE":
            return "A discipline linked to a group type cannot be deleted.";
          case "CUSTOM_DISCIPLINE_GROUP_TYPE_NOT_FOUND":
            return "Group type not found.";
          case "CUSTOM_DISCIPLINE_FIELD_KEY_DUPLICATE":
            return "Two fields in the same section cannot share the same key.";
          case "CUSTOM_DISCIPLINE_FIELD_KEY_REQUIRED":
            return "The field key is required.";
          case "CUSTOM_DISCIPLINE_FIELD_LABEL_REQUIRED":
            return "The field label is required.";
          case "CUSTOM_DISCIPLINE_SELECT_FIELD_REQUIRES_OPTIONS":
            return "A list field must have at least one option.";
          case "CUSTOM_DISCIPLINE_REFERENCE_FIELD_REQUIRES_SOURCE":
            return "A reference field must have a source.";
          case "CUSTOM_DISCIPLINE_REFERENCE_SOURCE_INVALID":
            return "The field reference source is invalid.";
          case "CUSTOM_DISCIPLINE_FIELD_OPTION_LABEL_REQUIRED":
            return "An option label is required.";
          case "CUSTOM_DISCIPLINE_FIELD_OPTION_VALUE_REQUIRED":
            return "An option value is required.";
          case "CUSTOM_DISCIPLINE_FIELD_OPTION_VALUE_DUPLICATE":
            return "Two options cannot share the same value.";
          case "CUSTOM_DISCIPLINE_ACTION_KEY_DUPLICATE":
            return "Two actions cannot share the same key.";
          case "CUSTOM_DISCIPLINE_ACTION_KEY_REQUIRED":
            return "The action key is required.";
          case "CUSTOM_DISCIPLINE_ACTION_LABEL_REQUIRED":
            return "The action label is required.";
          case "CUSTOM_DISCIPLINE_UNIQUE_CONSTRAINT":
            return "This code or association already exists.";
          case "CUSTOM_DISCIPLINE_INVALID_RELATION":
            return "The discipline relation is invalid.";
          case "CUSTOM_DISCIPLINE_CODE_REQUIRED":
            return "The code is required.";
          case "CUSTOM_DISCIPLINE_KEY_REQUIRED":
            return "The key is required.";
          case "CUSTOM_DISCIPLINE_ATHLETE_COUNT_INVALID":
            return dico().athleteCount.invalid;
          case "CUSTOM_DISCIPLINE_EXPORT_INVALID":
            return "The profile contains an invalid setting. Check columns, rows and template.";
          case "INVALID_ENTRY_PROFILE":
            return "The athlete entry configuration is invalid.";
          case "CUSTOM_DISCIPLINE_WEB_VIEW_INVALID":
            return "The web view configuration is invalid.";
          case "CUSTOM_DISCIPLINE_INVALID_BEHAVIOR":
            return "The behavior graph is invalid.";
          case "CUSTOM_DISCIPLINE_LABEL_TRANSLATION_NOT_FOUND":
            return "Translation not found.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_LOCALE_REQUIRED":
            return "The translation language is required.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_KEY_REQUIRED":
            return "The translation key is required.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_LABEL_REQUIRED":
            return "The translated label is required.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_IMPORT_INVALID":
            return "The translation file is invalid.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_IMPORT_EMPTY":
            return "The translation file does not contain any entries.";
          case "CUSTOM_DISCIPLINES_FETCH_ERROR":
            return "Unable to load custom disciplines.";
          case "CUSTOM_DISCIPLINE_CREATE_ERROR":
            return "Unable to create the discipline.";
          case "CUSTOM_DISCIPLINE_UPDATE_ERROR":
            return "Unable to update the discipline.";
          case "CUSTOM_DISCIPLINE_DELETE_ERROR":
            return "Unable to delete the discipline.";
          case "CUSTOM_DISCIPLINE_GROUP_TYPE_CREATE_ERROR":
            return "Unable to create the group type.";
          case "CUSTOM_DISCIPLINE_GROUP_TYPE_UPDATE_ERROR":
            return "Unable to update the group type.";
          case "CUSTOM_DISCIPLINE_GROUP_TYPE_DELETE_ERROR":
            return "Unable to delete the group type.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_CREATE_ERROR":
            return "Unable to create the translation.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_UPDATE_ERROR":
            return "Unable to update the translation.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_DELETE_ERROR":
            return "Unable to delete the translation.";
          case "CUSTOM_DISCIPLINE_TRANSLATION_IMPORT_ERROR":
            return "Unable to import translations.";
          default:
            return "An error occurred.";
        }
      },
    },
    ffForceExport: {
      sheetGridTitle: "Federation sheets",
      title: "FFForce export",
      empty: "No files in the federation folder",
      loading: "Loading files…",
      error: "Could not load the file list",
      deleteError: "Could not delete the file",
      downloadError: "Could not download the file",
      confirmDelete: (fileName: string) =>
        `Permanently delete the file “${fileName}”?`,
      columns: {
        fileName: "File name",
        fileSize: "Size",
        modifiedAt: "Last modified",
        createdAt: "Created at",
      },
      download: "Download",
      delete: "Delete",
      actionTooltipDownloadFederationSheet:
        "Download the federation sheet file as stored on the server.",
      actionTooltipDeleteFederationSheet:
        "Remove this federation sheet file from the server folder.",
      actionTooltipDownloadTemplateWorkbook:
        "Download the export template Excel workbook.",
      actionTooltipDownloadTemplateScript:
        "Download the PowerShell script (.ps1) linked to this template.",
      actionTooltipDownloadTemplateScriptDisabled:
        "No PowerShell script is attached to this template yet.",
      actionTooltipManagePs1:
        "Upload or copy the PowerShell (.ps1) script for this template.",
      actionTooltipDeleteTemplate:
        "Delete this template (workbook and script if any).",
      actions: "Actions",
      syncFromFfForceButton: "Fetch sheets from FFForce",
      syncFromFfForceRunning: "Downloading from FFForce…",
      syncFromFfForceSuccess: (
        downloaded: number,
        skipped: number,
        errorCount: number,
      ) =>
        `Sync completed: ${downloaded} file(s) downloaded, ${skipped} skipped, ${errorCount} error(s).`,
      syncFromFfForceError:
        "Failed to download federation sheets from the FFForce website.",
      templateGridTitle: "Export templates (Excel + script)",
      templateEmpty: "No template imported yet",
      templateError: "Could not load the template list",
      importTemplateButton: "Import a template",
      importTemplateModalTitle: "Import a workbook template",
      displayNameLabel: "Template name (unique)",
      selectWorkbook: "Excel file (.xlsx, .xlsm, .xls)",
      selectPs1: "PowerShell file (.ps1)",
      selectedFile: (fileName: string) => `Selected file: ${fileName}`,
      noFileSelected: "No file selected",
      importTemplateSubmit: "Import",
      importTemplateCancel: "Cancel",
      importTemplateSuccess: "Template imported",
      importTemplateError: "Template import failed",
      downloadTemplate: "Download template",
      downloadScript: "Download script",
      ps1ModalTitle: "PowerShell script (.ps1)",
      ps1ModeUpload: "Upload a file",
      ps1ModeCopy: "Copy from another template",
      ps1SourceLabel: "Source template",
      ps1NoSource: "No other template with a script",
      ps1CopySubmit: "Copy script",
      ps1UploadSubmit: "Save script",
      copyScriptSuccess: "Script copied",
      copyScriptError: "Could not copy the script",
      fileRequiredError: "A file is required (and must not be empty)",
      uploadScriptSuccess: "Script saved",
      uploadScriptError: "Could not save the script",
      linkPs1: "PS1 script",
      confirmDeleteTemplate: (name: string) =>
        `Delete template “${name}” (workbook and script)?`,
      deleteTemplateError: "Could not delete the template",
      templateConversionTitle:
        "Turn a federation sheet into an export template",
      templateConversionIntro:
        "In the workbook VBA editor, apply the replacements below before importing the file as a template.",
      templateConversionWindowsNote:
        "Make these edits on Windows, in Excel for Windows (not Excel for Mac or Excel online).",
      replaceLabel: "Replace",
      withLabel: "With",
      copyCodeTooltip: "Copy to clipboard",
      copyCodeSuccess: "Copied to clipboard",
      vbaSaveDialogOld: `'on va proposer la boite de dialogue pour enregistrer le fichier
Application.Dialogs(xlDialogSaveAs).Show`,
      vbaSaveDialogNew: `If Len(Trim$(exportPath)) = 0 Then
    Err.Raise vbObjectError + 1000, "feuillefédé_Silent", "exportPath est vide"
End If

Application.DisplayAlerts = False

If LCase$(Right$(exportPath, 5)) = ".xlsx" Then
    ActiveWorkbook.SaveAs Filename:=exportPath, FileFormat:=xlOpenXMLWorkbook
ElseIf LCase$(Right$(exportPath, 5)) = ".xlsm" Then
    ActiveWorkbook.SaveAs Filename:=exportPath, FileFormat:=xlOpenXMLWorkbookMacroEnabled
Else
    ActiveWorkbook.SaveAs Filename:=exportPath & ".xlsx", FileFormat:=xlOpenXMLWorkbook
End If

ActiveWorkbook.Close SaveChanges:=False

Application.DisplayAlerts = True`,
      vbaSubSignatureOld: "Sub feuillefédé()",
      vbaSubSignatureNew: "Sub feuillefédé(ByVal exportPath As String)",
      vbaOrderBarOld: 'Key2:=Range("K9"), Order1:=xlAscending, _',
      vbaOrderBarNew: 'Key2:=Range("A9"), Order2:=xlAscending, _',
    },
    referees: {
      title: "Referees",
      create: "Add referee",
      edit: "Edit referee",
      delete: "Delete",
      search: "Search (name, first name, rank, club…)",
      organisation: "Organisation",
      allOrganisations: "All",
      import: "Import Excel",
      importModalTitle: "Import referees (Excel)",
      mappingInfo: "Columns: A=Last name, B=First name, C=Rank, D=Year, E=Club",
      selectFile: "Choose .xlsx file",
      selectOrganisation: "Select organisation",
      preview: "Preview",
      importConfirm: "Import",
      duplicatesSkipped: (count: number) => `${count} duplicate(s) skipped`,
      created: (count: number) => `${count} referee(s) created`,
      firstname: "First name",
      lastname: "Last name",
      rank: "Rank",
      rankYear: "Rank year",
      club: "Club",
      cancel: "Cancel",
      save: "Save",
      confirmDelete: "Are you sure you want to delete this referee?",
      noReferees: "No referees",
      loading: "Loading...",
      error: "Error",
      user: "Associated user",
      noUser: "None",
    },
    testExport: {
      title: "Test Export Federation Sheet",
      userLabel: "User",
      userDescription:
        "Select the user whose license will be used for the export",
      generateSection: {
        title: "Generate test data",
        description:
          "Generates a temporary competition with athletes from different categories to test the federation sheet export.",
        button: "Generate test data",
        generating: "Generating...",
        meetCreated: "Competition created",
      },
      exportSection: {
        title: "Export federation sheet",
        description:
          "Exports the federation sheet for the generated test data.",
        formatLabel: "Format",
        sheetDateLabel: "Sheet date",
        button: "Export federation sheet",
        exporting: "Exporting...",
        formatOptions: {
          all: "All",
          fa: "FA/PL",
          bp: "DC/BP",
        },
      },
      compareSection: {
        title: "Compare with reference file",
        description:
          "Upload an Excel reference file to compare with the generated export.",
        selectFile: "Select a file",
        summary: "Comparison summary",
        totalDifferences: "Total differences",
        byType: {
          values: "Values",
          formulas: "Formulas",
          types: "Types",
        },
        details: "Difference details",
        columns: {
          sheet: "Sheet",
          cell: "Cell",
          expected: "Expected value",
          actual: "Actual value",
          type: "Type",
        },
        identical: "Files are identical!",
        differencesFound: (count: number) => `${count} difference(s) found`,
      },
      cleanup: {
        button: "Delete test data",
        success: "Test data deleted",
      },
      errors: {
        generate: "Error generating test data",
        export: "Error during export",
        compare: "Error during comparison",
        cleanup: "Error during cleanup",
        loadSheetDates: "Error loading dates",
      },
      success: {
        generate: "Test data generated successfully",
        export: "Export successful",
      },
    },
    records: {
      title: "Records",
      import: "Import",
      refresh: "Refresh",
      exportPortable: "Export federation template",
      importPortable: "Import federation template",
      portableExportSuccess: "The federation template was exported",
      portableImportSuccess: "Federation records were imported",
      parentRecordReadOnly: "Parent organisation records are read-only",
      parentRecordsHint:
        "Records from parent organisations (federation, world…) are visible read-only. Only your organisation's records can be edited or imported.",
      ownOrganisationBadge: "Your organisation",
      parentOrganisationBadge: "Read-only",
      federationScopeRequired:
        "Sync the federation first before importing or editing records",
      recordNotFound: "Record not found",
      columns: {
        organisation: "Organisation",
        movement: "Movement",
        weightCategory: "Weight Category",
        ageCategory: "Age Category",
        performance: "Performance",
        athlete: "Athlete",
        dateLocation: "Date/Location",
        sheetDate: "Sheet Date",
      },
      instructions: {
        fileFormat: "File format",
        sheets: "Sheets",
        structure: "Structure",
        sheetNames: {
          female: "Female records",
          male: "Male records",
          main: "Levels",
        },
        startRow: "Start row",
        columns: "Columns",
        column: "Column",
        columnNames: {
          weightCategory: "Weight category",
          ageCategory: "Age category",
          squat: "Squat",
          bench: "Bench press",
          deadlift: "Deadlift",
          total: "Total",
          R3: "R3",
          R2: "R2",
          R1: "R1",
          N2: "N2",
          N1: "N1",
          Europe: "Europe",
          Monde: "World",
        },
        subColumnNames: {
          athlete: "Athlete",
          weight: "Performance",
          date: "Date/Location",
        },
      },
      errors: {
        importError: "Error importing records",
        importEmpty:
          "No records imported. Use the federation portable template for KDKS/IPF, or an FFForce sheet for FFForce.",
        ageCategoryEmpty:
          "Missing age category (ageCategoryCode column). Pick a dropdown value, e.g. OPEN (FA).",
        ageCategoryInvalid: (detail: string) =>
          `Invalid age category: ${detail}`,
        weightCategoryNotFound: "Weight category not found",
        ageCategoryNotFound: "Age category not found",
        weightExtractionFailed: "Unable to extract weight from category",
        recordSaveFailed: "Error saving record",
        sheetNotFound: (sex: string) =>
          `${sex === "F" ? "Female" : "Male"} records sheet not found`,
        movementNotFound: "Movement not found",
        errorList: "Error list",
      },
      importSuccess: "Records import completed successfully",
      importedRecords: (count: number) => `${count} record(s) imported`,
      displayModal: {
        title: "Records",
        button: "Display records",
        discipline: "Discipline",
        sex: "Sex",
        age: "Age category",
        allAges: "All ages",
        class: "Class",
        lifter: "Lifter",
        weight: "Record",
        dateLocation: "Date/Location",
        empty: "No records for these filters.",
        closeAria: "Close",
        organisationFallback: "Organisation",
        movements: {
          squat: "Squat",
          bench: "Bench press",
          deadlift: "Deadlift",
          total: "Total",
        },
      },
    },
    niveaux: {
      title: "Levels",
      import: "Import",
      refresh: "Refresh",
      exportPortable: "Export federation template",
      importPortable: "Import federation template",
      portableExportSuccess: "The levels template was exported",
      portableImportSuccess: "Federation levels were imported",
      inheritedReadOnly:
        "Levels from the inherited source organisation are read-only",
      inheritedHint:
        "Your organisation inherits minima from another organisation. To manage your own levels, an administrator must point the « Levels / minima » source to your organisation.",
      ownOrganisationBadge: "Your organisation",
      parentOrganisationBadge: "Read-only",
      federationScopeRequired:
        "Sync the federation snapshot first (sign in) before managing levels.",
      niveauNotFound: "Level not found",
      columns: {
        level: "Level",
        weightCategory: "Weight Category",
        ageCategory: "Age Category",
        minTotal: "Minimum Total",
        maxTotal: "Maximum Total",
        sheetDate: "Sheet Date",
        organisation: "Organisation",
      },
      instructions: {
        fileFormat: "File format",
        sheets: "Sheets",
        structure: "Structure",
        sheetNames: {
          main: "Levels",
        },
        startColumn: "Start column",
        column: "Column",
        columnNames: {
          weightCategory: "Weight category",
          R3: "R3",
          R2: "R2",
          R1: "R1",
          N2: "N2",
          N1: "N1",
          Europe: "Europe",
          Monde: "World",
        },
        genderSections: {
          male: "Male section",
          female: "Female section",
        },
      },
      errors: {
        importError: "Error importing levels",
        errorList: "Error list",
      },
      importSuccess: "Levels import completed successfully",
      displayModal: {
        title: "Display Levels",
        discipline: "Discipline",
        masculine: "masculine",
        feminine: "feminine",
        category: "Cat.",
        note: "performances below regional level are classified by default as departmental level",
        button: "Display levels",
        emptyCell: "-",
        empty: "No levels for this discipline.",
        closeAria: "Close",
      },
    },
    accounts: {
      title: "Accounts",
      users: "Users",
      licenses: "Licenses",
      profile: "Profile",
    },
  },
  filesSelected: "file(s) selected",
  display: {
    referees: "Referees",
    webClient: "Web Client",
    arbitrage: "Arbitrage",
    essai: "ATTEMPT",
    prochainPoids: "NEXT WEIGHT",
    barChangeFlash: "BAR CHANGE",
    hauteur: "HEIGHT",
    securites: "SAFETIES",
    cales: "BLOCKS",
    rack: "RACK",
    total: "TOTAL",
    place: "CURRENT PLACE",
    displayAll: "Display All",
    category: "Category Weight - Age",
    placement: "Placement",
    totalAnnonced: "Total Annonced",
    totalReal: "Actual Total",
    attempt: "Attempt",
    status: "Status",
    value: "Value",
    settings: "Display settings",
    assetsPreparing: "Downloading resources for offline use…",
    assetsReady: "Resources downloaded - ready offline",
    assetsMissing: "Some resources are not available offline.",
    assetsRetry: "Retry",
    assetsFontList: "Font list",
    screen: "Screen",
    none: "None",
    noScreenAssigned:
      'This display is set to "None" and cannot open. Choose a screen in the display settings.',
    ticketTimes: "Ticket Times",
    plateau: "Platform",
    identify: "Identify Screens",
    hideIdentification: "Hide Identification",
    main: "Main Screen",
    restart: "Restart Displays",
    standardDisplaysTab: "Standard screens",
    customDisplaysTab: "Custom screens",
    customDisplayAdd: "Add screen",
    customDisplayName: "Screen name",
    customDisplayLayout: "Display layout",
    customDisplayOpen: "Open screen",
    customDisplayDelete: "Delete screen",
    customDisplayEmpty: "No custom screen configured",
    customDisplayNameRequired: "The screen name is required",
    customDisplayLayoutRequired: "Please choose a display layout",
    customDisplayCreateError: "Unable to add this custom screen",
    customDisplayMissing: "No custom screen selected",
    customDisplayNotFound: "Custom screen not found",
    customDisplayInvalid: "Invalid custom display",
    customDisplayFallbackLabel: "Custom display",
    customDisplayLoading: "Loading...",
    customDisplayQuota: (count: number, max: number) =>
      `${count}/${max} custom screens configured`,
    layoutButton: "Settings",
    layoutModalTitle: "Display layout",
    layoutModalHelp:
      "Select one of your account layouts, then import it to replace the screen.",
    layoutJsonPlaceholder: "Paste the screen layout here...",
    layoutJsonError: "The screen layout is invalid",
    layoutLoadMine: "Load my layouts",
    layoutSelectMine: "My layouts",
    layoutImportSelected: "Import",
    layoutLoadingList: "Loading layouts…",
    layoutLoadingImport: "Importing layout…",
    layoutAssigned: "Layout assigned",
    layoutClearAssignment: "Remove layout",
    layoutLoadListError: "Unable to load layouts list",
    layoutLoadError: "Unable to load this layout",
    layoutDiscipline: "Layout discipline",
    layoutDefaultDiscipline: "Default",
    duplicateError: "The following displays are used multiple times:",
    publicDisplay: "Public Display",
    currentAthlete: "Current Athlete",
    categoryAthletes: "Category Athletes",
    attempts: "Attempts",
    totalMax: "Max Total",
    totalAnnounced: "Announced Total",
    success: "Success",
    failed: "Failed",
    announced: "Announced",
    current: "Current",
    tooltips: {
      webClient: "Open web client",
      settings: "Configure display settings for screens",
      displayAll: "Show or hide all screens simultaneously",
      athlete: "Show athlete management screen",
      loader: "Show loaders screen",
      ranking: "Show rankings screen",
      publicDisplay: "Show public display screen",
      import: "Import data into the system",
      search: "Search in the group",
      start: "Start group display",
      stop: "Stop group display",
      results: "Show and export meet results for rewards",
    },
  },
  placement: (placement: number) => {
    if (placement === 0) return dico().athletes.hm;
    const j = placement % 10;
    const k = placement % 100;
    if (j === 1 && k !== 11) return `${placement}st`;
    if (j === 2 && k !== 12) return `${placement}nd`;
    if (j === 3 && k !== 13) return `${placement}rd`;
    return `${placement}th`;
  },
  close: "Close",
  ranking: {
    passageStatus: (count: number | null) =>
      count === null
        ? "-"
        : count === 0
          ? "Attempt in progress"
          : `${count} attempt${count > 1 ? "s" : ""} out`,
    passagesBefore: "Attempt",
    title: "Ranking",
    points: (formula?: string) =>
      String(formula ?? "").toUpperCase() === "DOTS" ? "DOTS" : "GL Points",
    attempt: "Attempt",
    placement: "Placement",
    placementAnnonced: "Placement Annonced",
    name: "Name",
    firstName: "First Name",
    total: "Total",
    liveMode: "Bar order",
    categoryMode: "Category",
  },
  results: {
    meetResults: "Meet Results",
    showPodiums: "Show Podiums",
    separateAgeCategories: "Separate Age Categories",
    resultsPerPage: "Results per page",
    page: "Page",
    of: "of",
    rank: "Rank",
    club: "Club",
    lastName: "Last Name",
    squat: "Squat",
    bench: "Bench Press",
    deadlift: "Deadlift",
    total: "Total",
    level: "Level",
    points: (formula?: string) =>
      String(formula ?? "").toUpperCase() === "DOTS" ? "DOTS" : "GL Points",
    podiums: "Podiums",
    menCategory: "Men",
    womenCategory: "Women",
    mixedCategory: "Mixed",
    sexFilter: "Sex",
    allSexes: "All",
    competitionFormatFilter: "Competition format",
    allFormats: "All formats",
    ageCategoryFilter: "Age category",
    weightCategoryFilter: "Weight category",
  },
  exportGroupModal: {
    feuilleFederation: "Export as federation sheet (Excel)",
    openPowerlifting: "Export as OpenPowerlifting (CSV)",
    showTickets: "Show group tickets",
  },
  errors: {
    anErrorOccured: "An error occured.",
    wrongPasswordOrLogin: "Incorrect login or password.",
    pleaseEnterPassword: "Please enter your password.",
    pleaseEnterLogin: "Please enter your login.",
    pleaseEnterLoginAndPassword: "Please enter your login and password.",
    needToBeConnectedToInternet:
      "You must be connected to the internet to enable live.",
    needToBeConnectedToNetwork:
      "You must be connected to WiFi to use referee lights.",
    criticalBlock: {
      title: "Critical Application Block",
      subtitle: "Athlete counting compromised",
      description:
        "The application must be closed for security and license compliance reasons.",
      reasonLabel: "Block reason",
      detailsLabel: "Technical details",
      warning:
        "This situation requires technical intervention. The application cannot continue to function.",
      instructions:
        "Contact your system administrator or close the application and try again later with a stable internet connection.",
    },
    licenseSync: {
      success: "License data synchronized",
      updated: "Athlete limit updated",
      error: "Synchronization error",
      noChanges: "No updates available",
    },
  },
  users: {
    title: "User List",
    fields: {
      id: "ID",
      login: "Login",
      password: "Password",
      roles: "Roles",
      created_at: "Creation Date",
      email: "Email Address",
      organization: "Organization",
    },
  },
  homeWeb: {
    searchHere: "Search here...",
    noMeetFound: "No meet matches your search",
    organisation: "Organisation",
    allOrganisations: "All organisations",
    discipline: "Discipline",
    allDisciplines: "All disciplines",
    athletesCount: (count: number) =>
      count === 1 ? "1 athlete" : `${count} athletes`,
    statusFinished: "Finished",
    statusOngoing: "Ongoing",
    statusThisWeek: "This week",
    statusThisMonth: "This month",
    statusUpcoming: "Upcoming",
  },
  meetManager: "Meet Manager",
  meets: "Meets",
  city: "City",
  ligue: "Organisation",
  date: "Date",
  attempt: "Attempt",
  meetPage: {
    name: "Name",
    status: "Status",
    athletes: "Athletes",
    finished: "Finished",
    ongoing: "Ongoing",
    upcoming: "Upcoming",
  },
  widget: {
    browserSourceHelp:
      "Copy the link into a browser source in your broadcasting software. Layout settings depend on the widget and license permissions.",
    announcedTotal: "Total ANNOUNCED",
    achievedTotal: "Total ACHIEVED",
    announcedRank: "Rank ANNOUNCED",
    squatPr: "Squat",
    benchPr: "Bench",
    deadliftPr: "Deadlift",
    totalPr: "Total",
    bestSquat: "Best Squat",
    bestBench: "Best Bench",
    bestDeadlift: "Best Deadlift",
    bestTotal: "Best Total",
    kg: "kg",
    personalBest: "Personal Best",
    total: "Total",
    squat: "Squat",
    bench: "Bench",
    deadlift: "Deadlift",
    settings: "Widgets settings",
    title: "Widgets",
    widgetAvailable: "Widgets Available",
    subtitle: "Select a widget to view it and get its embed URL.",
    currentAthleteWidgetName: "Current Athlete Widget",
    currentAthleteWidgetDescription:
      "This widget displays information about the athlete currently competing. Data is automatically updated.",
    categoryRankingWidgetName: "Current Category Widget",
    categoryRankingWidgetDescription:
      "This widget displays the ranking of athletes in the same category as the current athlete. Data is automatically updated.",
    currentGroupWidgetName: "Current Group Widget",
    currentGroupWidgetDescription:
      "This widget displays the current group of the meet. Data is auomatically updated.",
    lightsWidget: "Referee Lights Widget",
    lightsWidgetDescription:
      "This widget displays the referee lights. The data is updated automatically.",
    timerWidget: "Timer Widget",
    timerWidgetDescription:
      "This widget displays the timer of the competition. The data is updated automatically.",
    viewWidget: "View widget",
    openStreamMode: "Open in stream mode",
    openInNewPage: "Open in a new page",
    copyUrl: "Copy URL",
    widgetNotFound: "Widget not found",
    backToWidgets: "Back to widgets",
    integrationUrl: "Embed URL:",
    runtimeLayoutsTitle: "Custom runtimes",
    runtimeSectionTitle: "Displays",
    runtimeWidgetsButton: "Widgets",
    runtimeDisplaysButton: "Screens",
    runtimeDisplaysTitle: "Screens",
    runtimeDisplaysSubtitle:
      "Open each display in a new page to get a fullscreen render without surrounding UI.",
    layoutDisplaysTitle: "Layout-based displays",
    noLayoutDisplaysConfigured:
      "No layout-based display is configured for the current theme.",
    displayTargetNotFound: "This display does not exist.",
    displayLayoutNotConfigured: "No layout is configured for this display.",
    runtimeThemeLabel: (themeName: string) => `Current theme: ${themeName}`,
    missingMeetId: "Please specify a competition ID in the URL",
    noActiveMeet:
      "No active competition. Open a competition in Meet Manager to display links.",
    youNeedToActivateDirect:
      "You need to activate direct before using the widgets.",
    linkCopied: "Link copied!",
    helpTitle: "How to use widgets",
    helpDescription:
      "This interface allows you to configure and manage widgets for your competition.",
    helpStep1:
      "Connect to the same Wi-Fi network as the device that will use the widgets",
    helpStep2:
      "Select the application URL from the list of available addresses",
    helpStep3: 'Click "Start" to launch the widget server',
    helpStep4:
      "Copy the links of the widgets you want to use, and import them into your replay application.",
    helpNote:
      "Note: Multiple addresses may be available for the same network. If the connection does not work with the first address, try the others one by one until you find the one that allows referees to connect.",
    widgetsNeedActivation:
      "Widgets are only available on request. Please contact us if you would like to use them.",
    durationsTitle: "Durations",
    durationsCurrentAthleteTitle: "Durations - Current Athlete Widget",
    durationsCurrentCategoryTitle: "Durations - Category Ranking Widget",
    durationExit: "Exit (seconds)",
    durationShowPR: "Show PR (seconds)",
    durationPRDelay: "PR Delay (seconds)",
    durationHidden: "Hidden (seconds)",
    durationShow: "Show (seconds)",
    localWidgets: "Local displays",
    standardWidgetsTab: "Standard widgets",
    customWidgetsTab: "Custom widgets",
    customWidgetAdd: "Add widget",
    customWidgetName: "Widget name",
    customWidgetLayout: "Widget layout",
    customWidgetOpen: "Open widget",
    customWidgetCopy: "Copy link",
    customWidgetDelete: "Delete widget",
    customWidgetEmpty: "No custom widget configured",
    customWidgetNameRequired: "The widget name is required",
    customWidgetLayoutRequired: "Please choose a widget layout",
    customWidgetCreateError: "Unable to add this custom widget",
    customWidgetQuota: (count: number, max: number) =>
      `${count}/${max} custom widgets configured`,
    layoutButton: "Settings",
    layoutModalTitle: "Widget display",
    layoutModalHelp:
      "Select one of your account layouts, then import it to replace the widget.",
    layoutJsonPlaceholder: "Paste the widget layout here...",
    layoutJsonError: "The widget layout is invalid",
    layoutSelectMine: "My layouts",
    layoutImportSelected: "Import",
    layoutLoadingList: "Loading layouts…",
    layoutLoadingImport: "Importing layout…",
    layoutAssigned: "Layout assigned",
    layoutClearAssignment: "Remove layout",
    layoutLoadListError: "Unable to load the layouts list",
    layoutLoadError: "Unable to load this layout",
  },
  meetGrid: {
    firstname: "Firstname",
    lastname: "Lastname",
    dateOfBirth: "Date of birth",
    club: "Club",
    place: "Place",
    sex: "Sex",
    bodyweight: "Bodyweight",
    squat: "Squat",
    bench: "Bench",
    deadlift: "Deadlift",
    weightClass: "Weightclass",
    announcedTotal: "ANNOUNCED Total",
    achievedTotal: "ACHIEVED Total",
    dots: (formula?: string) =>
      String(formula ?? "").toUpperCase() === "DOTS" ? "DOTS" : "GL Points",
    ageClass: "Ageclass",
    ageClassPlus1: "Ageclass Y+1",
  },
  rank: "Rank",
  guest: "Guest",
  license: {
    unlimitedAthletes: "Unlimited athletes",
    title: "Licenses",
    key: "License key",
    name: "License name",
    maxActivations: "Maximum activations",
    expirationDate: "Expiration date",
    isActive: "Active license",
    activations: "Activations",
    create: "Create license",
    edit: "Edit license",
    delete: "Delete license",
    confirmDelete: (name: string) =>
      `Are you sure you want to delete the license "${name}"?`,
    unlimited: "Unlimited",
    capabilities: {
      sectionTitle: "Custom displays & control room",
      quotasTitle: "Quotas",
      maxCustomScreens: "Max custom screens",
      maxCustomWidgets: "Max custom widgets",
      quotasHelper: "0 = no quota allocated",
      layoutEditor: {
        accessControl: "Layout editor",
        accessTooltip:
          "Allows creating and editing layouts on the licensed portal",
      },
      customDisplay: {
        accessControl: "Custom displays (desktop)",
        accessTooltip:
          "Allows using custom displays in the desktop application",
      },
      remoteCustomDisplay: {
        accessControl: "Remote custom displays",
        accessTooltip:
          "Allows custom displays via the web runtime (OBS, public pages)",
      },
      controlRoom: {
        accessControl: "Control room",
        accessTooltip:
          "Allows access to the control room panel to drive displays",
      },
      enabled: "Enabled",
      disabled: "Disabled",
      accessUpdated: (
        licenseName: string,
        featureLabel: string,
        status: string,
      ) => `${featureLabel} updated for ${licenseName}: ${status}`,
      accessUpdateError: "Error updating license permissions",
      maxQuotaInvalid: "Value must be a non-negative integer",
    },
    viewActivations: "View activations",
    informations: "License information",
    configuration: "Configuration",
    status: "Status",
    valid: "Valid",
    expired: "Expired",
    noActivations: "No activations",
    noActivationsDescription:
      "This license has not been activated on any machine yet.",
    activationsUsed: "Activations used",
    machinesActivated: "Machines activated",
    expiresOn: "Expires on",
    limitReached: "Limit reached",
    limitReachedDescription:
      "This license has reached its maximum number of activations. No new machine can be activated.",
    machineId: "Machine ID",
    machineName: "Unnamed machine",
    activatedOn: "Activated on",
    lastUsed: "Last used",
    activeNow: "Active now",
    hoursAgo: (hours: number) => `${hours}h ago`,
    daysAgo: (days: number) => `${days} day${days > 1 ? "s" : ""} ago`,
    generateKey: "Generate random key",
    deactivateMachine: "Deactivate machine",
    confirmDeactivate: "Confirm deactivation",
    confirmDeactivateMessage: (machineName: string) =>
      `Are you sure you want to deactivate machine "${machineName}"? This action cannot be undone.`,
    deactivateSuccess: "Machine deactivated successfully",
    deactivateError: "Error deactivating machine",
    placeholders: {
      key: "DEMO-2024-XXXX",
      name: "Client ABC - Demo license",
    },
    errors: {
      keyRequired: "License key is required",
      keyTooShort: "Key must contain at least 5 characters",
      nameRequired: "Name is required",
      maxActivationsRange: "Number of activations must be between 1 and 100",
      expirationDateFuture: "Expiration date must be in the future",
    },
    activation: {
      title: "License activation",
      managementTitle: "License management",
      description:
        "To use Meet Manager, you must activate a valid license. Enter your license key below:",
      replaceDescription: "You can replace the current license with a new one:",
      licenseKey: "License key",
      newLicenseKey: "New license key",
      placeholder: "XXXX-XXXX-XXXX-XXXX",
      currentLicense: "Current license",
      key: "Key",
      machine: "Machine",
      expiration: "Expiration",
      expirationNotDefined: "Not defined",
      status: "Status",
      validStatus: (daysRemaining: number) =>
        `Valid (${daysRemaining} days remaining)`,
      expiredStatus: "Expired",
      invalidStatus: "Invalid",
      athleteCount: "Athlete count",
      checkingLicense: "Checking license...",
      activate: "Activate",
      replace: "Replace",
      remove: "Remove license",
      activating: "Activation...",
      removing: "Removing...",
      success: "License activated successfully!",
      noLicense: "No license activated",
      removeSuccess: "License removed successfully",
      errors: {
        required: "Please enter a license key",
        activationFailed: "Error during activation",
        removeFailed: "Error during license removal",
        unexpected: "An unexpected error occurred",
      },
    },
  },
  // Unified error management for license system
  systemErrors: {
    authentication: {
      invalidCredentials: {
        title: "Invalid credentials",
        message: "The username or password is incorrect",
      },
      accountDisabled: {
        title: "Account disabled",
        message: "Your account has been disabled. Contact your administrator",
      },
    },
    network: {
      connectionFailed: {
        title: "Connection problem",
        message:
          "Unable to connect to the server. Check your internet connection",
      },
    },
    license: {
      expired: {
        title: "License expired",
        message: (expirationDate?: Date) =>
          `Your license has expired${expirationDate ? ` on ${expirationDate.toLocaleDateString()}` : ""}. Contact your administrator to renew it`,
      },
    },
    generic: {
      unexpectedError: {
        title: "Unexpected error",
        message: "An unexpected error occurred. Please try again",
      },
    },
    athleteQuotaBlocked: {
      title: "Athlete quota reached",
    },
  },
  warning: {
    athleteQuotaNearLimit: {
      title: "Quota nearly reached",
    },
    athleteQuotaWarning: {
      title: "Quota warning",
    },
  },
  actions: {
    tryAgain: "Try again",
    resetPassword: "Reset password",
    workOffline: "Work offline",
    contactSupport: "Contact support",
    renewLicense: "Renew license",
    contactAdmin: "Contact administrator",
    useLocalLicense: "Use local license",
    reportBug: "Report bug",
    upgradeAccount: "Upgrade account",
    manageAthletes: "Manage athletes",
    checkConnection: "Check connection",
  },
  rankFromTo: (placeFrom: number, placeTo: number): string => {
    if (placeFrom == placeTo) return `${dico().placement(placeFrom)}`;
    return `${dico().placement(placeFrom)} à ${dico().placement(placeTo)}`;
  },
  stream: {
    noStreamConfigured: "No stream configured",
    invalidUrl: "Invalid stream URL",
    title: "Live Stream",
    liveGroup: "Live Group",
    hideStream: "Hide stream",
    showStream: "Show stream",
    additionalContent: "Additional content (coming soon)",
    plateauLinks: {
      title: "Live links",
      menuButton: "Live links",
      globalLabel: "Global link (all platforms)",
      globalHelper: "Used when no link is set for the live group's platform",
      plateauLabel: (n: number) => `Platform ${n}`,
      urlPlaceholder:
        "YouTube: youtube.com/watch?v=xxx or @channel | Twitch: twitch.tv/channel",
      save: "Save",
      cancel: "Cancel",
      saved: "Live links saved",
      saveError: "Could not save live links",
    },
    errors: {
      noLiveFound:
        "Could not find an active live stream on this channel. Please verify that the stream is live or use the direct live URL (youtube.com/watch?v=...).",
      twitchHttpsRequired:
        "⚠️  Twitch requires HTTPS. Stream may not work in development (http://localhost)",
      twitchProductionOnly:
        "    Twitch stream will work in production on https://meet-manager.fr",
    },
  },
  speaker: {
    title: "Speaker View",
    landing: {
      title: "Live meets",
      empty: "No live meet is currently available.",
      openSpeaker: "Open speaker view",
    },
    noAthleteSelected: "No athlete selected",
    noGroupSelected: "No group in progress",
    selectGroupFromMeetPage: "Select a group from the meet page",
    connectingToLive: "Connecting to live…",
    liveNotEnabled: "Live is not enabled",
    liveNotEnabledHint:
      "Enable live from the Meet Manager desktop app on the competition computer. This page will update automatically.",
    noData: "N/A",
    today: "Today",
    yesterday: "Yesterday",
    daysAgo: (days: number) => `${days} day${days > 1 ? "s" : ""} ago`,
    weeksAgo: (weeks: number) => `${weeks} week${weeks > 1 ? "s" : ""} ago`,
    monthsAgo: (months: number) =>
      `${months} month${months > 1 ? "s" : ""} ago`,
    yearsAgo: (years: number) => `${years} year${years > 1 ? "s" : ""} ago`,
    bestPerformances: "Best Performances",
    bestTotals: "Best Totals",
    bestTotal: "Best Total",
    bestSquat: "Best Squat",
    bestBench: "Best Bench",
    bestDeadlift: "Best Deadlift",
    successRates: "Success Rates",
    refereeLights: "Referee Lights",
    weightDifferences: "Weight Differences",
    attempt: "Attempt",
    squat: "Squat",
    bench: "Bench Press",
    deadlift: "Deadlift",
    rankingAndLevel: "Ranking and Level",
    projectedPlace: "Projected Place",
    announcedPlace: "Announced Place",
    currentLevel: "Level",
    announcedLevel: "Announced Level",
    levelMinimum: "Minimum total",
    nextLevel: "Next level",
    hidePanel: "Hide panel",
    showPanel: "Show panel",
    groupSelectLabel: "Group",
    liveGroupBadge: "Live",
    backToLiveGroup: "Back to the live group",
    currentAthleteBadge: "Current",
    backToCurrentAthlete: "Back to the current athlete",
    levelsButton: "Levels",
    recordsButton: "Records",
    notes: {
      title: "Notes",
      placeholder: "Private notes about this athlete…",
      hint: "Private notes, kept from one competition to the next.",
      saving: "Saving…",
      saved: "Saved",
      saveError: "Unable to save the note.",
    },
  },
  athleteSpeaker: {
    weightCategory: "Category",
    ageCategory: "Age",
  },
  mouvementName: (mouv: Exercise) => {
    switch (mouv.name) {
      case "squat":
        return "Squat";
      case "bench":
        return "Bench";
      case "deadlift":
        return "Deadlift";
      default:
        return "";
    }
  },
  meetDefinedPrivate: "Meet has been defined private.",
  meetDefinedPublic: "Meet has been defined public.",
  datagrid: {
    MuiTablePagination: {
      labelRowsPerPage: "Rows per page:",
      labelDisplayedRows: function defaultLabelDisplayedRows({
        from,
        to,
        count,
      }: {
        from: number;
        to: number;
        count: number;
      }) {
        return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
      },
    },
    toolbarFilters: "Filters",
    toolbarFiltersTooltipShow: "Show filters",
    toolbarFiltersTooltipHide: "Hide filters",
    filterPanelAddFilter: "Add filter",
    filterPanelRemoveAll: "Remove all",
    filterPanelDeleteIconLabel: "Delete",
    filterPanelEditIconLabel: "Edit",
    filterPanelColumns: "Column",
    filterPanelOperator: "Operator",
    filterPanelLinkOperator: "Logical operator",
    filterPanelInputLabel: "Value",
    filterPanelInputPlaceholder: "Filter value",
    linkOperatorAnd: "and",
    linkOperatorOr: "or",
    filterOperatorContains: "contains",
    filterOperatorDoesNotContain: "does not contain",
    filterOperatorEquals: "equals",
    filterOperatorNotEquals: "does not equal",
    filterOperatorStartsWith: "starts with",
    filterOperatorEndsWith: "ends with",
    filterOperatorIsEmpty: "is empty",
    filterOperatorIsNotEmpty: "is not empty",
    filterOperatorIsAnyOf: "is any of",
    filterOperatorIs: "is",
    filterOperatorNot: "is not",
    filterOperatorGreaterThan: "greater than",
    filterOperatorGreaterThanOrEqual: "greater than or equal to",
    filterOperatorLessThan: "less than",
    filterOperatorLessThanOrEqual: "less than or equal to",
    filterOperatorBefore: "before",
    filterOperatorOnOrBefore: "on or before",
    filterOperatorAfter: "after",
    filterOperatorOnOrAfter: "on or after",
    filterValueAny: "any",
    filterValueTrue: "true",
    filterValueFalse: "false",
    noRowLabel: "No rows",
    toolbarDensity: "Density",
    toolbarDensityLabel: "Density",
    toolbarDensityComfortable: "Comfortable",
    toolbarDensityCompact: "Compact",
    toolbarDensityStandard: "Standard",
    columnsManagementSearchTitle: "Search",
    columnsManagementNoColumns: "No columns",
    columnsManagementShowHideAllText: "Show/Hide All",
    columnsManagementReset: "Reset",
    columnsManagementDeleteIconLabel: "Clear",
    columnMenuLabel: "Menu",
    columnMenuAriaLabel: (columnName: string) => `${columnName} column menu`,
    columnMenuShowColumns: "Show columns",
    columnMenuManageColumns: "Manage columns",
    columnMenuFilter: "Filter",
    columnMenuHideColumn: "Hide column",
    columnMenuUnsort: "Unsort",
    columnMenuSortAsc: "Sort by ASC",
    columnMenuSortDesc: "Sort by DESC",
    columnMenuManagePivot: "Manage pivot",
    footerRowSelected: (count: number) =>
      `${count} row${count > 1 ? "s" : ""} selected`,
  },
  dragAndDrop: {
    defaultMessage: "Drag and drop your files here",
    filesSelected: "files selected",
    unsupportedFormat: (fileName: string) =>
      `Unsupported file format: ${fileName}`,
    fileProcessingError: (fileName: string) =>
      `Error processing file: ${fileName}`,
  } as const,
  export: {
    exportResultat: "Export results",
    meetFederationTitle: "Export Federation Sheets",
    meetFederationDescription: "Choose the type of athletes to export",
    meetFederation: "Federation Sheets",
    exportFaAthletes: "Export FA Athletes",
    exportPlAthletes: "Export PL Athletes",
    faAthletes: "FA Athletes",
    plAthletes: "PL Athletes",
    openPowerlifting: "OpenPowerlifting",
    exportOpenPowerlifting: "Export as OpenPowerlifting format (CSV)",
    attemptTimestamps: "Attempt timestamps",
    exportAttemptTimestamps:
      "CSV export of start and end times for each attempt (UTC)",
    exportFullPowerAthletes: "Export 3 lifts athletes (FA/PL)",
    exportSingleLiftAthletes: "Export 1 lift athletes (DC/BP)",
    // Discipline-specific descriptions
    exportFA: "Export Force Athlétique athletes (squat, bench press, deadlift)",
    exportPL: "Export Powerlifting athletes (squat, bench press, deadlift)",
    exportDC: "Export Développé Couché athletes (bench press only)",
    exportBP: "Export Bench Press athletes (bench press only)",
    saveFile: "Save File",
    success: "Export successful",
    error: "Export error",
    selectFormat: "Select export format",
    formatDescription:
      "This competition contains groups in FA/PL and DC/BP formats. Please select the format to export:",
    formatFAPL: "FA/PL - Force Athlétique / Powerlifting",
    formatDCBP: "DC/BP - Développé Couché / Bench Press",
    macroExcelModal: {
      title: "Federation sheet export (Macro Excel)",
      description:
        "Pick the Excel template to use. Templates are filtered to match the competition format.",
      selectTemplate: "Export template",
      noTemplate:
        "No template available for this format. Ask an administrator to import one.",
      loading: "Loading templates...",
      loadError: "Could not load the templates list.",
      accessDenied:
        "This account cannot access Macro Excel templates. Sign in with an account that has the Licensed role.",
      export: "Export",
      exporting: "Exporting...",
      cancel: "Cancel",
      close: "Close",
      success: "Export completed successfully",
      saved: (savePath: string) => `File saved: ${savePath}`,
      error: "Macro Excel export failed",
      progressTitle: "Generating the federation sheet",
      savingTitle: "Saving the file...",
      progress: {
        pending: "Waiting...",
        preparing: "Preparing files...",
        connecting: "Connecting to the export server...",
        uploading: "Uploading data and template...",
        executing: "Generating the workbook...",
        downloading: "Retrieving the result...",
        cleaning: "Cleaning up...",
        done: "Done",
      },
      phaseLabels: {
        athletes: (current: number, total: number) =>
          `${current}/${total} athletes`,
        macroFede: (current: number, total: number) =>
          `${current}/${total} sheets`,
        merge: (current: number, total: number) => `${current}/${total} merges`,
      },
      /**
       * Strings emitted by the Windows agent / PS1 scripts (`Write-StatusFile`);
       * parsed client-side by `translateMacroExcelExternalStatusMessage`.
       */
      externalToolMessages: {
        preparingExport: "Preparing export…",
        writingAthletes: (current: number, total: number) =>
          `Writing athletes ${current}/${total}`,
        macroFeuilleChunk: (
          kind: string,
          macroCurrent: number,
          macroTotal: number,
          chunkCurrent: number,
          chunkTotal: number,
        ) =>
          `Macro sheet (${kind}) ${macroCurrent}/${macroTotal} - chunk ${chunkCurrent}/${chunkTotal}`,
        mergeSimple: (current: number, total: number) =>
          `Merge ${current}/${total}`,
        mergeBlock: (
          rangeStart: number,
          rangeEnd: number,
          total: number,
          targetFileName: string,
        ) =>
          `Merge block ${rangeStart}-${rangeEnd}/${total} to ${targetFileName}`,
        mergeCompleted: (
          current: number,
          total: number,
          targetFileName: string,
        ) => `Merge ${current}/${total} completed to ${targetFileName}`,
        mergeSaveChunk: (chunkIndex: number, chunkTotal: number) =>
          `Merge save chunk ${chunkIndex}/${chunkTotal}`,
        mergeChunkSaved: (
          chunkIndex: number,
          chunkTotal: number,
          mergedFileName: string,
        ) =>
          `Merge chunk ${chunkIndex}/${chunkTotal} saved → ${mergedFileName}`,
        exportCompleted: "Export completed",
        done: "Done",
      },
      saveDialogTitle: "Save the federation sheet",
      saveDialogExcelFilesFilter: "Excel files",
      saveDialogDefaultName: (defaultName: string) => defaultName,
      cancelConfirm:
        "The export is running on the server. Do you really want to abort it?",
      abortExportDialogTitle: "Abort export?",
      abortExportConfirmButton: "Abort",
      abortExportContinueButton: "Continue",
      cancelled: "Export cancelled.",
      saveCancelled: "Save cancelled.",
      errorMessages: {
        notConfigured:
          "The export server is not configured. Please contact your administrator.",
        alreadyRunning:
          "An export is already running for this competition. Please wait.",
        connection: "Could not connect to the export server.",
        upload: "Failed to upload files to the export server.",
        powershell: "The export script failed.",
        powershellTimeout:
          "The export script timed out. Retry or contact your administrator.",
        outputNotFound:
          "Output file not found. Make sure the script produces a workbook.",
        download: "Failed to download the generated file.",
        templateNotFound: "The selected template was not found on the server.",
        scriptNotFound:
          "No PowerShell script is attached to this template. Contact your administrator.",
        unknown: "An unexpected error occurred.",
      },
    },
  },
  startGroupConfirm: (competitionFormat: CompetitionFormat) =>
    `Do you really want to start the display of the group? Levels are empty. Import a match sheet via the levels administration page. (${competitionFormat})`,
  startGroupIncompleteConfirm: (count: number) => ({
    title: "Incomplete data",
    message: `${count} athlete${count > 1 ? "s have" : " has"} incomplete data (bodyweight, birth date, lot, openers or rack heights). Do you still want to start the group?`,
  }),
  levelAreEmptyError: (competitionFormat: CompetitionFormat) =>
    `Levels are empty. Import a match sheet via the levels administration page. (${competitionFormat === CompetitionFormat.FULL_POWER ? "FA/PL" : "DC/BP"})`,
  levelDateEmptyError:
    "Levels date is empty. Import a match sheet via the levels administration page.",
  exportFederationConfirm: (fileDate: string) =>
    `The federation sheet will be exported using the match sheet "${fileDate}".`,
  electronWeb: {
    dashboard: "Dashboard",
    startTimer: "Start timer",
    resumeTime: "Resume timer",
    stopTimer: "Stop timer",
    resetTimer: "Reset timer",
    resetLights: "Reset lights",
    powerliftingMode: "Powerlifting Mode",
    powerliftingModeDescription:
      "Powerlifting, 1 Jury, 3 Referees, White, Red, Blue and Yellow cards",
    timer: "Timer",
    timerDescription:
      "When unchecked the timer is disabled. In case you are using a separate timer.",
    timerMinutes: "Timer Minutes",
    timerMinutesDescription: "Set the timer to a value: (for example 1:32)",
    currentMinutes: "Current minutes:",
    apply: "Apply",
    customisation: "Customisation",
    resultDuration: "Result Duration",
    resultDurationDescription: "The duration until the result is shown.",
    currentSeconds: "Current seconds:",
    enterSeconds: "Enter seconds",
    settings: "Settings",
    ticketTime: "Ticket time:",
    juryDiscuss: "Hold, jury discussion",
    juryOverruled: "Jury, has overruled",
    jury: "Jury",
    leftReferee: "Left referee",
    chiefReferee: "Chief referee",
    rightReferee: "Right referee",
    home: "Home",
    youHaveBeenDisconnected: "You have been disconnected.",
    refereeConnected: "Connected to the PC",
    refereeConnecting: "Connecting to the PC…",
    refereeConnectionLost:
      "PC connection unavailable. Reconnecting automatically. Check that the phone and PC are on the same Wi-Fi network.",
    refereeReconnect: "Reconnect",
    refereeCommandNotSent:
      "Input not sent. Please try again once connected to the PC.",
    unableToConnectToServer: "Unable to connect to server",
    invalidTimeFormat:
      "Invalid time format. Please enter time in MM:SS format (e.g., 05:30).",
    connectionErrorHelp:
      'Go to referee lights settings and click the "Start" button',
    lights: {
      white: "White",
      red: "Red",
      blue: "Blue",
      yellow: "Yellow",
      timer: "Timer",
      barLoaded: "Bar loaded",
      juryDiscussion: "Stop (jury discussion)",
      resetJury: "Reset",
    },
  },
  lights: {
    showLightsInRank: "Show referee lights and timers on the ranking screen",
    cantStopApp:
      "You cannot stop the application because it is being used by the widgets.",
    confirmTitle: "Retrieve Wi-Fi Configuration",
    confirmMessage:
      "Would you like to retrieve your Wi-Fi configuration? This operation may require you to enter your administrator password.",
    confirmLabel: "Retrieve",
    cancelLabel: "No",
    settings: "Referee lights settings",
    typeSelector: {
      title: "Arbitration lights type",
      meetManager: "Meet Manager lights",
      ipf: "Official IPF lights",
      description: "Choose the type of arbitration lights to use",
    },
    start: "Start",
    stop: "Stop",
    openApp: "Open referee lights app",
    url: "App URL",
    fetchNetworkInformations: "Fetching network information...",
    wifiInformations: "Wi-Fi network informations",
    refresh: "Refresh",
    networkName: "Network name (SSID)",
    password: "Password",
    clientsConnected: "Clients connected",
    clientDeconnect: "The client has been deconnected.",
    helpTitle: "How to use referee lights",
    helpDescription:
      "This interface allows you to configure and manage referee lights for your competition.",
    helpStep1:
      "Connect to the same Wi-Fi network as the referee devices (network information is displayed below)",
    helpStep2: "Select the app URL from the list of available addresses",
    helpStep3: 'Click "Start" to launch the lights server',
    helpStep4:
      "Scan the QR codes with referee devices to access their respective interfaces",
    helpNote:
      "Note: Multiple addresses may be available on the same network. If the connection doesn't work with the first address, try the others one by one until you find the one that allows referees to connect.",
    connectToWifi: "Connect to Wi-Fi",
    tooltips: {
      settings: "Configure referee lights settings",
      start: "Start referee lights",
      stop: "Stop referee lights",
      show: "Show referee lights screen",
      disconnect: "Disconnect client",
      copyUrl: "Copy URL",
    },
    copyUrl: "Copy URL",
    urlCopied: "URL copied!",
    adminTable: {
      columnOs: "Operating system",
      columnBrowser: "Browser",
      columnDevice: "Device",
      columnPage: "Page",
      columnAction: "Actions",
    },
  },
  appLanguage: {
    label: "Language",
    french: "French",
    english: "English",
  },
  theme: {
    previewAthlete: "Athlete",
    previewGroup: "Group 2 · Squat · Attempt 2",

    themes: "Themes",
    default: "Default",
    errors: {
      nameRequired: "Name is required",
      thisThemeAlreadExists: "This theme already exists",
    },
    edit: "Edit theme",
    create: "Create theme",
    informations: "Theme informations",
    name: "Name",
    owner: "Owner",
    public: "Public",
    publicYes: "Public",
    publicNo: "Private",
    publicLabel: "Make this theme public (visible to all users)",
    deleteConfirm: 'Are you sure you want to delete the theme "{name}"?',
    deleteBlockedTitle: "Cannot delete",
    deleteBlockedMessage: (layouts: string[]) =>
      `This theme is used by the following layouts: ${layouts.join(", ")}. Please remove the association first.`,
    manageThemes: "Manage themes",
    myThemes: "My themes",
    noThemes: "No themes created yet.",
    placeholders: {
      name: "Theme name",
    },
    title: "Theme",
    settings: "Theme settings",
    primaryColor: "Primary color",
    primaryTextColor: "Primary text color",
    backgroundColor: "Background color",
    backgroundTextColor: "Background text color",
    rankingColor: "Background ranking color",
    rankingTextColor: "Ranking text color",
    preview: "Preview",
    previewTooltip: "Live preview using the selected colors",
    primaryButtonSample: "Primary action",
    colors: "Colors",
    colorsTooltip: "Customize the colors of the display screens",
    reset: "Reset to defaults",
    resetTooltip: "Restore the default colors",
  },
  ipfLightsModal: {
    title: "IPF Lights Connection",
    description: "Configure connection to official IPF lights",
    explanation:
      "To use the official IPF referee lights, you must be connected to the same Wi-Fi network as the lights controller.",
    urlLabel: "Controller URL",
    placeholder: "e.g. http://192.168.0.50:8080",
    connect: "Connect",
    connecting: "Connecting…",
    status: {
      label: "Status",
      connected: "Connected",
      notConnected: "Not connected",
    },
    connectedTo: "Connected URL",
    snack: {
      connected: "Connected to IPF lights.",
      error: "Connection failed: not connected to IPF lights.",
    },
  },
  moveGroupModal: {
    title: "Move athlete to another group",
    description:
      "Select the destination group, then confirm to move the athlete.",
    selectLabel: "Destination group",
    submit: "Confirm move",
    moving: "Moving…",
    cancel: "Cancel",
    snack: {
      success: "Athlete moved successfully.",
      error: "Failed to move athlete.",
      sameGroup: "The athlete is already in this group.",
      formatMismatch:
        "The destination group must use the same competition format as the current group.",
    },
    noCompatibleGroups:
      "No other group in this meet shares the same competition format.",
  },
  live: {
    admin: {
      accessControl: "Live Access",
      accessTooltip: "Controls access to the live system for this license",
      enabled: "Enabled",
      disabled: "Disabled",
      accessUpdated: (licenseName: string, status: string) =>
        `Live access updated for ${licenseName}: ${status}`,
      accessUpdateError: "Error updating live access",
    },
  },
  planning: {
    admin: {
      accessControl: "Planning Access",
      accessTooltip: "Controls access to planning features for this license",
      enabled: "Enabled",
      disabled: "Disabled",
      accessUpdated: (licenseName: string, status: string) =>
        `Planning access updated for ${licenseName}: ${status}`,
      accessUpdateError: "Error updating planning access",
    },
  },
  backup: {
    cleanupBackups: "Cleanup backups",
    beforeRestore: "Before restore",
    restore: "Restore",
    restoreSuccess: "Backup restored successfully",
    restoreError: "Error restoring backup",
    restoreRefreshError:
      "The backup was restored, but the display could not be refreshed. Reopen the meet.",
    confirmRestoreMessage:
      "The matching meet will be replaced by this backup. Its current state will be backed up automatically before replacement so you can undo the restore. If it does not exist on this computer, it will be added. Custom discipline schemas must be synchronized.",

    saving: "Saving...",
    saveNow: "Save now",
    lastBackupInstant: "Last backup just now",
    lastBackupMinutes: (minutes: number) => `Last backup ${minutes}min ago`,
    lastBackupHours: (hours: number) => `Last backup ${hours}h ago`,
    lastBackupDays: (days: number) => `Last backup ${days}d ago`,
    noBackup: "No backup",
    noAccessToServer: "No access to server",
    success: "Backup created successfully!",
    error: "Error creating backup",
    noActiveLicense: "No active license found",
    backupError: "Error creating backup",
    manager: "Backup Manager",
    create: "Create Backup",
    loading: "Loading backups...",
    noBackupsAvailable: "No backups available",
    createFirstBackup: "Create your first backup to get started",
    noMeetSelected: "No meet selected",
    selectMeetFirst: "Select a meet to access backups",
    deleteSuccess: "Backup deleted successfully",
    deleteError: "Error during deletion",
    noAccess: "Your license does not have access to the backup system",
    accessDenied: "Backup access denied",
    contactAdmin: "Contact your administrator to enable backup access",
    admin: {
      accessControl: "Backup Access",
      accessTooltip:
        "Controls access to the automatic backup system for this license",
      enabled: "Enabled",
      disabled: "Disabled",
      accessUpdated: (licenseName: string, status: string) =>
        `Backup access updated for ${licenseName}: ${status}`,
      accessUpdateError: "Error updating backup access",
    },
    confirmDelete: "Confirm deletion",
    confirmDeleteMessage: "Are you sure you want to delete the backup",
    confirmDeleteWarning: "This action is irreversible.",
    cancel: "Cancel",
    delete: "Delete",
    athletes: "athletes",
    groups: "groups",
    createdOn: "Created on",
  },
  account: {
    loading: "Initializing account system...",
    login: {
      title: "Login",
      usernameLabel: "Username or email",
      usernamePlaceholder: "Enter your username or email",
      passwordLabel: "Password",
      submitButton: "Sign in",
      openWebClientButton: "Open web application",
      validation: {
        loginRequired: "Username or email is required",
        passwordRequired: "Password is required",
        passwordMinLength: "Password must be at least 6 characters",
      },
    },
    profile: {
      notConnected: "No user connected",
      userType: {
        title: "User type",
        admin: "Administrator",
        licensed: "Licensed user",
        demo: "Demo user",
        unknown: "Unknown",
      },
      organization: "Organization",
      license: {
        title: "License",
        valid: "Valid license",
        invalid: "Invalid or expired license",
      },
      memberSince: "Member since",
      lastUpdate: "Last update",
      changePassword: "Change password",
      editTooltip: "Edit profile",
      logout: "Logout",
      profileButton: "Profile",
    },
    changePassword: {
      title: "Change password",
      description: "Enter your old password and choose a new secure password.",
      oldPasswordLabel: "Old password",
      newPasswordLabel: "New password",
      confirmPasswordLabel: "Confirm new password",
      submitButton: "Change password",
      submittingButton: "Changing...",
      cancelButton: "Cancel",
    },
    connection: {
      title: "Connection required",
      description:
        "A temporary connection is required to validate your license.",
      note: "This connection is temporary and does not maintain a persistent session.",
      loginTitle: "Temporary connection",
      loginSubtitle: "Sign in to validate your license",
      cancelButton: "Cancel",
      closeButton: "Close",
    },
    errors: {
      invalidCredentials: "Invalid username or password",
      accountDisabled: "Account disabled",
      emailInvalid: "Invalid email address",
      passwordTooWeak: "Password too weak",
      networkError: "Network error",
      rateLimitExceeded: "Too many login attempts",
      connectionRequired: "Connection required",
      serverUnavailable: "Authentication server unavailable",
      licenseExpired: "License expired - contact your administrator",
      licenseInactive: "Inactive license - contact your administrator",
      noLicense: "No active license - contact your administrator",
      licenseTermsNotAccepted:
        "Your license is not yet activated. Please sign in to the web application to read and accept the license terms.",
      licenseTermsNotAcceptedTitle: "License terms not accepted",
      oldPasswordIncorrect: "Old password incorrect",
      passwordChangeFailed: "Password change failed",
      passwordChangedSuccess: "Password changed successfully",
      tokenExpired: "Session expired",
      tokenNotFound: "Session not found",
      tokenInvalid: "Invalid session",
      authenticationRequired: "Authentication required",
    },
    warnings: {
      tokenExpired: {
        title: "Session expired",
        message: "Your session has expired. Please log in again to continue.",
        action: "Log in again",
      },
      tokenNotFound: {
        title: "Session not found",
        message: "No active session found. Please log in to access features.",
        action: "Log in",
      },
      authenticationRequired: {
        title: "Authentication required",
        message: "You must be logged in to access this feature.",
        action: "Log in",
      },
    },
    admin: {
      usersTitle: "User Management",
      createUser: "Create user",
      editUser: "Edit user",
      createUserButton: "Create user",
      createUserDescription: "Fill out the form to create a new user",
      editUserDescription: "Edit user information",
      userInformation: "User information",
      loginLabel: "Username",
      emailLabel: "Email address",
      passwordLabel: "Password",
      newPasswordLabel: "New password",
      newPasswordHelper: "Leave empty to keep current password",
      nameLabel: "Full name",
      organizationLabel: "Organization",
      noOrganization: "None",
      userTypeLabel: "User type",
      rolesLabel: "Roles",
      rolesHelper: "Select user roles",
      parentUserLabel: "Parent licensed account",
      parentUserHelper:
        "The DisplayEditor account will use this parent account's license.",
      parentUserRequired:
        "A parent licensed account is required for the DisplayEditor role",
      licenseLabel: "License",
      licenseHelper: "Select a license for the user",
      noLicense: "No license",
      speakerLicensesLabel: "Speaker licenses",
      speakerLicensesHelper:
        "The speaker will only see live meets for these licenses and connected features only on those meets (0..N).",
      cancelButton: "Cancel",
      createButton: "Create",
      updateButton: "Update",
      savingButton: "Saving...",
      table: {
        login: "Login",
        email: "Email",
        name: "Name",
        organization: "Organization",
        userType: "Type",
        roles: "Roles",
        license: "License",
        status: "Status",
        actions: "Actions",
        installerFile: "Installer",
        installerSize: "Size",
        installerDate: "Upload date",
        installerChecksum: "Checksum",
        noInstaller: "None",
        downloadInstaller: "Download installer",
        downloadError: "Download failed",
        termsAccepted: "Terms accepted",
        termsPending: "Terms pending",
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
      actions: {
        edit: "Edit",
        delete: "Delete",
        activate: "Activate",
        deactivate: "Deactivate",
        more: "More actions",
      },
      confirmDelete: {
        title: "Confirm deletion",
        message: (login: string) =>
          `Are you sure you want to delete user "${login}"?`,
        cancel: "Cancel",
        confirm: "Delete",
      },
      uploadInstaller: {
        menuLabel: "Upload installer",
        success: "Installer uploaded successfully",
        error: "Upload failed. Please try again.",
        acceptedExtensions: ".exe,.msi,.dmg,.pkg,.zip",
      },
    },
  },
  licensedPortal: {
    layoutEditor: {
      workspace: {
        unsaved: "Unsynced changes",
        noResults: "No layout matches your search.",
        emptyLibrary: "No layouts are available for this account.",
        recovered: "A local copy was recovered. Save to sync it.",
        recoveryConflict:
          "An unsynced local copy exists, but the online version has changed. Choose which version to keep before editing this document.",
        useLocal: "Recover my local copy",
        useRemote: "Keep the online version",
        recoveryFailed:
          "Local storage is unavailable. Your copy remains in memory for this session: save or export before closing or reloading the page.",
        alreadySaved: "The document is already saved.",
        history: "History",
        insert: "Insert",
        selection: "Selection",
        automation: "Behaviors",
        fit: "Fit scene",
        zoomIn: "Zoom in",
        zoomOut: "Zoom out",
        expandGroups: "Expand groups",
        collapseGroups: "Collapse groups",
      },
      title: "Layout Editor",
      save: "Save",
      newLayout: "New layout",
      editLayout: "Edit layout",
      deleteLayout: "Delete layout",
      deleteConfirm: "Are you sure you want to delete this layout?",
      canvasSize: "Canvas size",
      width: "Width",
      height: "Height",
      layoutName: "Layout name",
      layoutType: "Type",
      typeWidget: "Widget",
      typeDisplay: "Display",
      toolbar: {
        text: "Text",
        image: "Image",
        video: "Video",
        shape: "Shape",
        rectangle: "Rectangle",
        square: "Square",
        ellipse: "Ellipse",
        oval: "Oval",
        line: "Line",
        triangle: "Triangle",
        rightTriangle: "Right triangle",
        trapezoid: "Trapezoid",
        parallelogram: "Parallelogram",
        pentagon: "Pentagon",
        hexagon: "Hexagon",
        octagon: "Octagon",
        star: "Star",
        ranking: "Ranking",
        barLoader: "Bar Loader",
        layoutList: "List",
        qrCode: "QR code",
        icon: "Icon",
        duplicate: "Duplicate",
        bringForward: "Bring forward",
        sendBackward: "Send backward",
        bringForwardOne: "Move one step forward",
        sendBackwardOne: "Move one step backward",
        group: "Put in a container",
        ungroup: "Remove container",
        extractFromContainer: "Extract from container",
        openBehavior: "Open behavior",
        openSceneBehavior: "Open scene behavior",
        createAnimation: "Create animation",
        addKeyframe: "Add keyframe",
        saveAnimation: "Save animation",
        cancelAnimation: "Cancel animation",
        cut: "Cut",
        copy: "Copy",
        paste: "Paste",
        resetPreview: "Reset the preview as if the page had just opened",
        openRuntimePreview: "Open competition preview",
        delete: "Delete",
        undo: "Undo",
        redo: "Redo",
        undoTooltip: "Undo (Ctrl+Z)",
        redoTooltip: "Redo (Ctrl+Y or Ctrl+Shift+Z)",
        customDisciplineInput: "Input field",
        customDisciplineButton: "Button",
      },
      runtimePreview: {
        loading: "Loading preview...",
        loadError: "Unable to load preview.",
        persistedLayoutRequired:
          "Save this layout before opening the competition preview.",
        play: "Play",
        pause: "Pause",
        previousFrame: "Previous frame",
        nextFrame: "Next frame",
        hideControls: "Hide controls",
        showControls: "Show controls",
        sexProfile: "Profile",
        sexProfileMen: "2 men's groups",
        sexProfileWomen: "2 women's groups",
        sexProfileMenWomen: "Men's group + women's group",
        sexProfileMixedMen: "Mixed group + men's group",
        sexProfileMixedWomen: "Mixed group + women's group",
        sexProfileMixed: "2 mixed groups",
        group1Age: "Group 1 age",
        group2Age: "Group 2 age",
        secondsPerFrame: "Seconds / frame",
        themeColors: "Theme colors",
        resetThemeColors: "Reset",
        controlRoomTitle: "Preview control room",
        controlRoomSubtitle: "Local display actions",
        controlRoomPreviewTarget: "Competition preview",
        controlRoomNoCards:
          "No Control Room event is published in this layout.",
        hideControlRoom: "Hide control room",
        showControlRoom: "Show control room",
      },
      tabs: {
        display: "Editor",
        behavior: "Behavior",
        sceneBehavior: "Scene behavior",
        sceneInvalidTitle: "Invalid scene animations",
        sceneInvalidWarning:
          "Some scene animations are invalid and will no longer be played:",
        sceneInvalidConfirm: "Continue",
        sceneInvalidCancel: "Cancel",
        nodeMissing: "The linked node no longer exists.",
      },
      layers: {
        title: "Layout components",
        empty: "No component yet",
        container: "Container",
        searchPlaceholder: "Search...",
      },
      properties: {
        title: "Properties",
        searchPlaceholder: "Search...",
        positionSize: "Position & Size",
        style: "Style",
        visibility: "Visibility",
        width: "Width",
        height: "Height",
        fontSize: "Font size",
        color: "Color",
        background: "Background",
        backgroundColor: "Color",
        backgroundImage: "Gradient",
        backgroundImageTextHint:
          "On text, the gradient fills the letters (not the block background).",
        styleSubsectionBackground: "Background",
        borderRadius: "Border radius",
        border: "Border",
        fontFamily: "Font family",
        padding: "Spacing",
        opacity: "Opacity",
        fontWeight: "Font weight",
        textAlign: "Text align",
        textTransform: "Text transform",
        whiteSpace: "White space",
        overflow: "Overflow (visible, hidden, auto)",
        breakBefore: "Page break before (auto, page)",
        breakInside: "Page break inside (auto, avoid)",
        textDecorationLine: "Decoration line",
        textDecorationColor: "Decoration color",
        textDecorationStyle: "Decoration style",
        textDecorationThickness: "Decoration thickness",
        propertyPanelLabels: {
          textDecorationLine: "Line",
          textDecorationColor: "Color",
          textDecorationStyle: "Style",
          textDecorationThickness: "Thickness",
          textTransform: "Case",
          textShadow: "Shadow",
        },
        transform: "Skew",
        filter: "Filter",
        cursor: "Cursor",
        boxShadow: "Box shadow",
        textShadow: "Text shadow",
        dataBinding: "Data Binding",
        source: "Source",
        path: "Path (e.g. prenom, catPoid.nom)",
        none: "None",
        staticText: "Static text",
        imageUrl: "Image URL",
        selectImage: "Select an image",
        selectVideo: "Select a video",
        videoMuted: "Muted",
        videoLoop: "Loop",
        booleanYes: "Yes",
        booleanNo: "No",
        totalWeight: "Total weight",
        barWeight: "Bar weight",
        collarsWeight: "Collars weight",
        mode: "Mode",
        container: "Container",
        containerLayout: "Layout",
        containerLayoutAbsolute: "Absolute",
        containerLayoutFlex: "Flex",
        containerLayoutLockedFlex:
          "Flex layout is required for a relatively positioned container inside a flex parent.",
        containerFlexDirection: "Flex direction",
        containerFlexDirectionRow: "Row",
        containerFlexDirectionRowReverse: "Row reverse",
        containerFlexDirectionColumn: "Column",
        containerFlexDirectionColumnReverse: "Column reverse",
        containerGap: "Gap",
        containerJustifyContent: "Justification",
        containerJustifyContent_center: "Center",
        "containerJustifyContent_flex-start": "Start",
        "containerJustifyContent_flex-end": "End",
        containerJustifyContent_normal: "Normal",
        "containerJustifyContent_space-between": "Space between",
        "containerJustifyContent_space-around": "Space around",
        "containerJustifyContent_space-evenly": "Space evenly",
        containerAlignItems: "Item alignment",
        containerAlignItems_center: "Center",
        "containerAlignItems_flex-start": "Start",
        "containerAlignItems_flex-end": "End",
        "containerAlignItems_space-between": "Space between",
        "containerAlignItems_space-around": "Space around",
        "containerAlignItems_space-evenly": "Space evenly",
        containerSizeModeFixed: "Fixed",
        containerSizeModeFitContent: "Fit Content",
        listModePreview: "Preview",
        listModeEdit: "Edit",
        listLine: "Row",
        listLineDefault: "Default",
        listMaxItems: "Maximum items",
        listRowKeyPath: "Identifier",
        listTransitionSection: "Transition",
        listTransitionEnabled: "Enable",
        listTransitionEnabledYes: "Yes",
        listTransitionEnabledNo: "No",
        listTransitionProperties: "Propertie",
        listTransitionDuration: "Duration (ms)",
        listTransitionEasing: "Easing",
        listTransitionDelay: "Delay (ms)",
        listTransitionPropertyLabels: {
          transform: "Transform",
          left: "Left",
          top: "Top",
          height: "Height",
          width: "Width",
          opacity: "Opacity",
          "background-color": "Background color",
          color: "Text color",
          border: "Border",
          "border-radius": "Border radius",
          "box-shadow": "Box shadow",
          "font-size": "Font size",
          padding: "Padding",
          margin: "Margin",
        },
        dataSource: "Data source",
        listDirection: "List direction",
        listDirection_column: "Column",
        listDirection_row: "Row",
        "listDirection_column-reverse": "Column reversed",
        "listDirection_row-reverse": "Row reversed",
        partsLabel: "Content (text and bindings)",
        addPart: "Add item",
        addPartLiteral: "Add text",
        addPartBinding: "Add binding",
        partLiteral: "Plain text",
        partBinding: "Binding",
        partTypeLiteral: "Text",
        data: "Data",
        partTypeBinding: "Binding",
        format: "Format",
        pathSubField: "Sub-field",
        positionX: "X",
        positionY: "Y",
        positionWidth: "Width",
        positionHeight: "Height",
        positionRotation: "Rotate",
        layerLabel: "Label",
        styleVisibilityVisible: "Visible",
        styleVisibilityHidden: "Hidden",
        stylePosition: "Position",
        stylePositionRelative: "Relative",
        stylePositionAbsolute: "Absolute",
        styleCategorylayout: "Layout",
        styleCategoryfont: "Text",
        styleCategorydecoration: "Decoration",
        styleCategorycolors: "Colors",
        styleCategoryborder: "Border",
        styleCategoryeffects: "Effects",
        styleCategorytransform: "Transform",
        margin: "Spacing",
        behaviorControlledTooltip:
          "This field is controlled by behavior. Click to open its Behavior tab.",
        tooltip: {
          section: "Tooltip",
          kindNone: "None",
          kindText: "Text",
          kindLayout: "Layout",
          text: "Tooltip text",
          editLayout: "Edit layout",
          layoutWidth: "Width",
          layoutHeight: "Height",
          backgroundColor: "Background",
          opacity: "Opacity",
          layoutDialogTitle: "Tooltip layout",
          layoutDialogCancel: "Cancel",
          layoutDialogSave: "Save",
        },
        customDisciplineInputFields: {
          key: "Key",
          label: "Label",
          type: "Type",
          defaultValue: "Default value",
          required: "Required",
          readOnly: "Read only",
          optionSource: "Option source",
          staticOptions: "Static options",
          optionValuePath: "Value path",
          optionLabelPath: "Label path",
        },
        customDisciplineButtonFields: {
          label: "Label",
          variant: "Variant",
          startIcon: "Icon",
          noneIcon: "No icon",
          size: "Size",
          color: "Color",
          disabled: "Disabled",
        },
        iconFields: {
          iconName: "Icon",
          noneIcon: "No icon",
          color: "Color",
          size: "Size",
        },
        barLoaderModeFull: "Full",
        barLoaderModeSmall: "Small",
        barLoaderModeBar: "Bar",
        barLoaderUnit: "Unit",
        barLoaderUnitKg: "KG",
        barLoaderUnitLbs: "LBS",
        barLoaderLabelOrientation: "Label orientation",
        barLoaderLabelOrientationHorizontal: "Horizontal",
        barLoaderLabelOrientationVertical: "Vertical",
        barLoaderOptionalDisks: "Optional plate",
        barLoaderOptionalDiskOff: "Disabled",
        barLoaderOptionalDiskOn: "Enabled",
        barLoaderCompactWidthMode: "Disk width",
        barLoaderCompactWidthModeFixed: "Fixed",
        barLoaderCompactWidthModeVariable: "Variable",
        barLoaderCompactDiskAlign: "Disk alignment",
        barLoaderCompactDiskAlignLeft: "Left",
        barLoaderCompactDiskAlignCenter: "Center",
        barLoaderCompactDiskAlignRight: "Right",
        barLoaderGlobalStyle: "Global style",
        barLoaderDiskStyle: "Disk label style",
        barLoaderDiskBodyStyle: "Disk style",
        barLoaderLabelSection: "Label",
        barLoaderDiskSection: "Disk",
        all: "All",
        barLoaderDiskSelect: "Disk",
        barLoaderResetDiskStyle: "Reset disk style",
        barLoaderResetDiskStyleTooltip: "Reset this disk to the global style",
        barLoaderLabelText: "Text",
        qrCodeValue: "Value",
        qrCodeStyleHint:
          "QR size follows the node width/height (resize on canvas). QR color uses text color; background uses background color.",
        styleOpacity: "Opacity",
        styleTransform: "Skew",
        styleFontSize: "Size",
        styleFontFamily: "Font",
        styleFontWeight: "Weight",
        styleTextAlign: "Align",
        styleTextTransform: "Text transform",
        styleWhiteSpace: "White space",
        styleTextDecorationLine: "Decoration line",
        styleTextDecorationColor: "Decoration color",
        styleTextDecorationStyle: "Decoration style",
        styleTextDecorationThickness: "Decoration thickness (px)",
        styleTextAlignLeft: "Left",
        styleTextAlignCenter: "Center",
        styleTextAlignRight: "Right",
        bindingFormats: {
          date: "Date",
          time: "Time",
          uppercase: "Uppercase",
          lowercase: "Lowercase",
          kg: "kg",
        },
      },
      colorPicker: {
        hexMode: "HEX",
        rgbMode: "RGB",
        hslMode: "HSL",
        opacity: "Opacity",
      },
      borderPicker: {
        title: "Border",
        reset: "Reset",
        scopeLabel: "Scope",
        scopeAll: "All",
        scopeTop: "Top",
        scopeRight: "Right",
        scopeBottom: "Bottom",
        scopeLeft: "Left",
        styleLabel: "Style",
        styleSolid: "Solid",
        styleDashed: "Dashed",
        styleDotted: "Dotted",
        styleDouble: "Double",
        styleNone: "None",
        widthLabel: "Width",
        colorLabel: "Color",
      },
      borderRadiusPicker: {
        title: "Border Radius",
        reset: "Reset",
        scopeLabel: "Scope",
        scopeAll: "All",
        scopeTopLeft: "Top left",
        scopeTopRight: "Top right",
        scopeBottomLeft: "Bottom left",
        scopeBottomRight: "Bottom right",
        radiusLabel: "Radius",
      },
      spacingPicker: {
        title: "Spacing",
        reset: "Reset",
        scopeAll: "All",
        scopeTop: "Top",
        scopeRight: "Right",
        scopeBottom: "Bottom",
        scopeLeft: "Left",
        marginScopeLabel: "Outer margin scope",
        marginValueLabel: "Outer margin value",
        paddingScopeLabel: "Inner margin scope",
        paddingValueLabel: "Inner margin value",
      },
      boxShadowPicker: {
        title: "Box Shadow",
        reset: "Reset",
        layerLabel: "Layer",
        blur: "Blur",
        spread: "Spread",
        color: "Color",
        inset: "Inset",
        insetNo: "No",
        insetYes: "Yes",
      },
      textShadowPicker: {
        title: "Text Shadow",
        reset: "Reset",
        layerLabel: "Layer",
        blur: "Blur",
        color: "Color",
      },
      filterPicker: {
        title: "Filter",
        reset: "Reset",
        blur: "Blur",
        brightness: "Brightness",
        contrast: "Contrast",
        grayscale: "Grayscale",
        hueRotate: "Hue rotate",
        invert: "Invert",
        opacity: "Opacity",
        saturate: "Saturate",
        sepia: "Sepia",
        dropShadow: "Drop shadow",
        svgUrl: "SVG filter URL",
        svgFileNamePlaceholder: "file.svg",
        svgFilterIdPlaceholder: "filter-element-id",
        selectFile: "Browse",
      },
      gradientPicker: {
        title: "Gradient",
        reset: "Reset",
        typeLabel: "Gradient type",
        typeLinear: "Linear",
        typeRepeatingLinear: "Repeating linear",
        typeRadial: "Radial",
        typeRepeatingRadial: "Repeating radial",
        typeConic: "Conic",
        typeRepeatingConic: "Repeating conic",
        linearSection: "Linear",
        radialSection: "Radial",
        conicSection: "Conic",
        direction: "Direction",
        angleMode: "Angle",
        sideMode: "Side / Corner",
        angle: "Angle",
        side: "Side",
        shape: "Shape",
        size: "Size",
        position: "Position",
        from: "From",
        interpolation: "Color interpolation",
        colorSpace: "Space",
        defaultSpace: "Default",
        hueMethod: "Hue",
        defaultHue: "Default",
        stopsLabel: "Color stops",
        stopLabel: "Stop",
        color: "Color",
        positionA: "Position A",
        positionB: "Position B",
        angleA: "Angle A",
        angleB: "Angle B",
        hint: "Hint",
        linearSideToTop: "To top",
        linearSideToRight: "To right",
        linearSideToBottom: "To bottom",
        linearSideToLeft: "To left",
        linearSideToTopRight: "To top right",
        linearSideToTopLeft: "To top left",
        linearSideToBottomRight: "To bottom right",
        linearSideToBottomLeft: "To bottom left",
        radialSizeClosestSide: "Closest side",
        radialSizeClosestCorner: "Closest corner",
        radialSizeFarthestSide: "Farthest side",
        radialSizeFarthestCorner: "Farthest corner",
        positionCenter: "Center",
        positionTop: "Top",
        positionBottom: "Bottom",
        positionLeft: "Left",
        positionRight: "Right",
        positionTopLeft: "Top left",
        positionTopRight: "Top right",
        positionBottomLeft: "Bottom left",
        positionBottomRight: "Bottom right",
        preview: "Preview",
        rawMode: "Raw CSS mode",
      },
      animationPanel: {
        title: "Animation",
        animationLabel: "Animation",
        addAnimation: "Add animation",
        removeAnimation: "Remove animation",
        animationType: "Animation type",
        none: "None",
        phase: "Launch mode",
        triggerSection: "Trigger",
        triggerType: "Trigger type",
        editCondition: "Edit condition",
        source: "Source",
        path: "Path",
        paramsSection: "Parameters",
        durationMs: "Duration (ms)",
        delayMs: "Delay (ms)",
        cooldownMs: "Cooldown (ms)",
        iterationCount: "Iterations",
        easing: "Easing",
        intensity: "Intensity",
        direction: "Direction",
        angleDeg: "Angle",
        preview: "Preview",
        triggerTypes: {
          mount: "On mount",
          "condition-true": "Condition",
          "value-change": "Value change",
        },
        types: {
          fade: "Fade",
          slide: "Slide",
          "slide-up": "Slide up",
          "slide-down": "Slide down",
          "slide-left": "Slide left",
          "slide-right": "Slide right",
          "zoom-in": "Zoom in",
          "zoom-out": "Zoom out",
          pop: "Pop",
          wipe: "Wipe",
          "wipe-left": "Wipe left",
          "wipe-right": "Wipe right",
          "wipe-up": "Wipe up",
          "wipe-down": "Wipe down",
          "blur-in": "Blur in",
          "blur-out": "Blur out",
          bounce: "Bounce",
          drift: "Drift",
        },
        easings: {
          linear: "Linear",
          ease: "Ease",
          "ease-in": "Ease in",
          "ease-out": "Ease out",
          "ease-in-out": "Ease in-out",
        },
        intensities: {
          soft: "Soft",
          medium: "Medium",
          strong: "Strong",
        },
        directions: {
          up: "Up",
          down: "Down",
          left: "Left",
          right: "Right",
        },
        phases: {
          in: "Entry",
          out: "Exit",
          "in-out": "Entry + exit",
        },
      },
      animationCreator: {
        cancelledBySelection:
          "Animation creation cancelled because selection changed.",
        nameDialogTitle: "Animation name",
        nameDialogLabel: "Name",
        nameDialogConfirm: "Create",
        nameDialogCancel: "Cancel",
        nameEmptyError: "Name cannot be empty.",
        nameTakenError: "An animation with this name already exists.",
      },
      catalogPaste: {
        conflictTitle: "Catalog name conflicts",
        conflictDescription:
          "Some referenced animations, events, functions or variables already exist in this layout. Rename them to continue pasting.",
        typeAnimation: "Animation",
        typeEvent: "Event",
        typeFunction: "Function",
        typeVariable: "Variable",
        oldNameLabel: "Previous name",
        newNameLabel: "New name",
        nameEmptyError: "Name cannot be empty.",
        nameTakenError: "An entry with this name already exists.",
        confirm: "Paste",
        cancel: "Cancel",
      },
      dataSources: {
        currentElement: "Current element",
        currentValue: "Current value",
        athlete: "Athlete",
        selectedRowId: "Selected row ID",
        selectedColumnKey: "Selected column",
        currentAthlete: "Current athlete",
        athletes: "Athletes",
        group: "Context group",
        currentGroup: "Current group",
        groups: "Groups",
        currentMeet: "Current meet",
        rowIndex: "Index",
        layoutListRowItem: "Current row item",
        currentNode: "Current node",
        timer: "Timer",
        timerSeconds: "Timer (seconds)",
        attemptTimers: "Attempt timers",
        attemptTimerSeconds: "Attempt timers (seconds)",
        currentLift: "Current lift",
        currentMouv: "Current exercise",
        currentMovement: "Current movement",
        attemptNumber: "Attempt number",
        placementIfSuccessful: "Placement if successful",
        meetName: "Meet name",
        startDate: "Start date",
        endDate: "End date",
        refereeLights: "Referee lights",
        nextLiftData: "Next lift",
        records: "Records",
        niveaux: "Levels",
        levels: "Levels",
        ageCategories: "Age categories",
        disciplineAgeCategories: "Discipline age categories",
        disciplineWeightCategories: "Discipline weight categories",
        weightCategories: "Weight categories",
        disciplines: "Disciplines",
        leagues: "Organisations",
        organisation: "Organisation",
        organisations: "Organisations · catalog",
        displaySettings: "Display settings",
        themeColors: "Theme colors",
      },
      fields: {
        athlete: {
          id: "ID",
          nom: "Last name",
          prenom: "First name",
          club: "Club",
          licence: "License",
          sexe: "Sex",
          dateNaissance: "Birth date",
          pdc: "Body weight",
          lot: "Lot",
          group_id: "Group ID",
          squat1: "Squat 1",
          squat1Status: "Squat 1 status",
          squat1Auto: "Squat 1 auto",
          squat1Overturn: "Squat 1 overturn",
          squat1LeftColor: "Squat 1 left",
          squat1ChiefColor: "Squat 1 chief",
          squat1RightColor: "Squat 1 right",
          squat1OverturnColor: "Squat 1 overturn color",
          squat2: "Squat 2",
          squat2Status: "Squat 2 status",
          squat2Auto: "Squat 2 auto",
          squat2Overturn: "Squat 2 overturn",
          squat2LeftColor: "Squat 2 left",
          squat2ChiefColor: "Squat 2 chief",
          squat2RightColor: "Squat 2 right",
          squat2OverturnColor: "Squat 2 overturn color",
          squat3: "Squat 3",
          squat3Status: "Squat 3 status",
          squat3Auto: "Squat 3 auto",
          squat3Overturn: "Squat 3 overturn",
          squat3LeftColor: "Squat 3 left",
          squat3ChiefColor: "Squat 3 chief",
          squat3RightColor: "Squat 3 right",
          squat3OverturnColor: "Squat 3 overturn color",
          bench1: "Bench 1",
          bench1Status: "Bench 1 status",
          bench1Auto: "Bench 1 auto",
          bench1Overturn: "Bench 1 overturn",
          bench1LeftColor: "Bench 1 left",
          bench1ChiefColor: "Bench 1 chief",
          bench1RightColor: "Bench 1 right",
          bench1OverturnColor: "Bench 1 overturn color",
          bench2: "Bench 2",
          bench2Status: "Bench 2 status",
          bench2Auto: "Bench 2 auto",
          bench2Overturn: "Bench 2 overturn",
          bench2LeftColor: "Bench 2 left",
          bench2ChiefColor: "Bench 2 chief",
          bench2RightColor: "Bench 2 right",
          bench2OverturnColor: "Bench 2 overturn color",
          bench3: "Bench 3",
          bench3Status: "Bench 3 status",
          bench3Auto: "Bench 3 auto",
          bench3Overturn: "Bench 3 overturn",
          bench3LeftColor: "Bench 3 left",
          bench3ChiefColor: "Bench 3 chief",
          bench3RightColor: "Bench 3 right",
          bench3OverturnColor: "Bench 3 overturn color",
          deadlift1: "Deadlift 1",
          deadlift1Status: "Deadlift 1 status",
          deadlift1Auto: "Deadlift 1 auto",
          deadlift1Overturn: "Deadlift 1 overturn",
          deadlift1LeftColor: "Deadlift 1 left",
          deadlift1ChiefColor: "Deadlift 1 chief",
          deadlift1RightColor: "Deadlift 1 right",
          deadlift1OverturnColor: "Deadlift 1 overturn color",
          deadlift2: "Deadlift 2",
          deadlift2Status: "Deadlift 2 status",
          deadlift2Auto: "Deadlift 2 auto",
          deadlift2Overturn: "Deadlift 2 overturn",
          deadlift2LeftColor: "Deadlift 2 left",
          deadlift2ChiefColor: "Deadlift 2 chief",
          deadlift2RightColor: "Deadlift 2 right",
          deadlift2OverturnColor: "Deadlift 2 overturn color",
          deadlift3: "Deadlift 3",
          deadlift3Status: "Deadlift 3 status",
          deadlift3Auto: "Deadlift 3 auto",
          deadlift3Overturn: "Deadlift 3 overturn",
          deadlift3LeftColor: "Deadlift 3 left",
          deadlift3ChiefColor: "Deadlift 3 chief",
          deadlift3RightColor: "Deadlift 3 right",
          deadlift3OverturnColor: "Deadlift 3 overturn color",
          squat1Changes: "Squat 1 changes",
          bench1Changes: "Bench 1 changes",
          bench3Changes: "Bench 3 changes",
          deadlift1Changes: "Deadlift 1 changes",
          deadlift3Changes: "Deadlift 3 changes",
          totalkg: "Total kg",
          totalAnnonced: "Total announced",
          placement: "Placement",
          hauteurSquat: "Squat height",
          hauteurDc: "Bench height",
          hauteurSecu: "Security height",
          cales: "Blocks",
          positionRack: "Rack position",
          catAgeId: "Age category ID",
          catPoidId: "Weight category ID",
          indice: "Index",
          disciplineId: "Discipline ID",
          isDisqualified: "Disqualified",
          catPoidLabel: "Weight category",
          catAgeLabel: "Age category",
          disciplineLabel: "Discipline",
          powerscoutBestsLabel: "Powerscout records",
          catPoid: {
            nom: "Name",
            poidMin: "Min weight",
            poidMax: "Max weight",
            sex: "Sex",
            isJunior: "Junior",
          },
          catAge: {
            nom: "Name",
            code: "Code",
            ageMin: "Min age",
            ageMax: "Max age",
          },
          discipline: { code: "Code", nom: "Name" },
          powerscoutBests: {
            best_squat_kg: "Squat",
            best_bench_kg: "Bench",
            best_deadlift_kg: "Deadlift",
            best_total_kg: "Total",
          },
        },
        group: {
          id: "ID",
          nom: "Name",
          meet_id: "Meet ID",
          plateauNumber: "Platform number",
          groupNumber: "Group number",
          observations: "Observations",
          secretaire: "Secretary",
          assesseur1: "Assessor 1",
          assesseur2: "Assessor 2",
          chefPlateau: "Platform chief",
          jury: "Jury",
          share: "Shared",
          competitionFormat: "Format",
          selected: "Selected",
        },
        meet: {
          id: "ID",
          guid: "GUID",
          name: "Name",
          ville: "City",
          startdate: "Start date",
          enddate: "End date",
          streamUrl: "Stream URL",
          ligue_id: "Organisation ID",
          ligueLabel: "Organisation",
          ligue: { id: "ID", nom: "Name" },
        },
        organisation: {
          id: "ID",
          name: "Name",
          code: "Code",
          countryCode: "Country code",
          recordLabel: "Record label",
          recordColor: "Record color",
          recordBadgeUrl: "Record badge URL",
          parentId: "Parent ID",
        },
        record: {
          organisationId: "Organisation ID",
          organisationLabel: "Organisation",
        },
        currentNode: {
          x: "Position X",
          y: "Position Y",
          width: "Width",
          height: "Height",
          rotation: "Rotation",
          skewX: "Skew X",
          skewY: "Skew Y",
        },
      },
      conditions: {
        addCondition: "Add condition",
        condition: "Condition",
        workspaceHint:
          "Drag blocks from the palette or reorder them using the dashed drop zones.",
        rootWrapHint:
          "To combine rules, drop an “And” or “Or” block above, or use the buttons.",
        paletteTitle: "Blocks",
        paletteItemSimple: "Comparison",
        paletteItemAnd: "And group",
        paletteItemOr: "Or group",
        paletteDragHint: "Drag a block and drop it on a dashed zone.",
        blockCompare: "Comparison",
        blockLogicAndHeader: "And (all must be true)",
        blockLogicOrHeader: "Or (at least one true)",
        dropInsertHere: "Drop zone",
        removeBlock: "Remove this block",
        styleValueLabel: "Value",
        valueWhenTrueParts: "Value when condition is true (text content)",
        defaultValue: "Default value",
        valueWhenTrue: "Value when condition is true",
        ifThen: "If … then",
        operator: "Operator",
        operators: {
          eq: "equals",
          neq: "not equals",
          gt: "greater than",
          gte: "greater or equal",
          lt: "less than",
          lte: "less or equal",
          defined: "defined",
          empty: "empty",
        },
        and: "And",
        or: "Or",
        addSimpleCondition: "Add simple condition",
        addAndGroup: "Add AND group",
        addOrGroup: "Add OR group",
        leftOperand: "Left operand",
        rightOperand: "Right operand",
        literal: "Literal",
        binding: "Binding",
        invalidCondition: "Invalid condition",
        conditionRequired: "Condition is required",
        partConditional: "Conditional",
        partConditionalPlaceholder:
          "Conditional part (edit in a future version)",
        removeConditionRule: "Remove rule",
        save: "Save",
        cancel: "Cancel",
      },
      mock: {
        mockData: "Preview data",
        editMockData: "Edit mock data",
      },
      propertyPanelAria: {
        movePartUp: "Move part up",
        movePartDown: "Move part down",
        removePart: "Remove part",
        addStyleCondition: "Add style condition",
        removeStyleConditionRule: "Remove style condition rule",
      },
      noSelection: "Select an element to edit its properties.",
      imageUploadError: "Error while uploading image",
      videoUploadError: "Error while uploading video",
      preview: "Preview",
      savedSuccess: "Layout saved successfully",
      savedError: "Error saving layout",
      loadError: "Layout not found",
      createModalTitle: "Create a layout",
      createModalCancel: "Cancel",
      createModalCreate: "Create",
      createSuccess: "Layout created successfully",
      createError: "Error creating layout",
      dimensionsPositive: "Dimensions must be positive",
      deleteSuccess: "Layout deleted",
      deleteError: "Error deleting layout",
      exportJson: "Export layout",
      exportSuccess: "Layout export downloaded",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",
    },
    layouts: {
      listTitle: "My layouts",
      createButton: "Create a layout",
      importButton: "Import a layout",
      importInvalidJson: "The layout file is invalid.",
      importApplyError: "The layout was created, but importing content failed.",
      open: "Open",
      delete: "Delete",
      emptyList: "No layouts. Create one to get started.",
      dimensions: "Dimensions",
      updatedAt: "Updated",
      themeLabel: "Theme",
      targetLabel: "Screen / Widget",
      disciplineLabel: "Discipline",
      defaultDisciplineLabel: "Default / all disciplines",
      noTheme: "No theme",
      selectTheme: "Select a theme",
      selectTarget: "Select a screen / widget",
      none: "None",
      targetAlreadyUsed: "This screen/widget is already used in this theme.",
      readonlyBanner:
        "This theme is public and you are not the owner. Read-only mode.",
      sectionMyLayouts: "My layouts",
      targetKeys: {
        athlete_display: "Athlete",
        loader_display: "Loader",
        ranking_display: "Ranking",
        public_display: "Public",
        lights_display: "Lights",
        current_athlete: "Current athlete",
        current_category: "Current category",
        current_group: "Current group",
        referee_lights: "Referee lights",
        timer: "Timer",
      },
    },
    title: "Licensed account space",
    subtitle:
      "Manage your contact details, license information, and installer downloads.",
    profile: {
      title: "Contact information",
      subtitle: "Review and update your contact details.",
      loginLabel: "Login",
      nameLabel: "Name",
      organizationLabel: "Organization",
      emailLabel: "Email",
      emailPlaceholder: "Enter your contact email",
      emailHelper: "This email will be used for all license communications.",
      saveButton: "Update email",
      emptyValue: "Not provided",
    },
    license: {
      title: "License details",
      subtitle: "License usage and activation status.",
      keyLabel: "License key",
      expirationLabel: "Expiration date",
      usageLabel: "Athlete usage",
      statusLabel: "Status",
      statusActive: "Active",
      statusInactive: "Inactive",
      activationsTitle: "Registered machines",
      noActivation: "No machine registered yet.",
      activationItem: (machine: string) => `Machine: ${machine}`,
    },
    installer: {
      title: "Desktop installer",
      subtitle: "Download the latest desktop application installer.",
      fileNameLabel: "File",
      sizeLabel: "Size",
      dateLabel: "Uploaded on",
      downloadButton: "Download installer",
      emptyState: "No installer available for this account yet.",
      refreshLabel: "Refresh installer metadata",
    },
    password: {
      title: "Change password",
      description: "Secure your account with a new password.",
      oldPasswordLabel: "Current password",
      newPasswordLabel: "New password",
      confirmPasswordLabel: "Confirm new password",
      passwordHelper: "At least 8 characters with recommended complexity.",
      mismatchError: "Passwords do not match.",
      weakPasswordError: "Password must contain at least 8 characters.",
      cancelLabel: "Cancel",
      submitLabel: "Update password",
      openButton: "Change password",
    },
    liveMeets: {
      liveButton: (meetName: string) => `Live: ${meetName}`,
      hubTitle: "Live competition",
      hubTitleWithName: (meetName: string) => `Live: ${meetName}`,
      hubSubtitle: "Open the screen and widget catalogs for this competition.",
      backToDashboard: "Back to licensed space",
      loadError: "Unable to load live competitions.",
      displaysButton: "Screen catalog",
      widgetsButton: "Widget catalog",
    },
    licenseTermsAcceptance: {
      refusalMessage:
        "You must accept the license terms to use Meet Manager. Your access has not been activated.",
      verifyError: "Unable to verify license terms",
      acceptSuccess: "License terms accepted.",
      acceptError: "Unable to record acceptance",
      pdfPreviewUnavailable:
        "PDF preview is not available. You can open the document in a new tab.",
      dialogTitle: "Meet Manager license terms",
      intro:
        "Before using Meet Manager, you must read and accept the license terms that apply to your account.",
      noActiveVersion:
        "No active version of the terms is available. Please contact support.",
      versionLabel: "Version",
      hashLabel: "SHA-256 hash",
      openPdfLink: "Open or download the terms as PDF",
      checkboxLabel:
        "I confirm that I have read and accept the Meet Manager license terms.",
      refuseButton: "Decline",
      signOutButton: "Sign out",
      acceptButton: "Accept and activate my license",
    },
    acceptedTerms: {
      title: "Accepted terms of use",
      subtitle: "Review the terms versions you have accepted for this account.",
      empty: "No acceptance recorded yet.",
      versionLabel: "Version",
      acceptedAtLabel: "Accepted on",
      openPdfButton: "Open PDF",
      versionFallback: (version: string) => `Terms version ${version}`,
      methodWeb: "Web acceptance",
      methodAdminManual: "Recorded by an administrator",
      methodEmailReply: "Email reply",
      methodImport: "Import",
    },
    snack: {
      emailUpdated: "Email updated successfully.",
      emailUpdateError: "Unable to update email.",
      downloadReady: "Installer download started.",
      downloadError: "Unable to download the installer.",
      missingInstaller: "No installer available yet.",
      passwordChanged: "Password updated successfully.",
      passwordChangeError: "Unable to change password.",
      loadError: "Unable to load user data.",
      unauthorized: "You are not allowed to access this area.",
      planningAccessRequired:
        "Your license does not allow access to planning pages.",
      planningSignInRequired:
        "You must be signed in with a valid license to access this page.",
      licenseSignInRequired:
        "You must be signed in with a valid license to access this page.",
      licenseFeatureAccessRequired:
        "Your license does not allow access to this feature.",
    },
    myMeets: {
      title: "My competitions",
      tabCompetitions: "Competitions",
      tabPlannings: "Plannings",
      noResults: "No competition matches your search",
      empty: "You have not created any competition",
    },
    myRecords: {
      title: "My records",
      subtitle:
        "Edit your organisation's records; parent organisation records are shown read-only.",
      openButton: "My records",
      organisationRequired:
        "No organisation is linked to your account. Please contact an administrator.",
      downloadEmptyTemplate: "Download empty template",
      downloadCurrentRecords: "Export current records",
      importRecords: "Import records",
      importedRecords: (count: number) => `${count} record(s) imported`,
      exportSuccess: "Records workbook downloaded",
      workbookError: "Unable to process the records workbook",
      deleteConfirm: (athleteName: string) =>
        `Delete the record for “${athleteName}”?`,
      deleteSuccess: "Record deleted",
      deleteError: "Unable to delete the record",
      discipline: "Discipline",
      sex: "Sex",
    },
    myLevels: {
      title: "My levels",
      subtitle:
        "Manage your organisation's minima via the portable workbook. Only one set of levels applies (exclusive inheritance, never stacked with the parent).",
      openButton: "My levels",
      organisationRequired:
        "No organisation is linked to your account. Please contact an administrator.",
      downloadEmptyTemplate: "Download empty template",
      downloadCurrentLevels: "Export current levels",
      importLevels: "Import levels",
      importedLevels: (count: number) => `${count} level(s) imported`,
      exportSuccess: "Levels workbook downloaded",
      workbookError: "Unable to process the levels workbook",
      deleteConfirm: (name: string) => `Delete the level “${name}”?`,
      deleteSuccess: "Level deleted",
      deleteError: "Unable to delete the level",
      discipline: "Discipline",
      cannotEditInherited:
        "Cannot edit inherited levels: change the federation source first.",
    },
    backups: {
      title: "My backups",
      noResults: "No backup matches your search",
      empty: "You have no backup",
      emptyForMeet: "You have no backup for this competition",
      status: {
        ongoing: "Ongoing",
        finished: "Finished",
      },
      backupLabel: "Backup",
      backupDateLabel: "Backup date",
      machineLabel: "Machine",
      athletesLabel: "Athletes",
      groupsLabel: "Groups",
    },
    planningBuilder: {
      title: "Planning builder",
      subtitle: "Excel import, automatic generation, drag & drop adjustment.",
      stepImport: "Import and edit",
      stepParams: "Parameters",
      stepBoard: "Planning",
      stepExports: "Exports",
      importExcel: "Import Excel file",
      meetName: "Competition name",
      editTable: "Edit fields below",
      maxAthletesPerGroup: "Max athletes per group",
      minAthletesPerGroup: "Min athletes per group",
      groupsPerPlateau: "Groups per platform",
      groupPackingOrder: "Group composition",
      groupPackingOrderByWeight: "By weight category",
      groupPackingOrderByAge: "By age category",
      attemptDurationSec: "Duration per attempt (s)",
      breakBetweenMovements: "Break between movements (min)",
      breakBetweenPlateaux: "Break between platforms (min)",
      weighInHoursBeforeStart: "Weigh-in (hours before start)",
      stepParamsGenerationSection: "Generation parameters",
      stepParamsTimingSection: "Durations and breaks",
      stepParamsHoursSection: "Hours per day",
      firstPlateauStart: "First platform start",
      generate: "Generate planning",
      plateau: "Platform",
      group: "Group",
      category: "Category",
      athletes: "Athletes",
      weighIn: "Weigh-in",
      start: "Start",
      exportPlanning: "Export planning (CSV/Excel)",
      exportInstagram: "Export (PDF/PNG)",
      maxAthletesError: "This group cannot accept more athletes.",
      disciplineRequired: "Please select a discipline.",
      weightCategoryRequired: "Please select a weight category.",
      groupCountAboveMax: (max: number) =>
        `This group exceeds the maximum of ${max} athletes.`,
      groupCountBelowMin: (min: number) =>
        `This group has fewer than the minimum of ${min} athletes.`,
      undo: "Undo",
      redo: "Redo",
      fullscreen: "Full screen",
      exitFullscreen: "Exit full screen",
      name: "Name",
      firstname: "Firstname",
      license: "License",
      club: "Club",
      sex: "Sex",
      birthDate: "Birth date",
      weightCategory: "Weight category",
      ageCategory: "Age category",
      discipline: "Discipline",
      actions: "Actions",
      deleteAthlete: "Delete athlete",
      deleteAthleteMessage: "Are you sure you want to delete the athlete?",
      deleteAthleteWarning: "This action is irreversible.",
      cancel: "Cancel",
      delete: "Delete",
      ligue: "Organisation",
      organisation: "Organisation",
      startDate: "Start date",
      endDate: "End date",
      lieu: "City",
      addAthlete: "Add athlete",
      athleteAlreadyExists: "An athlete with this license already exists",
      end: "End",
      back: "Back",
      backConfirmTitle: "Back to previous step",
      backConfirmMessage:
        "All changes made to the planning will be lost. Do you want to continue?",
      next: "Next",
      expand: "Expand",
      collapse: "Collapse",
      emptyOption: "-",
      exportInstagramNotImplemented:
        "Export (PDF/PNG): to be implemented with @react-pdf/renderer and html-to-image.",
      dayLabel: "Day",
      startHour: (date: Date) => {
        const dayName = new Intl.DateTimeFormat("en-US", {
          weekday: "long",
        }).format(date);
        const monthName = new Intl.DateTimeFormat("en-US", {
          month: "long",
        }).format(date);

        return `Start hour for ${dayName}, ${monthName} ${date.getDate()}`;
      },
      endHour: (date: Date) => {
        const dayName = new Intl.DateTimeFormat("en-US", {
          weekday: "long",
        }).format(date);
        const monthName = new Intl.DateTimeFormat("en-US", {
          month: "long",
        }).format(date);

        return `End hour for ${dayName}, ${monthName} ${date.getDate()}`;
      },
      formatDate: (date: Date) => {
        const dayName = new Intl.DateTimeFormat("en-US", {
          weekday: "long",
        }).format(date);
        const monthName = new Intl.DateTimeFormat("en-US", {
          month: "long",
        }).format(date);

        return `${dayName}, ${monthName} ${date.getDate()}`;
      },
      editAthleteModal: {
        title: "Edit athlete",
        name: "Name",
        firstname: "Firstname",
        license: "License",
        club: "Club",
        sex: "Sex",
        birthDate: "Birth date",
        weightCategory: "Weight category",
        discipline: "Discipline",
        plateauNumber: "Platform number",
        groupNumber: "Group number",
        segmentSuffix: "Category suffix",
        saveDisabledNoGroup: "Please select the group number.",
        saveDisabledNoSuffix: "Please select the category suffix.",
        save: "Save",
        cancel: "Cancel",
      },
      editGroupModal: {
        title: "Edit group",
        name: "Name",
        plateauNumber: "Platform number",
        groupPosition: "Group number",
        saveDisabledNoPosition: "Please select the group number.",
        save: "Save",
        cancel: "Cancel",
      },
      editSegmentModal: {
        title: "Edit category",
        plateauNumber: "Platform number",
        groupNumber: "Group number",
        segmentSuffix: "Category suffix",
        saveDisabledNoGroup: "Please select the group number.",
        save: "Save",
        cancel: "Cancel",
      },
      womenCategory: "Women",
      menCategory: "Men",
      categorySeparator: " & ",
      editPlateauModal: {
        title: "Edit platform",
        name: "Name",
        plateauNumber: "Platform number",
        startDate: "Start date",
        weightInDate: "Weigh-in date",
        endDate: "End date",
        dateManualTooltip: "Dates set manually",
        save: "Save",
        cancel: "Cancel",
        saveDisabledMissingDates:
          "Please fill in all dates (weigh-in, start, end).",
        saveDisabledInvalidPlateauNumber: (max: number) =>
          `Platform number must be between 1 and ${max}.`,
        saveDisabledSameDay: "All three dates must be on the same day.",
        resetDates: "Reset dates",
        recalcFromWeighIn: "Recalculate start and end from weigh-in",
        recalcFromStart: "Recalculate weigh-in and end from start",
        recalcFromEnd: "Recalculate start and weigh-in from end",
      },
      plateauExceedsDayEndTitle: "End of day time exceeded",
      plateauExceedsDayEndMessage:
        "One or more platforms exceed the end of day time. Do you want to confirm the move?",
      plateauDateExceedsDayEndTooltip: (maxDate: Date) =>
        `One or more dates exceed the end of day time (max. ${maxDate.getHours()}:${maxDate.getMinutes() < 10 ? "0" + maxDate.getMinutes() : maxDate.getMinutes()}).`,
      plateauDateExceedsDayStartTooltip: (minDate: Date) =>
        `One or more dates exceed the start of day time (min. ${minDate.getHours()}:${minDate.getMinutes() < 10 ? "0" + minDate.getMinutes() : minDate.getMinutes()}).`,
      plateauDateConflictTooltip: (plateauNumbers: number[]) =>
        plateauNumbers.length === 1
          ? `Conflict with platform ${plateauNumbers[0]}`
          : `Conflict with platforms ${plateauNumbers
              .slice()
              .sort((a, b) => a - b)
              .join(", ")}`,
      resetDayDatesTooltip: "Reset day dates",
      confirm: "Confirm",
      addPlateauZone: "New platform",
      addGroupZone: "New group",
      importFile: "Import a file",
      importSettings: "Import settings",
      bodyweight: "Bodyweight",
      bestSquat: "Best Squat",
      bestBench: "Best Bench",
      bestDeadlift: "Best Deadlift",
      bestTotal: "Best Total",
      squat: "Squat",
      bench: "Bench",
      deadlift: "Deadlift",
      total: "Total",
      filterPlaceholder: "Filter...",
      allValues: "All",
      filteredCount: (filtered: number, total: number) =>
        `${filtered} / ${total}`,
      requiredFieldsMissing:
        "Please fill in all required fields (name, city, dates, organisation).",
      importSettingsModal: {
        title: "Import settings",
        requiredColumns: "Required columns",
        optionalColumns: "Optional columns",
        addColumn: "Add a column",
        columnNumber: "Column #",
        removeColumn: "Remove this column",
        save: "Save",
        cancel: "Cancel",
        reset: "Reset",
        tooltips: {
          meetName: "Text: competition name",
          discipline: "Discipline code: FA, DC, BP, PL",
          licence: "Athlete's license number",
          club: "Athlete's club name",
          sex: "Athlete's sex: M or F",
          birthDate: "Date of birth: DD/MM/YYYY or YYYY-MM-DD",
          lastname: "Athlete's last name",
          firstname: "Athlete's first name",
          bodyweight: "Bodyweight in kg (decimal number)",
          ligueName: "Organisation name (text)",
          startDate: "Competition start date",
          endDate: "Competition end date",
          lieu: "Competition city",
          squat: "Nomination/qualification performance in kg (squat)",
          bench: "Nomination/qualification performance in kg (bench press)",
          deadlift: "Nomination/qualification performance in kg (deadlift)",
          total: "Nomination/qualification performance in kg (total)",
        },
        columnLabels: {
          meetName: "Competition name",
          discipline: "Discipline",
          licence: "License",
          club: "Club",
          sex: "Sex",
          birthDate: "Date of birth",
          lastname: "Last name",
          firstname: "First name",
          bodyweight: "Bodyweight",
          ligueName: "Organisation",
          startDate: "Start date",
          endDate: "End date",
          lieu: "City",
          squat: "Squat",
          bench: "Bench",
          deadlift: "Deadlift",
          total: "Total",
        },
      },
      importSummaryModal: {
        title: "Import summary",
        totalAthletes: "Total athletes imported",
        men: "Men",
        women: "Women",
        byDiscipline: "By discipline",
        byWeightCategory: "By weight category",
        byAgeCategory: "By age category",
        byCombination: "By combination (weight + age + discipline)",
        close: "Close",
        continue: "Continue",
        unknown: "Undefined",
      },
      duplicateResolverModal: {
        title: "Duplicates detected",
        description:
          "The following athletes appear to be duplicates (same name, first name and date of birth). Select those you want to remove.",
        keepAll: "Keep all",
        removeSelected: "Remove selected",
        cancel: "Cancel",
        duplicateGroup: (count: number) => `${count} occurrences`,
        selectToRemove: "Select to remove",
        noDuplicates: "No duplicates detected.",
      },
      createCompetition: "Create competition",
      createCompetitionConfirm:
        "The planning will be locked and you will be able to manage referees and export data. Do you want to continue?",
      persistenceError:
        "Data will not be saved because the server connection is unavailable.",
      saving: "Saving...",
      saved: "Saved",
      status: {
        import: "Import",
        settings: "Settings",
        draft: "Draft",
        created: "Created",
      },
      createdPage: {
        title: "Competition created",
        backToDraft: "Back to draft",
        backToDraftConfirmTitle: "Back to draft?",
        backToDraftConfirmMessage:
          "Warning: going back to draft will reset all referee availabilities and assignments for this competition. This action is irreversible. Do you want to continue?",
        planningPreview: "Planning preview",
        previewHint:
          "Expand a group, then a category, to browse the athlete list.",
        previewEmpty: "No platforms in this planning.",
        previewNoCategories: "No categories in this group.",
        previewNoAthletes: "No athletes in this category.",
        previewColumns: {
          lot: "Lot",
          name: "Name",
          licence: "Licence",
          club: "Club",
        },
        planningExports: "Planning exports",
        refereeSection: "Referee management",
        refereeExports: "Referee exports",
        copyAvailabilityLink: "Copy availability link",
        linkCopied: "Link copied to clipboard",
        manageMyReferees: "Manage my referees",
        noAssignments: "No referee assigned",
        exportPngError: "Error during image export",
        assignErrorGeneric: "Error",
        refereeExportsPngDialogTitle: "Referee exports - Image",
        defaultFileName: "planning",
        exportRefereesIncompleteTitle: "Referee export",
        exportRefereesIncompleteMessage:
          "Some platforms do not have all referee positions filled. Do you still want to export?",
        exportRefereesConfirmExport: "Export anyway",
        noPlateauxForPng: "No platforms to export as image.",
        engagementsButton: "Tickets",
        engagementsSelectGroup: "Select group",
        engagementsNoAthletes: "No athletes in this group.",
        engagementsGroupOption: (
          plateauNum: number,
          groupNum: number,
          groupName?: string,
        ) =>
          groupName
            ? `Platform ${plateauNum} - Group ${groupNum} - ${groupName}`
            : `Platform ${plateauNum} - Group ${groupNum}`,
        plateauObservations: "Observations",
        plateauSecretary: "Secretariat",
        organisationSheets: {
          sectionTitle: "Organization documents",
          controlMaterial: "Material check",
          heightFa: "Bar height (FA)",
          heightDc: "Bar height (DC)",
          tirageAuSort: "Draw",
          selectPlateaux: "Select platforms",
          print: "Print / Save as PDF",
          close: "Close",
          expandPreview: "Open preview full size",
          plateauTitle: (num: number, name: string) =>
            `Platform ${num} - ${name}`,
          groupTitle: (num: number, name: string) =>
            name ? `Group ${num} - ${name}` : `Group ${num}`,
          subtitleControlMaterial: "MATERIAL CHECK",
          subtitleHeightFa: "BAR HEIGHT (FA)",
          subtitleHeightDc: "BAR HEIGHT (DC)",
          weighInAt: (time: string) => `Weigh-in at ${time}`,
          noteSection: "Note",
          noteVisible: "Show note",
          tirageNoteDefault: "Athletes must have a valid identity document.",
          columns: {
            lastname: "LAST NAME",
            firstname: "First name",
            remark: "Remark",
            signature: "Signature",
            squat: "SQUAT",
            rack: "Rack (In/Out)",
            dc: "DC",
            cales: "BLOCKS (Y/N)",
            safetyHeight: "Safety height",
            num: "No.",
            nomPrenom: "LAST NAME First name",
            fait: "Done",
          },
        },
      },
      pngExportModal: {
        defaultFileName: "planning",
        title: "Image Export",
        formatSection: "Image format",
        formatSquare: "Square",
        formatLandscape: "Landscape",
        formatPortrait: "Portrait",
        templateSection: "Template",
        templateFidele: "Classic",
        templateMinimal: "Minimal",
        templateCartes: "Cards",
        templateTimeline: "Timeline",
        templateDark: "Dark",
        colorsSection: "Colors",
        noteSection: "Note",
        close: "Close",
        noteVisible: "Show note",
        preview: "Preview",
        exportButton: "Export Image",
        exportError: "Error during image export",
        imagePageIndicator: (current: number, total: number) =>
          `Image ${current} of ${total}`,
        imagePrev: "Previous image",
        imageNext: "Next image",
        pngNoteDefault:
          "Athletes who will not be at the weight defined during their registration will participate Out of Competition. This means the athlete will not be in the match and will not be ranked in this competition but their result will be published and valid for a level at the federation (except TPU, see regulations).",
        templateLabels: {
          weighIn: "Weigh-in",
          weighInUpper: "WEIGH-IN",
          start: "Start",
          startUpper: "START",
          weighInStartHeader: "WEIGH-IN / START",
          plateau: "Platform",
          groups: "Groups",
          detail: "Detail",
          group: "group",
          groupsPlural: "groups",
          groupShort: "gr.",
          groupDetailPrefix: "GR",
          athleteShort: "ath.",
          athlete: "athlete",
          athletes: "athletes",
          footerSite: "meet-manager.fr",
        },
        colorLabels: {
          headerBg: "Header bg",
          titleColor: "Title",
          noteColor: "Note",
          dayBannerBg: "Day banner",
          dayBannerText: "Day text",
          tableBorder: "Table border",
          tableBg: "Table bg",
          tableText: "Table text",
          footerBorder: "Footer border",
          footerText: "Footer text",
          postBg: "Background",
          postBorder: "Border",
          metaColor: "Metadata",
          noteBg: "Note bg",
          noteBorder: "Note border",
          noteBorderLeft: "Note border",
          dayLabelColor: "Day label",
          blockBorder: "Block border",
          blockTimeBg: "Time bg",
          blockTimeText: "Time text",
          blockTimeLabelColor: "Time label",
          bodyText: "Body text",
          plateauText: "Platform name",
          groupText: "Group text",
          cardBg: "Card bg",
          cardBorder: "Card border",
          cardTitleColor: "Card title",
          badgeBg: "Badge bg",
          badgeText: "Badge text",
          cardTimesColor: "Card times",
          cardGroupsBorder: "Groups border",
          cardGroupsText: "Groups text",
          dayTitleBg: "Day title bg",
          dayTitleText: "Day title text",
          dayHeadColor: "Day head",
          dayHeadBorder: "Day head border",
          timelineBorder: "Timeline border",
          dotBg: "Dot",
          dotBorder: "Dot border",
          timeColor: "Time",
          itemTitleColor: "Item title",
          detailColor: "Detail",
          gradientStart: "Gradient (top)",
          gradientEnd: "Gradient (bottom)",
          titleShadow: "Title shadow",
          noteBorderLeft2: "Note border",
          blockBg: "Block bg",
          blockTitleColor: "Block title",
          blockTimeColor: "Block time",
          catText: "Category text",
        },
      },
      refereesPngExportModal: {
        defaultFileName: "planning",
        fileNameSuffix: "-referees",
        title: "Image Export - Referees",
        formatSection: "Image format",
        formatSquare: "Square",
        formatLandscape: "Landscape",
        formatPortrait: "Portrait",
        templateSection: "Template",
        templateFidele: "Classic",
        templateMinimal: "Minimal",
        templateCartes: "Cards",
        templateTimeline: "Timeline",
        templateDark: "Dark",
        colorsSection: "Colors",
        close: "Close",
        preview: "Preview",
        exportButton: "Export Image",
        exportError: "Error during image export",
        imagePageIndicator: (current: number, total: number) =>
          `Image ${current} of ${total}`,
        imagePrev: "Previous image",
        imageNext: "Next image",
        templateLabels: {
          referees: "Referees",
          weighIn: "Weigh-in",
          start: "Start",
          end: "End",
          plateau: "Platform",
          observations: "Observations",
          secretary: "Secretariat",
          footerSite: "meet-manager.fr",
          headerSuffix: "Referees",
        },
        colorLabels: {
          headerBg: "Header bg",
          titleColor: "Title",
          dayBannerBg: "Day banner",
          dayBannerText: "Day text",
          tableBorder: "Table border",
          tableBg: "Table bg",
          tableText: "Table text",
          roleColor: "Roles",
          footerBorder: "Footer border",
          footerText: "Footer text",
          postBg: "Background",
          postBorder: "Border",
          metaColor: "Metadata",
          dayLabelColor: "Day label",
          blockBorder: "Block border",
          blockTimeBg: "Time bg",
          blockTimeText: "Time text",
          blockTimeLabelColor: "Time label",
          bodyText: "Body text",
          plateauText: "Platform name",
          cardBg: "Card bg",
          cardBorder: "Card border",
          cardTitleColor: "Card title",
          cardTimesColor: "Card times",
          dayTitleBg: "Day title bg",
          dayTitleText: "Day title text",
          dayHeadColor: "Day head",
          dayHeadBorder: "Day head border",
          timelineBorder: "Timeline border",
          dotBg: "Dot",
          dotBorder: "Dot border",
          timeColor: "Time",
          itemTitleColor: "Item title",
          detailColor: "Detail",
          gradientStart: "Gradient (top)",
          gradientEnd: "Gradient (bottom)",
          titleShadow: "Title shadow",
          blockBg: "Block bg",
          blockTitleColor: "Block title",
          blockTimeColor: "Block time",
        },
      },
      exports: {
        csv: "CSV",
        excel: "Excel",
        png: "Image",
        configureColumns: "Exports configuration",
        resetColumns: "Reset",
        separator: "CSV separator",
        primaryColor: "Primary color",
        defaultColumns: "Default columns",
        additionalColumns: "Additional columns",
        dragToReorder: "Drag to reorder",
        exportModalTitle: "Export settings",
        save: "Save",
        cancel: "Cancel",
      },
      refereeAssignment: {
        weightIn: "Weigh-in",
        materialCheck: "Equipment",
        leftReferee: "Left referee",
        chiefReferee: "Chief referee",
        rightReferee: "Right referee",
        jury: "Jury",
        addReferee: "Add referee",
        selectReferee: "Select referee",
        noAvailableReferees: "No available referee",
        unavailableWarning:
          "This referee has changed their availability and is no longer available for this platform",
        roleAlreadyAssigned: "This role is already assigned on this platform",
        exclusiveRoleConflict:
          "This referee already has an exclusive role on this platform",
      },
    },
    myPlannings: {
      createError:
        "The planning could not be created. Check your organization and try again.",
      loadError: "Competitions or plannings could not be loaded.",
      retry: "Retry",
      title: "My plannings",
      createPlanning: "Create a competition",
      empty: "You have not created any planning",
      noResults: "No planning matches your search",
      deletePlanning: "Delete planning",
      deletePlanningConfirm:
        "Are you sure you want to delete this planning? This action is irreversible.",
      athleteCount: (count: number) =>
        count === 1 ? "1 athlete" : `${count} athletes`,
      plateauCount: (count: number) =>
        count === 1 ? "1 platform" : `${count} platforms`,
      allRefereeRolesAssigned: "Referees assigned",
      refereeRolesIncomplete: "Referees not assigned",
      cityUndefined: "Not set",
    },
    referees: {
      title: "My referees",
      addReferee: "Add referee",
      importReferees: "Import",
      noReferees: "No referee",
      firstname: "First name",
      lastname: "Last name",
      rank: "Rank",
      rankYear: "Rank year",
      club: "Club",
      organisation: "Organisation",
      privateLabel: "Private",
      readOnly: "Read only",
      readOnlyHint:
        "Organisation referees are read-only. You can only edit your private referees.",
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      editTitle: "Edit referee",
      createTitle: "New referee",
      deleteConfirm:
        "Are you sure you want to delete this referee? This action is irreversible.",
      importModal: {
        title: "Import referees (Excel)",
        selectFile: "Select file",
        selectOrganisation: "Select organisation",
        preview: "Preview",
        import: "Import",
        cancel: "Cancel",
        duplicatesSkipped: (count: number) => `${count} duplicate(s) skipped`,
        created: (count: number) => `${count} referee(s) created`,
        mappingInfo:
          "Format: A=Last name, B=First name, C=Rank, D=Year, E=Club",
      },
    },
    refereeAvailability: {
      title: "Referee availability",
      subtitle: "Indicate your availability for each platform",
      searchPlaceholder: "Search your name...",
      selectYourName: "Select your name",
      available: "Available",
      unavailable: "Unavailable",
      submit: "Submit",
      submitted: "Your availabilities have been saved",
      weightIn: "Weigh-in",
      start: "Start",
      end: "End",
      previousResponsesLoaded: "Your previous responses have been pre-filled",
    },
  },
  controlRoom: {
    title: "Control Room",
    noCards:
      "No events available. Load layouts with Control Room events on your displays or widgets.",
    selectMeetLabel: "Live meet",
    loadingLiveMeets: "Loading live meets...",
    noLiveMeets: "No live meet is currently available for this account.",
    hubDisconnected: "Hub disconnected - triggering is disabled.",
    triggerButton: "Trigger",
    targets: "Targets",
    shortcutLabel: "Shortcut",
    openButton: "Control Room",
    backButton: "Back",
    landing: {
      referee: "Referee selection",
      widgets: "Widgets",
      displays: "Displays",
      controlRoom: "Control Room",
    },
  },
  docs: {
    displayEditor: {
      appName: "Meet Manager Docs",
      eyebrow: "User documentation",
      title: "Display editor",
      subtitle:
        "Complete guide to create, import, theme, edit and automate custom Meet Manager displays.",
      searchLabel: "Search",
      searchPlaceholder: "Element, property, behavior…",
      noSearchResult: "No result matches the search.",
      searchModalTitle: "Results",
      searchModalHint: "Type to search titles and keywords across the guide.",
      searchModalClose: "Close search",
      searchModalNavigate: "Open",
      onThisPage: "On this page",
      screenshotFallback: "Screenshot to add",
      screenshotMissing:
        "This screenshot is not available in the documentation assets yet.",
      tableColumns: {
        item: "Item",
        category: "Category",
        availability: "Available when",
        purpose: "Use",
        details: "Details",
      },
      nav: {
        getStarted: "Get Started",
        editor: "Editor",
        behavior: "Behavior",
      },
      sections: {
        intro: {
          title: "Overview",
          summary:
            "This guide covers the display editor: how to create displays, edit them on the canvas, and automate them with behaviors.",
        },
        createLayout: {
          title: "Create a display",
          summary:
            "From My layouts, create a display or widget with a name, type, canvas size and background.",
        },
        importExport: {
          title: "Import and reuse",
          summary:
            "You can import an exported layout file, duplicate displays and reuse them across themes or meets.",
        },
        themes: {
          title: "Themes and assignment",
          summary:
            "A theme assigns each screen or widget target to the custom layout that should appear there.",
        },
        previewEdition: {
          title: "Preview and editing",
          summary:
            "The central canvas is where you compose the display, select elements, zoom, move them and preview with competition data.",
        },
        displayPreview: {
          title: "Competition preview",
          summary:
            "Open a full-screen competition preview from the editor to walk a simulated meet: timeline, play/pause, scenario parameters, and a local Control Room overlay.",
        },
        layers: {
          title: "Element list",
          summary:
            "The left panel shows everything placed on the display. The subsections below explain each kind of element you can add from the toolbar.",
        },
        elementText: {
          title: "Text",
          summary:
            "Show a fixed label, a live competition value, or a mix of both in one line.",
        },
        elementImage: {
          title: "Image",
          summary:
            "Show a picture you upload, or switch the picture from competition data or a behavior.",
        },
        elementVideo: {
          title: "Video",
          summary:
            "Play a video clip on waiting or public screens, with mute and loop options.",
        },
        elementShape: {
          title: "Shape",
          summary:
            "Draw backgrounds, frames, separators and decorative markers behind or around other elements.",
        },
        elementContainer: {
          title: "Container",
          summary:
            "Group several elements so you can move, hide or animate them together. Prefer Flex for everyday layout; Absolute mainly when you animate free positions.",
        },
        elementLayoutList: {
          title: "List",
          summary:
            "Repeat the same row design for every athlete (or other list), with preview, filters, sorting and appear/disappear animations.",
        },
        elementBarLoader: {
          title: "Bar loader",
          summary:
            "Show the loaded plates for a weight in kilograms or pounds, with several display modes and styles per plate or label.",
        },
        elementQrCode: {
          title: "QR code",
          summary:
            "Show a scannable code for a link or text, sized by the element frame and colored in the Style section.",
        },
        properties: {
          title: "Properties",
          summary:
            "The right panel shows shared settings, settings specific to the selected element, and fields driven by a behavior.",
        },
        mockData: {
          title: "Mock data",
          summary:
            "Mock data lets you edit test competition values to check Data Binding without a live meet.",
        },
        toolbar: {
          title: "Toolbar",
          summary:
            "The toolbar groups creation tools, preview, behavior, animation, copy-paste, grouping, stacking order and delete.",
        },
        behaviorInterface: {
          title: "Behavior interface",
          summary:
            "A behavior is a visual graph: events start a sequence, variables provide values, controls choose a path, and actions change what appears on screen.",
        },
        sceneBehavior: {
          title: "Scene behavior",
          summary:
            "Scene behavior is the shared library of animations, events, functions and variables that every element behavior in the display can reuse.",
        },
        sceneAnimations: {
          title: "Scene animations",
          summary:
            "Reusable animations are created in scene behavior, then started from an element with Play animation.",
        },
        sceneFunctions: {
          title: "Scene functions",
          summary:
            "A function packages reusable logic with configurable input and output ports (name and type) that several behaviors can call.",
        },
        sceneEvents: {
          title: "Scene events",
          summary:
            "A custom scene event becomes available to element behaviors once its trigger graph reaches Event output.",
        },
        sceneVariables: {
          title: "Scene variables",
          summary:
            "A scene variable stores or calculates a shared value; you can add typed inputs and update it during the display (for example from events) over the competition.",
        },
        behaviorNodes: {
          title: "All available nodes",
          summary:
            "This list groups behavior blocks by type (events, variables, controls, actions, operators). Within each group, nodes are sorted by value type - the same colored dots as in the editor (for example green for numbers).",
        },
        controlRoom: {
          title: "Control Room",
          summary:
            "Control Room events become operator actions you can trigger from the control room page or with keyboard shortcuts.",
        },
      },
      screenshots: {
        layoutsList: {
          alt: "Custom layouts list",
          caption:
            "My layouts: create, import, manage themes, then open a display or widget already assigned to a target.",
        },
        createLayout: {
          alt: "Create custom layout",
          caption:
            "Creation dialog: name, type (display or widget), optional theme, canvas size (for example 1920×1080) and background color.",
        },
        importJson: {
          alt: "Import layout action",
          caption:
            "Header actions: Import a display loads an exported layout file and opens the creation dialog already filled in.",
        },
        themes: {
          alt: "Theme management",
          caption:
            "My themes: create a theme, then assign layouts to display or widget targets.",
        },
        editorOverview: {
          alt: "Full display editor view",
          caption:
            "Editor: element list on the left, canvas in the center, properties on the right, toolbar above.",
        },
        displayPreviewOverview: {
          alt: "Competition preview with both overlays",
          caption:
            "Competition preview: the display fills the screen. Two overlays stay on top - parameters and timeline at the bottom, local Control Room at the top right.",
        },
        layers: {
          alt: "Element list panel",
          caption:
            "Layout components: search and tree (containers, text, lights…) to select outside the canvas.",
        },
        properties: {
          alt: "Properties panel",
          caption:
            "Selected element properties: position, size, rotation, label, container layout and styles.",
        },
        mockData: {
          alt: "Mock data panel",
          caption:
            "Mock data sources: open Current athlete, timers, lists and other competition values to test without a live meet.",
        },
        toolbar: {
          alt: "Editor toolbar",
          caption:
            "Toolbar: creation tools, preview, mock data, scene/element behavior, copy-paste and stacking order.",
        },
        behavior: {
          alt: "Element behavior canvas",
          caption:
            "Element behavior: left palette, central graph, right scene library. Pan with middle-click drag; zoom with Ctrl/Cmd + scroll.",
        },
        sceneBehavior: {
          alt: "Scene behavior hub",
          caption:
            "Scene hub: Animations, Events, Functions and Variables shared by every element in the display.",
        },
        sceneAnimation: {
          alt: "Scene animation graph",
          caption:
            "Animation graph: Animation begin → keyframe(s) → Animation end. Only properties that differ between poses are animated.",
        },
        sceneFunction: {
          alt: "Scene function graph",
          caption:
            "Function graph: Function begin (add inputs with name and type) → inner logic → Function end (add outputs with name and type). You can also create one from the behavior context menu.",
        },
        sceneEvent: {
          alt: "Scene event trigger graph",
          caption:
            "Event trigger graph: On Load / On Change flows must reach Event output before other behaviors can listen to the custom event.",
        },
        sceneVariable: {
          alt: "Scene variable graph",
          caption:
            "Variable graph centered on Variable end. Optional Variable begin lets you add typed named inputs; Set variable can update the value during the display.",
        },
        controlRoom: {
          alt: "Control Room page",
          caption:
            "Control Room: pick a live meet to trigger actions published by loaded displays and widgets.",
        },
      },
      blocks: {
        learn: "What you will learn",
        procedure: "Procedure",
        shortcuts: "Shortcuts and gestures",
        reference: "Reference",
        propertyReferenceTitle: "Property families",
        graphicNodesTitle: "Graphic elements",
        styleFieldsTitle: "Style fields",
        sourceOfTruth:
          "This guide follows what you see in the display editor: toolbar, canvas, element list, properties, mock data and behavior.",
        sectionDetails: {
          intro: [
            "The display editor builds what screens and widgets show during a meet. A display contains a canvas size, a background, graphic elements, data bindings and, when needed, behaviors that react to competition data or Control Room actions.",
            "Work step by step: create or import a display, assign it to a screen or widget through a theme, then compose the visuals. Add behaviors later for anything that must change automatically during the meet.",
          ],
          createLayout: [
            "Creating a display starts a new working file. Choose Display for a full screen, or Widget for a smaller reusable area (current athlete, timer, lights, and so on).",
            "Canvas size is the work area. Place elements with position, size and stacking order inside that area, so pick dimensions close to the real destination before you compose.",
          ],
          importExport: [
            "Import restores an existing display with its scene, dimensions and background. Use it to share a base between meets or to start from a layout that already works.",
            "After importing, check Data Bindings (known sources), media files, and scene items (animations, events, functions, variables) that behaviors may call.",
          ],
          themes: [
            "A theme is more than colors: it also assigns which custom display or widget appears on each target.",
            "Pick a layout that matches the target. Displays cover main screens; widgets cover areas such as current athlete, referee lights or timer.",
          ],
          previewEdition: [
            "The canvas is where you compose. You see the real look of the display, plus handles and guides to select, move, resize and rotate elements.",
            "Preview shows how the display reacts to competition data. When data changes, bindings update and behaviors can run actions - test before going live.",
            "Use the shortcuts and gestures below to navigate the canvas and edit elements without leaving the mouse or keyboard.",
          ],
          displayPreview: [
            "Competition preview opens the layout full screen with simulated competition frames. Use it to check data bindings, list transitions and Control Room actions before assigning the layout to a live target.",
            "Two overlays stay available: the bottom bar for scenario parameters, theme colors, play/pause and the timeline; the top-right panel for local Control Room actions published by this layout.",
            "You can hide either overlay and bring it back with the floating buttons. Stepping frames updates the simulated meet state so the display reacts as it would during a competition.",
          ],
          layers: [
            "The element list shows the structure of your display. Use it when elements overlap or are hard to click on the canvas.",
            "The order in the list matches what appears in front. Containers can contain other elements, and lists show their row design while you edit them.",
            "The subsections under Element list explain each element type: when to use it, which settings matter in Properties, and which behavior tools are specific to it.",
          ],
          elementText: [
            "Add Text from the toolbar when you need a name, club, weight, record or any label operators will read.",
            "In Properties, build the content as successive parts: plain text and/or a Data Binding (Source + field). Parts are joined in order, so you can mix fixed words with live values in one label.",
            "Useful settings: Width (Fixed or Fit Content), font, size, weight, alignment, color and text shadow, plus position, size, rotation and Visibility.",
            "Open the element's behavior when the label must change during the meet (for example hide a line, change color, or swap the bound value). Prefer connecting competition data directly so the text updates as soon as the data changes.",
          ],
          elementImage: [
            "Add Image from the toolbar, then choose a file on your computer. Meet Manager stores it for this layout so it can be reused on screens and widgets.",
            "In Properties you set the picture, size and Style (opacity, filter, border, shadow, Visibility). You can also switch the picture later from a Data Binding or from a behavior.",
            "Images are prepared ahead of time so they usually appear without a blank flash when you show them.",
            "Typical behaviors: show a badge only for a record, fade an image in, or change Visibility when a condition becomes true.",
          ],
          elementVideo: [
            "Add Video from the toolbar and pick a file to upload, the same way as an image.",
            "Specific settings: Muted and Loop. Prefer muted looping clips on public waiting screens so playback starts reliably.",
            "Keep clips reasonably short so remote widgets do not download heavy files.",
            "Behaviors usually show or hide the video, or change its style. Test Muted and Loop in preview before assigning the layout to a live screen.",
          ],
          elementShape: [
            "Add a Shape from the toolbar menu: rectangle, square, ellipse, oval, line, triangles, trapezoid, parallelogram, polygons or star.",
            "Use shapes as backgrounds, frames, separators or badges. Style them with background, border, opacity, shadow and skew in Properties.",
            "Shapes do not connect to competition data by themselves. Combine them with Text or a Container when you need a labeled panel.",
            "Behaviors typically change Visibility, color or opacity. Absolute containers often hold shapes that move together in an animation.",
          ],
          elementContainer: [
            "A Container groups elements so you can move, hide or animate them as one block. Create it from the toolbar, or select several elements and use Group.",
            "Layout Absolute: each child keeps its own free position on the canvas. Prefer Absolute when you animate positions or compose freely. It is powerful for motion, but nested Absolute layouts can look less stable if you expect a packed interface that reflows.",
            "Layout Flex: children follow a flow (Flex direction, Gap, Justification, Item alignment). Use Relative position for items that should stack naturally, and Absolute only for overlays inside the Flex container. Width and Height can be Fixed or Fit Content.",
            "Practical rule: build the stable interface with Flex; switch to Absolute mainly when you need free animation. If you change a Flex container to Absolute, its direct children become freely positioned.",
            "A behavior on the container can hide the whole group or play a scene animation; each child can still have its own behavior.",
          ],
          elementLayoutList: [
            "Add List from the toolbar. By default it shows athletes: the same row design is repeated for each person in the list.",
            "Mode Edit lets you design the default row (and optional variants) in the element list, like editing any other group of elements. Mode Preview shows how the list looks with mock or live competition data, with Maximum items and List direction applied.",
            "Important settings: Source (which list), List direction, Maximum items, Identifier (a unique field such as licence or id), Transition options, and row variants when some rows must look different.",
            "Fill Identifier when rows should animate in and out. Without it, appear and disappear animations on list items are unreliable.",
            "On the list's behavior you can Filter or Sort the visible rows, and Create row variant for conditional designs. Inside a row you get Current row item, Index and Comparator helpers for advanced sorting.",
            "Inside a row, List item enter and List item exit start appear/disappear animations. Prefer Play animation with a scene animation for polished transitions.",
          ],
          elementBarLoader: [
            "Add Bar Loader from the toolbar. Set or bind Total weight (and Bar weight / Collars weight if needed) so the loader shows the correct plates for KG or LBS.",
            "Mode Full shows the classic stack, Small a compact row, and Bar rectangular blocks. Unit switches kilograms or pounds. Optional heavy plates stay off until you enable them.",
            "In Small or Bar, Disk width can be Fixed or Variable, and Disk alignment places the plates. Variable width follows the loaded plates and prevents stretching the loader sideways by hand; Fixed keeps a full reserved width so alignment has room.",
            "Style plates and labels globally (global style), then override one plate weight if needed. Resizing on the canvas keeps the loader footprint consistent.",
            "In behavior, Total weight and Bar weight change the load. Style actions can Target a Disk or a Label for temporary look changes during the meet; those temporary changes are not kept as permanent property values.",
          ],
          elementQrCode: [
            "Add a QR code from the toolbar. In Properties, set the value to encode (a link or text), or change it later from a behavior with Source / Set Binding.",
            "The code fills the smaller side of the element frame. Color styles the dots; Background color styles the empty area; Visibility and opacity work like other elements.",
            "Use it for links to widgets, public screens or external pages. Check the scanned result with the same mock or live data you will use in competition.",
            "Typical behaviors: change the encoded value or show/hide the code when a condition becomes true.",
          ],
          properties: [
            "The property panel adapts to the selected element. Text, video, QR code and list do not show the same fields, but they all share position, size, label and basic style.",
            "When a property is driven by a behavior, edit it in the graph rather than as a manual field. Open the element's behavior to see which action changes the value.",
          ],
          mockData: [
            "Mock data temporarily replaces competition values in the editor. It does not change the display file: it lets you test Data Binding, conditions and behaviors safely.",
            "Useful for lists, row variants and screens that depend on the current athlete or timer. Change test data to check empty values, reordering or timer updates.",
          ],
          toolbar: [
            "The toolbar groups actions for the whole editor. Creation tools add elements; editing tools act on the selection; preview and behavior open specialized modes.",
            "Work like a graphic editor: select first, then use the action. Some buttons stay disabled until the context fits (for example Group needs a selection; Behavior needs a selected element).",
          ],
          behaviorInterface: [
            "A behavior is a directed graph. An event starts the sequence, controls decide when to continue, variables and operators compute values, then actions update the element, list, animation or scene variable.",
            "Read the graph from left to right. Flow connections (arrows) set the order of steps; value connections pass a typed value (number, text, yes/no, and so on). Pan with middle-click drag; zoom with Ctrl/Cmd + scroll.",
          ],
          sceneBehavior: [
            "Scene behavior is not tied to one canvas element: it is the shared library of animations, custom events, functions and variables for the whole display.",
            "Open it from Open scene behavior in the toolbar. You land on a hub with four cards; each opens a list, then a detail graph for one entry.",
            "Element behaviors can reuse those entries (Play animation, listen to a custom event, call a function, read or set a variable) without rebuilding the same graph everywhere.",
          ],
          sceneAnimations: [
            "A scene animation is reusable. It describes a sequence of poses and can be played from several element behaviors without copying the same graph onto each element.",
            "The graph starts with Animation begin, continues with one or more Animation keyframe blocks, and must end with Animation end. Begin holds the animation name; End closes the chain so Play animation can run it.",
            "You can also capture poses from the display toolbar (Create animation / Add keyframe / Save animation) instead of building the graph by hand.",
          ],
          sceneFunctions: [
            "A scene function packages logic. It receives values on Function begin, runs an inner graph, and returns results on Function end.",
            "On Function begin you can add or remove input ports and set each port’s name and type. On Function end you can do the same for output ports. If you change ports, every Call function that uses this function must stay compatible.",
            "Create a function from the scene hub list, or extract a selection into a function from the behavior canvas context menu.",
          ],
          sceneEvents: [
            "A scene event is a custom trigger defined in scene behavior. Element behaviors can listen with On custom scene event.",
            "The trigger graph must reach Event output. Without that end block, the graph may exist but other behaviors cannot react to the event.",
            "Build the trigger with On Load / On Change and controls, then finish on Event output so listeners can start their own sequences.",
          ],
          sceneVariables: [
            "A scene variable stores or computes a shared value. Several elements can read it or update it with Set variable during the display.",
            "The graph is anchored on Variable end (the shared result). If the variable needs inputs, Variable begin appears: you can add or remove input ports and set each port’s name and type.",
            "You can redefine the variable while the display runs - for example from an On Change or Control Room event with Set variable - to keep different values over the competition without editing the layout file.",
            "Create a variable from the scene hub, or extract a value selection into a variable from the behavior canvas context menu.",
          ],
          behaviorNodes: [
            "Available blocks depend on context. An element behavior, a list row, a scene event, a function or a variable graph do not offer exactly the same sources and actions.",
            "Tables are grouped by palette section. Within each section, rows are sorted by value type and show the same color dots as in the behavior editor (number in green, string in pink, and so on).",
          ],
          controlRoom: [
            "Control Room turns a behavior into an operator command. When a loaded display or widget publishes a Control Room event, Meet Manager can show it as an action on the Control Room page.",
            "Name, description and keyboard shortcut make the action usable during competition. The page needs a live meet: without one hosted for the account, no action cards appear.",
          ],
        },
        createLayoutSteps: [
          "Open the licensed portal and go to My layouts.",
          "Create a new display.",
          "Choose a clear name for operators.",
          "Choose Display or Widget.",
          "Set canvas size and background color.",
          "Create the display and open the editor.",
        ],
        importSteps: [
          "Import a display file from My layouts.",
          "Check that the imported file opens correctly.",
          "Review the type, dimensions, scene and background.",
          "Choose a theme and target if the display replaces an existing screen or widget.",
          "Confirm and save the imported display.",
        ],
        themeSteps: [
          "Create or edit a theme from theme management.",
          "Set colors used by theme bindings if needed.",
          "Assign the display to the theme.",
          "Choose one target compatible with Display or Widget.",
          "Test the preview with the active theme.",
        ],
        displayPreviewSteps: [
          "Save the layout, then use Open competition preview in the editor toolbar (opens a new tab).",
          "Use the bottom overlay to set the group profile, age categories and theme colors for the simulated meet.",
          "Step with Previous / Next, drag the timeline, or press Play to walk frames automatically (seconds per frame).",
          "Use the top-right Control Room overlay to trigger local actions published by this layout (or note when none are published yet).",
          "Hide an overlay with its close button; bring it back with the floating button that replaces it.",
        ],
        canvasFeatures: [
          "Select an element with a click; add to the selection with Shift, Ctrl or Cmd + click.",
          "Drag an element (or its move handle) to move it; use the corner and side handles to resize.",
          "Rotate with the bottom handle while holding Ctrl or Cmd (the handle shows a rotate icon), or when move is disabled and only rotate is available.",
          "Multi-selection moves as one block (blue outline). Resize and rotate stay single-element only.",
          "Alignment guides appear while dragging; hold Ctrl while dragging to turn snap off.",
          "Right-click opens the context menu (duplicate, delete, group, ungroup, stacking order, behavior…).",
          "In preview, check the result with mock or live competition data.",
        ],
        canvasShortcuts: [
          "Pan: drag with the middle mouse button, or scroll the wheel (without Ctrl/Cmd).",
          "Zoom: Ctrl or Cmd + scroll wheel (zooms toward the pointer).",
          "Move selection: arrow keys (10 px, with guides and snap).",
          "Fine move: Shift + arrow keys (1 px; guides visible, no snap).",
          "Move without snap: Ctrl + arrow keys (same as holding Ctrl while dragging).",
          "Copy / Cut / Paste: Ctrl or Cmd + C / X / V.",
          "Undo: Ctrl or Cmd + Z. Redo: Ctrl or Cmd + Shift + Z, or Ctrl or Cmd + Y.",
          "Delete selection: Delete or Backspace (on the Display tab, outside text fields).",
        ],
        displayPreviewFeatures: [
          "Bottom overlay - transport: previous frame, play/pause, next frame, frame counter and current step label.",
          "Bottom overlay - timeline: scrub to any simulated frame without waiting for play.",
          "Bottom overlay - parameters: group profile, age category per group, theme colors, and seconds per frame for automatic play.",
          "Top-right overlay - Control Room: trigger actions defined as Control Room events in this layout’s behaviors.",
          "If the Control Room panel says no events are published, add On Control Room events in the layout behavior, then reopen the preview.",
          "Either overlay can be hidden; floating buttons restore them without leaving the preview.",
        ],
        layersFeatures: [
          "Shows every element on the display, including items inside containers.",
          "Shows the row design of a list while the list is in Edit mode.",
          "Search by name or type.",
          "Drag to reorder elements inside the same parent.",
          "Right-click for the same actions as on the canvas.",
          "Subsections explain each element type in plain language.",
        ],
        elementTextFeatures: [
          "Toolbar: Text.",
          "Content parts: plain text and/or Data Binding, joined in order.",
          "Width: Fixed or Fit Content.",
          "Fonts and text shadow in Style.",
          "Behavior can change content, color or Visibility during the meet.",
        ],
        elementImageFeatures: [
          "Toolbar: Image (pick a file on your computer).",
          "The picture is stored with the layout for reuse on screens.",
          "Style: opacity, filter, border, shadow.",
          "The picture can stay fixed or follow data / behavior.",
          "Prepared ahead of time to avoid a blank flash when shown.",
        ],
        elementVideoFeatures: [
          "Toolbar: Video (upload a file).",
          "Settings: Muted, Loop.",
          "Prefer muted looping clips on public waiting screens.",
          "Keep clips short for remote widgets.",
          "Behavior usually shows, hides or restyles the video.",
        ],
        elementShapeFeatures: [
          "Toolbar: Shape menu (rectangle, ellipse, line, polygons, star…).",
          "Decorative or structural - no competition data on its own.",
          "Style with background, border, opacity, shadow and skew.",
          "Useful behind text, or as accents inside Absolute containers.",
          "Behavior: Visibility, color or opacity.",
        ],
        elementContainerFeatures: [
          "Toolbar: Container, or Group from several selected elements.",
          "Flex: everyday layout with direction, gap, justification and alignment.",
          "Absolute: free positions - best for animations; can feel less stable for packed UI.",
          "On Flex, Width/Height can be Fixed or Fit Content.",
          "Ungroup puts children back on the canvas.",
          "One behavior can hide or animate the whole group.",
        ],
        elementLayoutListSteps: [
          "Add a List from the toolbar.",
          "Stay in Edit mode and design the default row (texts, shapes, bindings).",
          "Set Source and Identifier (for example licence).",
          "Set List direction, Maximum items and optional transitions.",
          "Switch to Preview to check mock or live data; add Filter, Sort or row variants in behavior if needed.",
          "For appear/disappear, use List item enter / List item exit in the row with Play animation.",
        ],
        elementLayoutListFeatures: [
          "Edit = design the row; Preview = see the real list.",
          "List direction: column, row and reverse options.",
          "Identifier required for reliable appear/disappear animations.",
          "Row variants: different designs when a condition is true.",
          "List behavior: Filter, Sort, Create row variant.",
          "Inside a row: Current row item, Index, Comparator.",
          "Events: List item enter / List item exit.",
        ],
        elementBarLoaderSteps: [
          "Add Bar Loader from the toolbar.",
          "Choose Unit (KG/LBS) and Mode (Full, Small or Bar).",
          "Set or bind Total weight; adjust Bar weight and Collars weight if needed.",
          "Enable optional heavy plates only if the meet uses them.",
          "In Small/Bar, choose Disk width (Fixed or Variable) and Disk alignment.",
          "Style plates and labels globally, then override one plate if needed.",
          "Optional: open behavior to drive Total weight / Bar weight or Target a Disk / Label.",
        ],
        elementBarLoaderFeatures: [
          "Unit switches kilograms or pounds plates.",
          "Modes: Full, Small, Bar.",
          "Collars are always counted as a pair.",
          "Variable disk width follows the loaded plates.",
          "Global style plus optional style per plate weight.",
          "Behavior: Total weight, Bar weight; style with optional Target (Disk / Label).",
          "Targeted look changes during the meet are temporary.",
        ],
        elementQrCodeFeatures: [
          "Toolbar: QR code.",
          "Value: fixed text/link, or changed from behavior.",
          "Size follows the smaller side of the frame.",
          "Color = code dots; Background color = empty area.",
          "Typical use: links to widgets or public pages.",
        ],
        propertyFeatures: [
          "Position, size, rotation and layer label.",
          "Data Binding by source and field.",
          "Specific fields for text, image, video, shape, container, list, QR code and bar loader.",
          "Visual styles, spacing, typography, borders, shadows, filters and transitions.",
          "Fields controlled by behavior are indicated.",
          "Local search among visible properties.",
        ],
        mockFeatures: [
          "Editable sources include current athlete, lists, group, meet, timers, records, levels, categories, disciplines, organisations and theme colors.",
          "Objects open by sections.",
          "Arrays are browsed item by item.",
          "Yes/no fields use a choice control.",
          "Dates use a date picker.",
          "Color fields use the color picker.",
        ],
        behaviorInterfaceFeatures: [
          "The left palette lists blocks available in the current context.",
          "The central canvas receives blocks by drag and drop.",
          "Flow connections link triggers to actions.",
          "Value connections pass a typed value: number, text, yes/no, list, object, date or style value.",
          "The right panel lists reusable scene items when available.",
          "The context menu cuts, copies, pastes, duplicates, deletes and extracts selections into scene functions or variables.",
        ],
        sceneBehaviorSteps: [
          "Open the layout editor on a display or widget.",
          "Click Open scene behavior in the toolbar.",
          "From the hub, open Animations, Events, Functions or Variables.",
          "Create or select an entry, then edit its graph in the detail view.",
          "Reuse that entry from element behaviors (play, listen, call, read or set).",
        ],
        sceneBehaviorFeatures: [
          "Shared library for the whole display, not for a single canvas element.",
          "Hub with four cards: Animations, Events, Functions, Variables.",
          "Breadcrumb navigation: Scene / section / entry name.",
          "Element behaviors reuse scene items instead of duplicating graphs.",
          "Pan graphs with middle-click drag; zoom with Ctrl/Cmd + scroll.",
        ],
        sceneAnimationSteps: [
          "Open scene behavior.",
          "Enter Animations.",
          "Create an animation or capture one from the toolbar.",
          "Build a graph with Animation begin, one or more keyframes and Animation end.",
          "Play the animation from an element with Play animation.",
        ],
        sceneAnimationFeatures: [
          "Animation begin holds the unique animation name.",
          "Keyframes store only properties that differ between poses.",
          "Animation end closes the chain so Play animation can run the sequence.",
          "Capture from the display toolbar or edit the graph by hand.",
        ],
        sceneFunctionSteps: [
          "Open scene behavior → Functions, or extract a selection via the behavior context menu.",
          "On Function begin, add the input ports you need and set each name and type.",
          "Build the inner graph.",
          "On Function end, add the output ports you need and set each name and type.",
          "Call the function from an element behavior.",
        ],
        sceneFunctionFeatures: [
          "Function begin: add/remove inputs; each input has a name and a type.",
          "Function end: add/remove outputs; each output has a name and a type.",
          "Create from the scene list or Extract to function in the context menu.",
          "Reuse the same logic from several element behaviors.",
        ],
        sceneEventSteps: [
          "Open scene behavior → Events and create a custom event.",
          "Build the trigger graph with On Load / On Change and controls.",
          "Connect the final step to Event output.",
          "Listen from element behaviors with On custom scene event.",
        ],
        sceneEventFeatures: [
          "Publishes a named event for the whole display.",
          "Event output is required; without it nothing is published.",
          "Element behaviors listen with On custom scene event.",
          "Trigger graphs stay in scene behavior, not on each element.",
        ],
        sceneVariableSteps: [
          "Open scene behavior → Variables, or extract a value via the behavior context menu.",
          "If you need inputs, use Variable begin: add ports and set each name and type.",
          "Build the computation that feeds Variable end.",
          "Read with a variable reference, or update during the display with Set variable (for example from an event).",
        ],
        sceneVariableFeatures: [
          "Variable end publishes the shared result - this end block is required.",
          "Variable begin (optional): add/remove inputs; each input has a name and a type.",
          "Set variable from element behaviors (events, Control Room, and so on) to store changing values during the competition.",
          "Create from the scene list or Extract to variable in the context menu.",
        ],
        controlRoomSteps: [
          "Add a Control Room event in a behavior.",
          "Set a readable name.",
          "Set a useful operator description.",
          "Choose a one to three key shortcut.",
          "Connect the event to actions.",
          "Load the display in a screen or widget.",
          "Trigger from the Control Room page or keyboard.",
        ],
      },
      references: {
        targets: {
          athlete_display: "Athlete display.",
          loader_display: "Loader display for bar and plates.",
          ranking_display: "Ranking or result display.",
          public_display: "Public or stream display.",
          lights_display: "Referee lights display.",
          current_athlete: "Current athlete widget.",
          current_category: "Current category widget.",
          current_group: "Current group widget.",
          referee_lights: "Referee lights widget.",
          timer: "Timer widget.",
        },
        toolbar: {
          undo: "Undo the last change.",
          redo: "Redo an undone change.",
          resetPreview: "Reset preview as if the display just loaded.",
          openRuntimePreview:
            "Open competition preview with local Control Room.",
          text: "Add a text element.",
          image: "Add an image element.",
          video: "Add a looping video element.",
          shape: "Open the shape menu.",
          barLoader: "Add a bar loader.",
          layoutList: "Add a list bound to athletes by default.",
          qrCode: "Add a styleable QR code.",
          customDisciplineInput: "Add a custom discipline input.",
          customDisciplineButton: "Add a custom discipline button.",
          mockData: "Open test data editing.",
          openSceneBehavior: "Open the shared scene library.",
          createAnimation: "Start animation capture.",
          addKeyframe: "Add a keyframe.",
          saveAnimation: "Save capture as a scene animation.",
          cancelAnimation: "Cancel animation capture.",
          openBehavior: "Open the selected element's behavior.",
          duplicate: "Duplicate selection.",
          cut: "Cut selection.",
          copy: "Copy selection.",
          paste: "Paste compatible copied content.",
          group: "Create a container around the selection.",
          ungroup: "Remove the selected container.",
          extractFromContainer:
            "Extract the selected element from its parent container.",
          bringForward: "Bring the element to the front.",
          sendBackward: "Send the element to the back.",
          bringForwardOne: "Move the element one step forward.",
          sendBackwardOne: "Move the element one step backward.",
          delete: "Delete selection.",
        },
        properties: {
          positionSize: "Controls position, size, rotation and layer label.",
          dataBinding: "Links an element to a competition source and field.",
          style:
            "Groups visibility, background, typography, spacing, borders, shadows, filters and transforms.",
          transitions: "Animates supported visual changes.",
          text: "Static text, a single Data Binding, or composed text/binding parts.",
          image:
            "Fixed picture, or a picture driven by Data Binding or behavior.",
          video: "Video source with mute and loop options.",
          shape: "Shape styled by background, border, opacity and transforms.",
          container: "Structures children with Absolute or Flex layout.",
          layoutList:
            "Repeats a row design over a list source with direction, limit, identifier, variants and transitions.",
          barLoader:
            "Shows a loaded bar with total weight, bar, collars, unit, mode, plates and label styles.",
          qrCode: "Encodes a value or Data Binding into a styleable QR code.",
          behaviorLocked:
            "A behavior-driven field must be edited in the graph.",
        },
        graphicNodes: {
          purpose: {
            text: "Show static text, bound data or composed text made of several parts.",
            image: "Show a fixed picture or an image from competition data.",
            video:
              "Show a local or remote video for public or waiting screens.",
            rectangle: "Create a rectangular background, frame or separator.",
            square: "Create a stable square shape.",
            ellipse: "Create a disk or free rounded shape.",
            oval: "Create a horizontal or vertical pill.",
            line: "Draw a separator or visual guide.",
            triangle: "Create a simple directional marker.",
            rightTriangle: "Create a visual corner or diagonal composition.",
            trapezoid: "Create a slanted label block.",
            parallelogram: "Create an oblique banner.",
            pentagon: "Create a decorative polygon.",
            hexagon: "Create a balanced polygon.",
            octagon: "Create a badge or stop-style panel.",
            star: "Create a highlighted marker.",
            container:
              "Group elements and choose Absolute or Flex positioning.",
            barLoader:
              "Show a loaded bar with weight, plates, collars, units and labels.",
            layoutList:
              "Repeat a graphic row for every item of a competition list.",
            qrCode: "Encode a URL, text or bound value into a QR code.",
          },
          details: {
            common:
              "Shared settings: position, size, rotation, stacking order, lock, label, style, transitions, behavior and Data Binding.",
            text: "Specific settings: static text, Fixed or Fit Content width, text/binding parts and typography.",
            media:
              "Specific settings: fixed media source, file selection and source driven by Data Binding or behavior.",
            video: "Specific settings: video source, Muted and Loop.",
            shape:
              "Specific settings: shape type, background, border, opacity, shadow and transforms.",
            container:
              "Specific settings: Absolute or Flex layout, Fixed or Fit Content size, direction, justification, alignment and gap.",
            layoutList:
              "Specific settings: data source, direction, Preview/Edit mode, maximum items, identifier, row design, variants and transitions.",
            barLoader:
              "Specific settings: total weight, bar weight, collars, unit, Full/Small/Bar mode, optional plates, compact width, alignment and disk/label styles.",
            qrCode:
              "Specific settings: encoded value, code color via text color, background via background color and size through the element frame.",
          },
        },
        styleFields: {
          valueTypeLabel: "Expected type:",
          targets: {
            "primitive:text": "Text",
            "primitive:image": "Image",
            "primitive:video": "Video",
            "primitive:shape": "Shape",
            container: "Container",
            component: "Component",
            "component:qrCode": "QR code",
          },
          categoryPurpose: {
            layout:
              "Layout: visibility, background, visual dimensions and global effects.",
            font: "Text: typography, color and character rendering.",
            decoration:
              "Decoration: underline, color, style and decoration thickness.",
            border: "Border: outline and radius applied to the element frame.",
          },
          valueTypes: {
            boolean: "yes/no",
            number: "number",
            string: "text",
            color: "color",
            gradient: "gradient",
            border: "border or detailed border value",
            fontFamily: "font family",
            fontWeight: "font weight",
            textAlign: "text alignment",
            textTransform: "case transform",
            whiteSpace: "white-space handling",
            textDecorationLine: "decoration line type",
            textDecorationColor: "decoration color",
            textDecorationStyle: "decoration style",
            textDecorationThickness: "decoration thickness",
            filter: "visual filter",
            spacing: "single or detailed spacing",
          },
        },
        behaviorAvailability: {
          node: "Element behavior",
          nodeList: "List or list-row behavior",
          scene: "Scene behavior",
          triggerGraph: "Scene event graph",
          functionGraph: "Function graph",
          variableGraph: "Variable graph",
        },
        behaviorSectionTitles: {
          events: "Events",
          variables: "Variables",
          controls: "Controls",
          actions: "Actions",
          operators: "Operators",
        },
        behaviorPurposeBySection: {
          events: "Starts a sequence.",
          variables: "Produces a value.",
          controls: "Routes or synchronizes the sequence.",
          actions:
            "Updates what appears on screen, a list, an animation or a scene value.",
          operators: "Transforms, compares or combines values.",
        },
        behaviorDetailsByType: {
          event: "Place at the start of a sequence.",
          variable: "Connect to a compatible value input.",
          control: "Connect between an event and actions.",
          action:
            "Connect to an incoming flow; value inputs expect their colored type.",
          operator: "Use between variables and actions to compute a value.",
          sceneReference:
            "Available only when the scene entry is valid and ready to use.",
        },
      },
    },
  },
  behavior: {
    timers: {
      title: "Stopwatches and timers",
      read: "Read timer",
      value: "Integer value",
      timerStart: "Start timer",
      timerStop: "Stop timer",
      timerResume: "Resume timer",
      timerReset: "Reset timer",
      timerSet: "Set timer value",
      timerDelete: "Delete timer",
      reference: "Reference",
      duration: "Initial value",
      unit: "Unit",
      mode: "Mode",
      output: "Value (ms)",
      seconds: "Value (s)",
      formatted: "Text (m:ss)",
      formattedMs: "Text (m:ss.SSS)",
      elapsedMs: "Elapsed (ms)",
      remainingMs: "Remaining (ms)",
      running: "Running",
      finished: "Finished",
      exists: "Exists",
      connected: "Server connected",
      state: "Full state",
      countdown: "Countdown",
      stopwatch: "Stopwatch",
      ms: "Milliseconds",
      s: "Seconds",
      min: "Minutes",
      help: "The same reference in the same competition shares one timer. Start replaces its value; Resume continues after a stop.",
    },
    canvas: {
      cut: "Cut",
      copy: "Copy",
      paste: "Paste",
      duplicate: "Duplicate",
      delete: "Delete",
      insertConnectedNode: "Add node",
      searchNode: "Search node",
      noCompatibleNode: "No compatible node available for this connection.",
      createVariableFromSelection: "Create variable",
      createVariableFromSelectionDefaultName: (index: number) =>
        `Variable ${index}`,
      createFunctionFromSelection: "Create function",
      createFunctionFromSelectionDefaultName: (index: number) =>
        `Function ${index}`,
    },
    nodeList: {
      title: "Available nodes",
      searchPlaceholder: "Search...",
      categories: {
        events: "Events",
        operators: "Operators",
        variables: "Variables",
        controls: "Controls",
        actions: "Actions",
        animation: "Animation",
      },
      inputTypes: {
        number: "Number",
        string: "String",
        boolean: "Boolean",
        list: "List",
        object: "Object",
        date: "Date",
        fontFamily: "Font family",
        fontWeight: "Font weight",
        textAlign: "Align",
        textTransform: "Text transform",
        whiteSpace: "White space",
        textDecorationLine: "Decoration line",
        textDecorationColor: "Decoration color",
        textDecorationStyle: "Decoration style",
        textDecorationThickness: "Decoration thickness",
        color: "Color",
        gradient: "Gradient",
        filter: "Filter",
        boxShadow: "Box shadow",
        textShadow: "Text shadow",
        border: "Border",
        borderRadius: "Border radius",
        spacing: "Spacing",
        location: "Position",
        transform: "Transform",
        animationDirection: "Animation direction",
        animationFill: "Animation fill mode",
        animationEasing: "Animation easing",
        layoutListTarget: "List target",
        flow: "Flow",
        any: "Any type",
      },
      eventSubCategories: {
        onChange: "On Change",
        lifecycle: "Lifecycle",
        listTransition: "List transition",
        controlRoom: "Control Room",
      },
      actionSubCategories: {
        content: "Content",
        position: "Position",
        style: "Style",
        animation: "Animation",
        barLoader: "Bar loader",
      },
      variableSubCategories: {
        literal: "Literals",
        object: "Objects",
        lists: "Lists",
        source: "Sources",
        context: "Context",
      },
    },
    operators: {
      readTimer: "Read timer",
      defined: "Defined",
      empty: "Empty",
      length: "Length",
      isNotEmpty: "Not empty",
      sum: "Sum",
      average: "Average",
      min: "Minimum",
      max: "Maximum",
      equals: "=",
      notEquals: "≠",
      greaterThan: ">",
      lessThan: "<",
      greaterThanOrEqual: "≥",
      lessThanOrEqual: "≤",
      contains: "Contains",
      notContains: "Not contains",
      startsWith: "Starts with",
      endsWith: "Ends with",
      compare: "Compare",
      and: "And",
      or: "Or",
      not: "Not",
      boolToSign: "Convert for comparison",
      ifElse: "If",
      add: "+",
      subtract: "−",
      multiply: "×",
      divide: "÷",
      modulo: "%",
      power: "^",
      round: "Round",
      factorial: "!",
      ceil: "Round up",
      concat: "+",
      chartAt: "Character at index",
      join: "Join",
      push: "Push",
      remove: "Remove",
      take: "Take",
      get: "Get",
      map: "Map",
      find: "Find",
      groupBy: "Group by",
      aggregateGroups: "Aggregate groups",
      findByPath: "Find by path",
      findByCondition: "Find by condition",
      filter: "Filter",
      sort: "Sort",
      sortBy: "Sort by keys (current item)",
      currentEntry: "Current entry",
      currentItem: "Current item",
      comparator: "Comparator",
      constructObject: "Create object",
      extendObject: "Extend object",
      setProperty: "Set property",
      pickObject: "Pick properties",
      omitObject: "Omit properties",
      objectKeys: "Object keys",
      objectValues: "Object values",
      objectEntries: "Object entries",
      mapItems: "Map each item",
      mapValues: "Map each value",
      getByPath: "Get by path",
      getFieldLabel: "Field",
      replace: "Replace",
      castToString: "Cast to string",
      castToNumber: "Cast to number",
      castToFontFamily: "Cast to font family",
      castToFontWeight: "Cast to font weight",
      castToTextAlign: "Cast to text align",
      castToTextTransform: "Cast to text transform",
      castToWhiteSpace: "Cast to white space",
      castToTextDecorationLine: "Cast to decoration line",
      castToTextDecorationStyle: "Cast to decoration style",
      castToBoolean: "Cast to boolean",
      castToColor: "Cast to color",
      castToGradient: "Cast to gradient",
      castToFilter: "Cast to filter",
      castToBoxShadow: "Cast to box shadow",
      castToTextShadow: "Cast to text shadow",
      castToBorder: "Cast to border",
      castToBorderRadius: "Cast to border radius",
      castToObject: "Cast to object",
      withAlpha: "Alpha",
      mix: "Mix",
      replacePorts: {
        source: "Source",
        search: "Search",
        replacement: "Replace with",
      },
      mixPorts: {
        colorA: "Color A",
        colorB: "Color B",
        ratio: "Ratio %",
      },
      boolToSignPorts: {
        condition: "Condition",
        whenTrue: "When true",
        whenFalse: "When false",
      },
      ifElsePorts: {
        whenTrue: "Value if true",
        whenFalse: "Value if false",
        condition: "Condition",
      },
      findByPathPorts: {
        list: "List",
        fieldPath: "Field path",
        expected: "Value to match",
      },
      isSame: "Is same",
      dateDifference: "Date difference",
      dateDifferencePorts: {
        dateA: "Date A",
        dateB: "Date B",
        years: "Years",
        months: "Months",
        days: "Days",
      },
      isAfter: "After",
      isBefore: "Before",
      addDays: "Add days",
      addMonths: "Add months",
      addYears: "Add years",
      addHours: "Add hours",
      addMinutes: "Add minutes",
      addSeconds: "Add seconds",
      getDay: "Day",
      getMonth: "Month",
      getYear: "Year",
      getHour: "Hours",
      getMinute: "Minutes",
      getSecond: "Seconds",
      toUpperCase: "Upper case",
      toLowerCase: "Lower case",
      capitalize: "Capitalize",
      datePartFormat: {
        label: "Output format",
        numeric: "Numeric",
        narrow: "Narrow",
        short: "Short",
        long: "Long",
        twoDigit: "Two-digit",
      },
    },
    events: {
      listener: "Event",
      wholeObject: "Any field",
      output: "Output",
      triggerOnInit: "Run on first load",
      kinds: {
        onChange: "On Change",
        onLoad: "On Load",
        onClick: "On Click",
        onListEnter: "List item enter",
        onListExit: "List item exit",
        onListFilterExit: "List filter exit",
        onListFilterEnter: "List filter enter",
        onControlRoom: "Control Room",
        onCustomSceneEvent: "Custom event",
      },
      controlRoom: {
        name: "Name",
        namePlaceholder: "Event name",
        description: "Description",
        descriptionPlaceholder: "Event description",
        shortcut: "Shortcut",
        shortcutCapture: "Press keys…",
        shortcutEditing: "Enter a shortcut (1 to 3 keys)…",
        shortcutEmpty: "No shortcut defined",
        shortcutClear: "Clear shortcut",
        preference: "Operator keyboard preference",
        ownShortcut: "Shortcut defined above",
        preferenceHelp:
          "Uses the keyboard settings preference. The combination above is the default.",
        tooltipAction: "Action key to advertise in the tooltip",
        tooltipActionHelp:
          "Empty: this action. Links a hidden shortcut to a visible button.",
        invalidNode:
          "Fill in name, description and shortcut to publish this event.",
      },
    },
    controls: {
      ifElse: "If...Else",
      wait: "Wait",
      repeat: "Repeat",
      condition: "Condition",
      value: "Value",
      delayMs: "Delay (ms)",
      count: "Repeats",
      body: "Body",
      loopIndex: "Loop index",
      done: "Done",
      mergeFlows: "Merge flows",
      eventOutput: "Event output",
      functionBegin: "Function begin",
      functionEnd: "Function end",
      variableBegin: "Variable begin",
      variableEnd: "Variable end",
      flowIn1: "Input 1",
      flowIn2: "Input 2",
      true: "True",
      false: "False",
    },
    actions: {
      setHeader: "Set",
      setStyle: "Style",
      setText: "Text",
      setListSource: "Computed source",
      setFilter: "Filter",
      setSort: "Sort",
      setBinding: "Binding",
      iconColor: "Icon color",
      iconSize: "Icon size",
      customActionDisabled: "Disabled",
      customActionIcon: "Icon",
      customActionStartIcon: "Icon",
      customActionVariant: "Variant",
      customActionSize: "Size",
      customActionColor: "Color",
      customActionTooltip: "Tooltip",
      customActionLabel: "Label",
      customListSetField: "Update item field",
      customListSetFieldList: "List",
      customListSetFieldSearchKey: "Search key",
      customListSetFieldSearchValue: "Search value",
      customListSetFieldFieldKey: "Field to update",
      customListSetFieldNextValue: "New value",
      customListSetRows: "Replace rows",
      customListSetRowsList: "List",
      customListSetRowsMode: "Mode",
      customListSetRowsTargetGroupId: "Target group",
      customListSetRowsPersistFields: "Persist fields",
      customListSetRowsDeleteMissing: "Delete missing",
      customListSetViewRows: "Replace view rows",
      customListSetViewRowsList: "List",
      customListSetViewRowsIncludeNewRows:
        "Include new athletes (sort without filtering)",
      customGroupSetField: "Update group field",
      customGroupSetFieldGroupId: "Group",
      customGroupSetFieldFieldKey: "Field to update",
      customGroupSetFieldNextValue: "New value",
      customOpenGroup: "Open group",
      customOpenGroupId: "Group ID",
      customSetCurrentGroup: "Set current group",
      customSetCurrentGroupId: "Group ID",
      customSetCurrentAthlete: "Set current athlete",
      customSetCurrentAthleteId: "Athlete ID",
      customOpenModal: "Open modal",
      customOpenModalKey: "Modal key",
      customOpenModalAthleteId: "Context athlete",
      customOpenModalGroupId: "Context group",
      customRunAction: "Run a configured action",
      customRunActionColumn: "Context column (optional)",
      customRunActionKey: "Action key",
      customIncrementAthleteCount: "Increment athlete counter",
      customGroupService: "Group service",
      customGroupServiceName:
        "Service (synchronise, federation, macroExcel, openpowerlifting, powerscout, automaticRefereeing, competitionResults)",
      customGroupServiceMap: "Field mapping",
      customGroupServiceOptions: "Service options",
      customGroupServiceFormat: "Service format",
      customSubmitForm: "Save the form",
      customPrintModal: "Print a modal layout",
      customCloseModal: "Close a modal",
      customCloseModalKey: "Modal key",
      customInputOptions: "Input options",
      customEntryEligible: "Ticket: entry allowed",
      customEntryMinimum: "Ticket: minimum",
      customEntryMaximum: "Ticket: maximum",
      customEntryStep: "Ticket: input step",
      customEntryAllowWhileCalled: "Ticket: changes while called",
      customEntryUnlimitedChanges: "Ticket: unlimited changes",
      customEntryReviewChanges: "Ticket: secretary review",
      customEntryMaxChanges: "Ticket: change limit",
      customEntryTicketReference: "Ticket: timer reference",
      customReferenceFilter: "Include item",
      setLocation: "Location",
      setTransform: "Transform",
      setTarget: "Target",
      currentRow: "Current row",
      comparator: "Comparator",
      comparatorA: "Comparator A",
      comparatorB: "Comparator B",
      findIndexOutput: "Index",
      createRowVariant: "Create row variant",
      barLoaderDisks: "Disks",
      setBarLoaderTotalWeight: "Total weight",
      setBarLoaderBarWeight: "Bar weight",
      setVariableHeader: "Set variable",
      setVariablePrefix: "Set",
      setVariableValue: "Value",
      barLoaderTargetDisk: "Disk",
      barLoaderTargetLabel: "Label",
      barLoaderTargetAll: "All",
      styleNodeLabels: {
        fontSize: "Font size",
        fontWeight: "Font weight",
        borderRadius: "Border radius",
        backgroundColor: "Background color",
        backgroundImage: "Background image",
        textAlign: "Text align",
        textTransform: "Text transform",
        textShadow: "Text shadow",
        textDecorationLine: "Decoration line",
        textDecorationColor: "Decoration color",
        textDecorationStyle: "Decoration style",
        textDecorationThickness: "Decoration thickness (px)",
      },
      textValuePort: "Text",
      flowIn: "In",
      flowOut: "Out",
      splitPorts: "Split handles",
      joinPorts: "Join handles",
      locationX: "X",
      locationY: "Y",
      locationZ: "Z",
      transformWidth: "W",
      transformHeight: "H",
      transformRotation: "R",
    },
    fontWeightChoices: {
      100: "Thin",
      200: "Extra Light",
      300: "Light",
      400: "Regular",
      500: "Medium",
      600: "Semi Bold",
      700: "Bold",
      800: "Extra Bold",
      900: "Black",
    },
    fontFamilyChoices: {
      Arial: "Arial",
      Verdana: "Verdana",
      Tahoma: "Tahoma",
      "Trebuchet MS": "Trebuchet MS",
      "Times New Roman": "Times New Roman",
      Georgia: "Georgia",
      Garamond: "Garamond",
      "Courier New": "Courier New",
      "Palatino Linotype": "Palatino Linotype",
      "Lucida Sans Unicode": "Lucida Sans Unicode",
      "Comic Sans MS": "Comic Sans MS",
      Impact: "Impact",
      "Brush Script MT": "Brush Script MT",
    },
    textAlignChoices: {
      left: "Left",
      center: "Center",
      right: "Right",
    },
    textTransformChoices: {
      none: "None",
      capitalize: "Capitalize",
      uppercase: "Uppercase",
      lowercase: "Lowercase",
    },
    whiteSpaceChoices: {
      normal: "normal",
      pre: "pre",
      "pre-wrap": "pre-wrap",
      "pre-line": "pre-line",
      nowrap: "nowrap",
      wrap: "wrap",
      "break-spaces": "break-spaces",
      collapse: "collapse",
      "preserve nowrap": "preserve nowrap",
    },
    textDecorationLineChoices: {
      none: "None",
      underline: "Underline",
      overline: "Overline",
      "line-through": "Line through",
      blink: "Blink",
      "spelling-error": "Spelling error",
      "grammar-error": "Grammar error",
    },
    textDecorationStyleChoices: {
      solid: "Solid",
      double: "Double",
      dotted: "Dotted",
      dashed: "Dashed",
      wavy: "Wavy",
    },
    layoutList: {
      createRowVariant: {
        label: "Create row variant",
        name: "Name",
        condition: "Condition",
        order: "Order",
      },
    },
    animation: {
      begin: "Animation begin",
      keyframe: "Keyframe",
      end: "Animation end",
      play: "Play animation",
      name: "Name",
      durationMs: "Duration (ms)",
      delayMs: "Delay (ms)",
      iterationCount: "Iterations",
      playInfinite: "Play forever",
      direction: "Direction",
      fillMode: "Fill mode",
      freezeChildren: "Freeze children",
      offset: "Offset (%)",
      offsetOut: "Offset out",
      easing: "Easing",
      easingOut: "Easing out",
      mirrorSuffix: "(out)",
      directionChoices: {
        normal: "Normal",
        reverse: "Reverse",
        alternate: "Alternate",
        alternateReverse: "Alternate reverse",
      },
      fillModeChoices: {
        none: "None",
        forwards: "Forwards",
        backwards: "Backwards",
        both: "Both",
      },
      easingChoices: {
        linear: "Linear",
        ease: "Ease",
        "ease-in": "Ease in",
        "ease-out": "Ease out",
        "ease-in-out": "Ease in-out",
      },
      props: {
        opacity: "Opacity",
        x: "X",
        y: "Y",
        scale: "Scale",
        scaleX: "Scale X",
        scaleY: "Scale Y",
        rotation: "Rotation",
        skewX: "Skew X",
        skewY: "Skew Y",
        width: "Width",
        height: "Height",
      },
      addPort: "Add property",
      addPortEmpty: "All properties are already present",
      deletePort: "Remove property",
    },
    sceneCatalog: {
      objectEditor: {
        nativeTypes: {
          currentAthlete: "Athlete",
          currentGroup: "Group",
          currentMeet: "Competition",
          refereeLights: "Refereeing",
          nextLiftData: "Next attempt",
          records: "Record",
          levels: "Level",
          organisation: "Organisation",
          ageCategories: "Age category",
          weightCategories: "Weight category",
        },
        definition: "Object definition",
        end: "Object output",
        objectMode: "Add as",
        objectField: "Nested object field",
        extension: "Extend an object",
        extendType: "Object to extend",

        title: "Reusable object types",
        base: "1 · Base object",
        baseType: "Base type",
        emptyObject: "Empty object",
        inherited: "Inherited fields",
        extend: "2 · Extend",
        fields: "fields",
        key: "Field key",
        valueType: "Value type",
        defaultValue: "Default value",
        noDefault: "No default value",
        itemType: "Item type",
        untyped: "Untyped object",
        removeField: "Remove field",
        addField: "Add field",
        output: "3 · Output type",
        typeName: "Type name",
        availableInVariables: "Available in variables",
        inUse: "This type is used by an object, variable or element.",
        errors: {
          graph:
            "The graph must contain one object definition and one output, with valid connections and no cycles.",
          nameTaken: "A type already uses this name.",
          name: "Give this type a name.",
          cycle: "This reference creates a cycle between types.",
          reference: "A referenced type no longer exists.",
          field: "Keys must be unique without spaces, and types must be valid.",
          default: "The default value does not match the selected type.",
        },
      },
      objects: "Objects",
      countObjects: "{n} object type(s)",
      emptyObjects: "No object types yet",
      panelObjects: "Objects",

      breadcrumbScene: "Scene",
      animations: "Animations",
      events: "Events",
      functions: "Functions",
      functionCall: "Function",
      variableRef: "Variable",
      variables: "Variables",
      countAnimations: "{n} animation(s)",
      countEvents: "{n} event(s)",
      countFunctions: "{n} function(s)",
      countVariables: "{n} variable(s)",
      create: "Create",
      rename: "Rename",
      duplicate: "Duplicate",
      delete: "Delete",
      nameEmptyError: "Name cannot be empty.",
      animationNameTakenError: "An animation with this name already exists.",
      nameTakenError: "An entry with this name already exists.",
      emptyAnimations: "No animations yet",
      emptyEvents: "No events yet",
      emptyFunctions: "No functions yet",
      emptyVariables: "No variables yet",
      panelTitle: "Scene catalog",
      panelAnimations: "Animations",
      panelEvents: "Events",
      panelFunctions: "Functions",
      panelVariables: "Variables",
      panelEmpty: "-",
      searchPlaceholder: "Search...",
      defaultAnimations: {
        fade: "Fade",
        "slide-up": "Slide up",
        "slide-down": "Slide down",
        "slide-left": "Slide left",
        "slide-right": "Slide right",
        "zoom-in": "Zoom in",
        "zoom-out": "Zoom out",
        pop: "Pop",
        bounce: "Bounce",
        drift: "Drift",
      },
      functionGraph: {
        addInputPort: "Add input",
        addOutputPort: "Add output",
        selectValueType: "Value type",
        portLabel: "Port label",
        portLabelPlaceholder: "Display name",
        confirmAddPort: "Add",
        renamePort: "Rename port",
        confirmRenamePort: "Rename",
        deletePort: "Delete port",
        cancel: "Cancel",
      },
      variableGraph: {
        addInputPort: "Add input",
        selectValueType: "Value type",
        portLabel: "Port label",
        portLabelPlaceholder: "Display name",
        confirmAddPort: "Add",
        renamePort: "Rename port",
        confirmRenamePort: "Rename",
        deletePort: "Delete port",
        cancel: "Cancel",
      },
    },
  },
} as const;

export default en;
